import { getCurrentApiConfig, getCurrentEnvironment, DEFAULT_HEADERS, API_ENDPOINTS } from '../config/api.config';
import { tokenManager, TokenProvider } from './token-manager';
import type { AiStatus, AiModelChoice, AiConfig, AiTestResult, AiUsageHistory, AiAssistantResult, AiQuickSuggestions } from './ai.types';
import type { WorkspaceChat } from './workspace-assistant.types';
import { Business, User, useAppStore } from '../store/appStore';
import { loadCredentials, clearCredentials, consumeTrustDeviceIntent } from '../utils/credentialManager';
import { decodeBase64Url } from '../utils/token-utils';
import { PermissionLevel, PermissionCatalogItem, PermissionSummary, BusinessPermissionProfile, levelRank } from '../constants/permissionLevels';
import { getDeviceId } from '../utils/deviceId';

// API Response Types
export interface ApiResponse<T> {
  data: T | null;
  statusCode: number;
  errors?: Array<{ message: string; code?: string }>;
  requestIdentifier?: string;
  messages?: string[];
  additionalInfo?: any;
  user?: any;
}

export interface DeliveryRetryStatus {
  taskId: string;
  enabled: boolean;
  state: string;
  attempts: number;
  nextAttemptAt?: string;
  lastAttemptAt?: string;
  lastError?: string | null;
  sourceMessageId: string;
}

/** Who a business calendar is shared with — the single mechanism for both
 * "assignment" (users/departments) and cross-business sharing on a Calendar.
 * Mirrors sendifier_api's Calendar['sharedWith'] shape exactly. */
export interface CalendarSharedWithEntry {
  userId?: string;
  departmentId?: string;
  businessId?: string;
  permission: 'view' | 'edit';
}

// ─── Visits (field visits, Phase 2) ────────────────────────────────────────
// Mirrors sendifier_api's Visit entity exactly (see modules/visits).

export type VisitStatus = 'pending' | 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'cancelled' | 'rescheduled';
export type VisitPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface VisitPhoto {
  url: string;
  mediaId: string;
  capturedAt: string;
  coordinates?: { latitude: number; longitude: number };
  phase: 'start' | 'end';
}

export interface VisitGeoStamp {
  latitude: number;
  longitude: number;
  accuracyMeters?: number;
  distanceMeters?: number;
  at: string;
  overrideReason?: string;
}

export interface VisitOutcomeRecord {
  outcomeId: string;
  label: string;
  resultType: 'success' | 'neutral' | 'failure';
  actionType?: string;
  actionResult?: Record<string, any>;
  recordedAt: string;
  recordedBy: string;
}

export interface VisitTargetLocation {
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  country?: string;
  isGeocoded?: boolean;
}

export interface VisitNote {
  id: string;
  text: string;
  addedBy: string;
  addedAt: string;
}

/** A single Overture Places record returned by /api/places — see
 * sendifier_api's Place entity (infra/places/build_places_db.sql) for the
 * full field set; only what the mobile app actually renders is declared
 * here. */
export interface Place {
  overture_id: string;
  longitude: number;
  latitude: number;
  name_primary: string;
  name_ar: string | null;
  name_en: string | null;
  category: string | null;
  address_freeform: string | null;
  locality: string | null;
  distance_meters?: number;
}

export interface VisitActivity {
  id: string;
  type: 'order' | 'quotation' | 'vendor' | 'reminder' | 'list' | 'assignment' | 'note';
  refId?: string;
  label?: string;
  at: string;
  by: string;
}

export interface Visit {
  _id: string;
  /** What was done for the contact during the visit. */
  activities?: VisitActivity[];
  businessId: string;
  campaignId?: string;
  contactId: string;
  targetLocation: VisitTargetLocation;
  assignedTo?: string;
  departmentId?: string;
  assignedBy?: string;
  assignedAt?: string;
  status: VisitStatus;
  priority: VisitPriority;
  scheduledFor?: string;
  dueBy?: string;
  expectedDurationMinutes: number;
  startedAt?: string;
  endedAt?: string;
  cancelledAt?: string;
  history?: Array<{ id: string; type: string; at: string; by?: string }>;
  actualDurationMinutes?: number;
  startGeoStamp?: VisitGeoStamp;
  endGeoStamp?: VisitGeoStamp;
  photos: VisitPhoto[];
  outcome?: VisitOutcomeRecord;
  calendarEventId?: string;
  parentVisitId?: string;
  followUpVisitId?: string;
  assignmentId?: string;
  notes: VisitNote[];
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface VisitOutcome {
  id: string;
  label: string;
  labelAr?: string;
  icon?: string;
  color?: string;
  resultType: 'success' | 'neutral' | 'failure';
  action?: { type: 'reschedule' | 'reminder' | 'assign' | 'note'; config?: Record<string, any> };
  order: number;
  isActive: boolean;
}

// ─── Visit Campaigns (field visits, Phase 3) ───────────────────────────────
// Mirrors sendifier_api's VisitCampaign entity exactly (see modules/visits).

export type VisitCampaignStatus = 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';

export interface VisitCampaign {
  _id: string;
  businessId: string;
  name: string;
  description?: string;
  status: VisitCampaignStatus;
  startDate: string;
  endDate: string;
  expectedDurationMinutes?: number;
  dailyVisitTarget?: number;
  outcomes?: VisitOutcome[];
  locationOverride?: Record<string, any>;
  photosOverride?: Record<string, any>;
  calendarOverride?: Record<string, any>;
  assignedDepartmentIds: string[];
  assignedUserIds: string[];
  contactCount: number;
  sourceUploadJobIds?: string[];
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Auth Interfaces
export interface LoginRequest {
  email: string;
  password: string;
  provider?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  phoneNumber?: string;
  provider?: string;
}

export interface LoginResponse {
  expiresIn: number;
  refreshToken: string;
  token: string;  // Backend returns "token", not "accessToken"
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    emailVerified: boolean;
  };
}

export interface RegisterResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    emailVerified: boolean;
  };
}

// Unified login: phone (WhatsApp OTP)/Google/Meta all mint a phase-1 ("no business
// selected yet") token — narrower than LoginResponse, no business/subscription context.
export interface Phase1TokenResponse {
  token: string;
  expiresIn: number;
  isNewUser: boolean;
  refreshToken: string;
}

export interface MembershipCard {
  businessId: string;
  businessName: string;
  logo?: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  subscriptionStatus?: string;
  billingState?: 'none' | 'free' | 'trial' | 'active' | 'grace' | 'locked';
  billingUntil?: string;
  /** Owner may delete it from the picker (server switch ALLOW_BUSINESS_DELETE). */
  canDelete?: boolean;
}

export interface PendingInvitation {
  invitationCode: string;
  businessId: string;
  businessName: string;
  businessLogo?: string;
  role: string;
  invitedByName?: string;
  expiresAt: string | null;
  isExpired: boolean;
}

// Business Interfaces
export interface CreateBusinessRequest {
  name: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  industry: string;
  description?: string;
  logo?: string;
  size?: string;
}

export interface JoinBusinessRequest {
  invitationCode: string;
}

// Dashboard / analytics interfaces — mirrors the real (no-longer-stubbed)
// shapes returned by sendifier_api's omni-channel/assignment stats endpoints.
export interface OmniChannelStats {
  contacts: { total: number; new: number; active: number };
  conversations: { total: number; active: number; resolved: number; averageResolutionTime: number };
  messages: {
    total: number;
    inbound: number;
    outbound: number;
    averageResponseTime: number;
    dailyVolume: { date: string; count: number }[];
  };
  channels: {
    total: number;
    active: number;
    error: number;
    whatsapp: { total: number; active: number; error: number };
    facebookMessenger: { total: number; active: number; error: number };
    xTwitter: { total: number; active: number; error: number };
    phone: { total: number; active: number; error: number };
  };
  // Phone call analytics — separate from channels.phone (account connection
  // status only) since calls have volume/outcome/duration data with no
  // equivalent in the message-based channels.
  calls: {
    total: number;
    inbound: number;
    outbound: number;
    averageDuration: number;
    dailyVolume: { date: string; count: number }[];
    outcomes: { completed: number; notResponded: number; cancelled: number; ended: number; failed: number };
    byProvider: { provider: string; total: number; averageDuration: number }[];
  };
  business: { name: string; subscriptionPlan: string; subscriptionStatus: string } | null;
  businessId: string;
  period: { startDate?: Date; endDate?: Date };
}

export type FlowSessionStatus = 'active' | 'waiting_on_child' | 'manually_paused' | 'completed' | 'abandoned';

export interface FlowSessionNodeError {
  nodeId: string;
  nodeName?: string;
  nodeType: string;
  message: string;
  at: string;
}

export interface FlowSessionItem {
  id: string;
  flowId: string;
  flowName: string;
  contactId: string;
  conversationId?: string;
  channel: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
  channelIdentifier: string;
  currentNodeId: string;
  status: FlowSessionStatus;
  endedReason?: 'timeout' | 'agent_reply' | 'manual' | 'flow_missing';
  nodeErrors?: FlowSessionNodeError[];
  waitUntil?: string; // held at a wait step until then
  startedAt: string;
  lastAdvancedAt: string;
}

export interface FlowSessionsResponse {
  items: FlowSessionItem[];
  total: number;
  limit: number;
  skip: number;
}

export interface AssignmentAgentBreakdown {
  userId: string;
  name: string;
  total: number;
  active: number;
  completed: number;
  overdue: number;
}

export interface AssignmentItem {
  _id: string;
  businessId: string;
  assignedTo?: string;
  departmentId?: string;
  assignedBy: string;
  assignmentType: string;
  targetType: string;
  targetId: string;
  title: string;
  description?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'active' | 'inProgress' | 'locked' | 'completed' | 'cancelled' | 'transferred';
  dueDate?: string;
  assignedAt: string;
  completedAt?: string;
}

export interface AssignmentListResponse {
  items: AssignmentItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface AssignmentStats {
  totalAssignments: number;
  activeAssignments: number;
  completedAssignments: number;
  overdueAssignments: number;
  assignmentsByType: Record<string, number>;
  assignmentsByPriority: Record<string, number>;
  assignmentsByStatus: Record<string, number>;
  averageCompletionTime: number;
  completionRate: number;
  byAgent?: AssignmentAgentBreakdown[];
}

export interface DashboardCampaign {
  id: string;
  taskId: string;
  name: string;
  platform: string;
  status: string;
  progress: number;
  sent: number;
  delivered: number;
  opened: number;
  read: number;
  contactReplied: number;
  handledConversations: number;
  endDate?: string;
  nextExecutionAt?: string;
  lastExecutedAt?: string;
  createdAt: string;
  totalContacts: number;
  successful: number;
  failed: number;
}

// API Service Class
export interface HomeDailyPoint { date: string; count: number }
export interface HomeAgendaItem { kind: 'visit' | 'event'; id: string; title: string; subtitle?: string; at: string; status?: string }
export interface HomeTeamRow { userId: string; name?: string; messages: number; calls: number; visits: number; talkTimeSeconds: number }
export interface HomeDashboardData {
  scope: { team: boolean };
  attention: {
    overdueAssignments: number;
    visitsToday: number;
    visitsOverdue: number;
    eventsToday: number;
    pendingOrders: number;
    expiringQuotations: number;
  };
  kpis: {
    messages: { sent: number; received: number };
    customersReached: number;
    calls: { total: number; answered: number; missedInbound: number; talkTimeSeconds: number };
    visits: { total: number; completed: number; open: number } | null;
    orders: { count: number; revenue: { currency: string; total: number }[] } | null;
    quotations: { created: number; accepted: number } | null;
  };
  trend: { messages: HomeDailyPoint[]; calls: HomeDailyPoint[]; visits: HomeDailyPoint[] };
  agenda: HomeAgendaItem[];
  team: HomeTeamRow[];
}

let businessLockedHandler: (() => void) | null = null;
/** Called on any 423 (business locked) response — AppNavigator opens billing. */
export const setBusinessLockedHandler = (handler: (() => void) | null) => { businessLockedHandler = handler; };

export type BillingCycle = 'monthly' | 'quarterly' | 'yearly';
export interface BillingPlan {
  _id: string;
  name: string;
  code: string;
  description?: string;
  features: string[];
  currency: string;
  moPrice: number;
  quartPrice: number;
  yearPrice: number;
  isFree: boolean;
  isPopular: boolean;
  trialDays: number;
  graceDays?: number;
  trialGraceDays?: number;
  translations?: Record<string, { name?: string; description?: string; features?: string[] }>;
  entitlements?: BillingEntitlements | null;
  services: Record<string, any>;
}
export interface BillingEntitlements { modules: string[]; limits: Record<string, number> }
export interface BillingFeature {
  key: string; kind: 'module' | 'limit'; group: string; comingSoon: boolean;
  label: { en: string; ar: string }; unit?: { en: string; ar: string };
}
export interface BillingAddon {
  code: string; name: string; description?: string; translations?: Record<string, { name?: string; description?: string }>;
  kind: 'quantity' | 'module'; feature: string; unitSize: number; maxUnits: number;
  currency: string; moPrice: number; quartPrice: number; yearPrice: number; plans: string[];
}
export interface BillingCatalog { features: BillingFeature[]; addons: BillingAddon[]; enforced: boolean }
export interface AddonQuote {
  lines: Array<{ code: string; description: string; quantity: number; unitPrice: number; amount: number }>;
  total: number; currency: string; daysLeft: number; periodEnd: string; billingCycle: BillingCycle;
}
export interface BillingOverview {
  state: 'none' | 'free' | 'trial' | 'active' | 'grace' | 'expired';
  businessLocked: boolean;
  canManage: boolean;
  trialAvailable: boolean;
  daysLeft: number;
  /** In grace: when access stops unless renewed. */
  lockAt?: string | null;
  graceDaysLeft?: number;
  wasTrial?: boolean;
  paymentsEnabled: boolean;
  subscription: { _id: string; planId: string; billingCycle: BillingCycle; currentPeriodStart: string; currentPeriodEnd: string; isTrial: boolean; status: number } | null;
  plan: { _id: string; name: string; code: string; currency: string; moPrice: number; quartPrice: number; yearPrice: number; isFree: boolean } | null;
  payments: Array<{ _id: string; amount: number; currency: string; status: string; paidAt?: string; createdAt: string; billingCycle?: BillingCycle; reference?: string; periodEnd?: string; failureReason?: string; invoiceNumber?: string; invoiceEmailedAt?: string; kind?: 'plan' | 'addon' }>;
  addons?: Array<{ code: string; quantity: number; renewQuantity: number }>;
  entitlements?: BillingEntitlements & { legacy?: boolean };
}
export interface BillingPaymentStatus {
  paymentId: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'expired';
  amount: number;
  currency: string;
  planId?: string;
  billingCycle?: BillingCycle;
  periodEnd?: string;
  failureReason?: string;
  kind?: 'plan' | 'addon';
}

export class ApiService implements TokenProvider {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;
  private authToken: string | null = null;
  private refreshToken: string | null = null;
  private onAuthError: (() => void) | null = null;
  private silentLoginPromise: Promise<boolean> | null = null;

  constructor() {
    try {
      const config = getCurrentApiConfig();
      this.baseURL = config?.baseURL?.trim();
      this.timeout = config?.timeout || 15000;
      this.defaultHeaders = DEFAULT_HEADERS;
      
      // Register this service as the token provider
      tokenManager.setTokenProvider(this);
      
      console.log('🔍 [ApiService] Initialized with config:', {
        baseURL: this.baseURL,
        timeout: this.timeout,
        headers: this.defaultHeaders,
        configSource: config,
        environment: getCurrentEnvironment(),
        configBaseURL: config?.baseURL,
        configBaseURLTrimmed: config?.baseURL?.trim(),
        finalBaseURL: this.baseURL
      });
      
      // CRITICAL: Load persisted tokens from AsyncStorage on initialization
      this.loadPersistedTokens().catch(err => {
        console.error('❌ [ApiService] Failed to load persisted tokens:', err);
      });
    } catch (error) {
      console.error('❌ [ApiService] Error initializing:', error);
      // Fallback configuration - use staging for EAS builds
      const isEASBuild = process.env.EXPO_PUBLIC_EAS_BUILD === 'true';
      this.baseURL = isEASBuild ? 'https://api-dev.sendifier.com' : 'https://api-dev.sendifier.com';
      this.timeout = 15000;
      this.defaultHeaders = DEFAULT_HEADERS;
      tokenManager.setTokenProvider(this);
      
      console.log('🔍 [ApiService] Using fallback config:', {
        baseURL: this.baseURL,
        timeout: this.timeout,
        isEASBuild,
        error: (error as Error).message,
        stack: (error as Error).stack
      });
      
      // Test network connectivity
      this.testConnectivity();
      
      // CRITICAL: Load persisted tokens even in fallback
      this.loadPersistedTokens().catch(err => {
        console.error('❌ [ApiService] Failed to load persisted tokens in fallback:', err);
      });
    }
  }

  // CRITICAL: Load persisted tokens from AsyncStorage on initialization
  private async loadPersistedTokens(): Promise<void> {
    try {
      console.log('🔍 [ApiService] Loading persisted tokens from AsyncStorage...');
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      const storedData = await AsyncStorage.getItem('sendifier-app-store');
      
      if (storedData) {
        const parsed = JSON.parse(storedData);
        if (parsed.state?.tokens?.token) {
          this.authToken = parsed.state.tokens.token;
          this.refreshToken = parsed.state.tokens.refreshToken || null;
          console.log('✅ [ApiService] Loaded persisted tokens:', {
            hasToken: !!this.authToken,
            hasRefreshToken: !!this.refreshToken,
            tokenPreview: this.authToken ? this.authToken.substring(0, 20) + '...' : 'NONE'
          });
        } else {
          console.log('⚠️ [ApiService] No tokens found in persisted data');
        }
      } else {
        console.log('⚠️ [ApiService] No persisted data found in AsyncStorage');
      }
    } catch (error) {
      console.error('❌ [ApiService] Error loading persisted tokens:', error);
    }
  }

  // Set authentication token
  setAuthToken(token: string | null): void {
    this.authToken = token;
    console.log('🔑 [ApiService] Auth token set:', token ? 'Token available' : 'Token cleared');
  }

  // Set callback for authentication errors (e.g., redirect to login)
  setOnAuthError(callback: (() => void) | null): void {
    this.onAuthError = callback;
  }

  // Test network connectivity
  private async testConnectivity(): Promise<void> {
    try {
      console.log('🔍 [ApiService] Testing connectivity to:', this.baseURL);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${this.baseURL}/health`, {
        method: 'GET',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      console.log('✅ [ApiService] Connectivity test successful:', {
        status: response.status,
        statusText: response.statusText
      });
    } catch (error) {
      console.error('❌ [ApiService] Connectivity test failed:', {
        baseURL: this.baseURL,
        error: (error as Error).message
      });
    }
  }

  // Extract business ID from JWT token
  getBusinessIdFromToken(): string | null {
    if (!this.authToken) {
      return null;
    }
    
    try {
      const tokenPayload = JSON.parse(decodeBase64Url(this.authToken.split('.')[1]));
      return tokenPayload.businessId || tokenPayload.defaultBusinessId || null;
    } catch (error) {
      return null;
    }
  }

  // Set refresh token
  setRefreshToken(refreshToken: string | null): void {
    this.refreshToken = refreshToken;
  }

  // Get current authentication token
  getAuthToken(): string | null {
    // First, check memory (fastest path)
    if (this.authToken) {
      return this.authToken;
    }

    // Second, try to get from store
    try {
      const tokens = useAppStore.getState().tokens;
      if (tokens?.token) {
        console.log('🔄 [ApiService] getAuthToken: Found token in store, syncing to API service...');
        this.authToken = tokens.token;
        if (tokens.refreshToken) {
          this.refreshToken = tokens.refreshToken;
        }
        return this.authToken;
      }
    } catch (error) {
      // Silently fail - store might not be available in all contexts
    }
    
    // Third, try AsyncStorage as a fallback (important for Android timing issues)
    // This is a synchronous wrapper - we'll make it async-compatible in authenticatedRequest
    try {
      const AsyncStorage = require('@react-native-async-storage/async-storage').default;
      // Note: This is synchronous check only, actual async loading happens in loadPersistedTokens
      console.log('⚠️ [ApiService] getAuthToken: Token not in memory or store, check debug logs for persistence issues');
    } catch (error) {
      // Silently fail
    }

    return null;
  }

  // Get current refresh token
  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  // Get base URL (required by TokenProvider interface)
  getBaseURL(): string {
    return this.baseURL;
  }

  // Update base URL (useful for environment changes)
  updateBaseURL(): void {
    const newBaseURL = getCurrentApiConfig().baseURL.trim();
    this.baseURL = newBaseURL;
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<ApiResponse<T>> {
    if (!endpoint) {
      throw new Error('API request called with an undefined endpoint — check API_ENDPOINTS for a missing entry.');
    }
    const url = `${this.baseURL}${endpoint}`;

    console.log('🔍 [ApiService] Making request:', {
      url,
      baseURL: this.baseURL,
      endpoint,
      fullUrl: url,
      retryCount
    });
    
    // Prepare headers with authentication
    const headers: Record<string, string> = {
      ...this.defaultHeaders,
      ...(options.headers as Record<string, string> || {}),
    };

    // CRITICAL: If no auth token but request requires auth, try to load from storage one more time
    if (!this.authToken && !headers.Authorization && endpoint.includes('/api/')) {
      console.log('⚠️ [ApiService] No auth token for authenticated request, attempting to reload from storage...');
      try {
        await this.loadPersistedTokens();
        if (this.authToken) {
          console.log('✅ [ApiService] Successfully reloaded token from storage');
        } else if (this.refreshToken) {
          // Access token missing (e.g. a cold tab reload raced this request ahead of
          // rehydration) but a refresh token is available — exchange it before letting
          // this request go out unauthenticated.
          console.log('⚠️ [ApiService] No access token, but refresh token found — attempting refresh...');
          const refreshed = await tokenManager.refreshTokens();
          if (refreshed?.token) {
            console.log('✅ [ApiService] Refreshed token before initial request');
          } else {
            console.log('❌ [ApiService] Refresh failed, request will fail');
          }
        } else {
          console.log('❌ [ApiService] No token found in storage, request will fail');
        }
      } catch (err) {
        console.error('❌ [ApiService] Failed to reload token:', err);
      }
    }
    
    // Automatically add Authorization header if token is available
    if (this.authToken && !headers.Authorization) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    // Identify this device/app-install so the backend can scope refresh-token sliding
    // windows (trusted vs untrusted) per device instead of per user account.
    if (!headers['X-Device-Id']) {
      try {
        headers['X-Device-Id'] = await getDeviceId();
      } catch {
        // best-effort — refresh still works without it, just without device-level isolation
      }
    }

    const config: RequestInit = {
      headers,
      ...options,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      
      // Handle 401 Unauthorized - try to refresh token and retry (with retry limit)
      if (response.status === 401 && this.authToken && retryCount < 1) {
        try {
          // Use centralized token manager to refresh tokens
          const refreshResult = await tokenManager.refreshTokens();
          if (refreshResult) {
            // Retry the original request with new token (increment retry count)
            return this.request<T>(endpoint, options, retryCount + 1);
          } else {
            // Refresh failed, clear tokens and throw error
            this.authToken = null;
            this.refreshToken = null;
            throw new Error('Authentication failed - token expired and refresh failed. Please login again.');
          }
        } catch (refreshError) {
          // Clear invalid tokens and throw error
          this.authToken = null;
          this.refreshToken = null;
          throw new Error('Authentication failed - token expired. Please login again.');
        }
      }
      
      if (response.status === 423) {
        // Business locked (subscription ended) — let the app send the user
        // to billing instead of surfacing a raw error everywhere.
        try { businessLockedHandler?.(); } catch { /* best-effort */ }
      }

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = null;
        
        try {
          errorData = await response.json();
          if (errorData.errors && errorData.errors.length > 0) {
            const first = errorData.errors[0];
            // Some public routes return plain-string errors.
            errorMessage = (typeof first === 'string' ? first : first?.message) || errorMessage;
          }
        } catch (parseError) {
          // Failed to parse error response
        }
        
        // Create a structured error that preserves all backend error data
        const structuredError = new Error(errorMessage);
        (structuredError as any).statusCode = response.status;
        (structuredError as any).data = errorData;
        (structuredError as any).errors = errorData?.errors || [];
        (structuredError as any).message = errorMessage;
        
        throw structuredError;
      }

      const responseData = await response.json();
      
      return responseData;
    } catch (error: any) {
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection and try again');
      }

      // A structured server error (non-2xx, built above) — keep its
      // statusCode/errors so callers can show the server's own message.
      if (typeof error.statusCode === 'number' && error.statusCode !== 401 && !String(error.message || '').includes('jwt expired') && !String(error.message || '').startsWith('HTTP error! status:')) {
        throw error;
      }
      
      // Handle JWT expiration errors specifically
      if (error.message && error.message.includes('jwt expired')) {
        console.log('🔄 [ApiService] JWT expired error detected. Attempting recovery...');

        if (this.authToken && retryCount < 1) {
          try {
            console.log('🔄 [ApiService] Attempting token refresh via tokenManager...');
            const refreshResult = await tokenManager.refreshTokens();
            if (refreshResult) {
              console.log('✅ [ApiService] Token refresh succeeded. Retrying original request.');
              return this.request<T>(endpoint, options, retryCount + 1);
            }
            console.warn('⚠️ [ApiService] Token refresh returned null. Falling back to remember-me credentials.');
          } catch (refreshError) {
            console.error('❌ [ApiService] Token refresh threw an error:', refreshError);
          }
        }

        const silentLoginSucceeded = await this.attemptSilentReauthentication();

        if (silentLoginSucceeded && retryCount < 2) {
          console.log('✅ [ApiService] Remember-me reauthentication succeeded. Retrying request.');
          return this.request<T>(endpoint, options, retryCount + 1);
        }

        console.error('❌ [ApiService] Automatic recovery failed. Clearing tokens and redirecting to login.');
        this.authToken = null;
        this.refreshToken = null;

        if (this.onAuthError) {
          console.log('🔄 [ApiService] Calling onAuthError callback to redirect to login');
          this.onAuthError();
        }

        throw new Error('Authentication failed - session expired. Please login again.');
      }
      
      // If it's already a formatted error message, pass it through
      if (error.message && !error.message.includes('HTTP error! status:')) {
        throw new Error(error.message);
      }
      
      // Provide more specific error messages based on error type
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.name === 'AbortError') {
        throw new Error('Request timeout. Please check your connection and try again.');
      } else {
        throw new Error('Network error occurred - please check your connection and try again');
      }
    }
  }

  // Auth Methods
  async login(credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<RegisterResponse>> {
    
    const response = await this.request<RegisterResponse>(API_ENDPOINTS.AUTH.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    return response;
  }

  async refreshAuthToken(refreshToken: string): Promise<ApiResponse<LoginResponse>> {
    return this.request<LoginResponse>(API_ENDPOINTS.AUTH.REFRESH, {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Unified login: phone (WhatsApp OTP), social providers, business picker
  // ─────────────────────────────────────────────────────────────────────────

  async sendPhoneOTP(mobile: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.PHONE_SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ mobile }),
    });
  }

  async verifyPhoneOTP(mobile: string, code: string, trustDevice: boolean = false): Promise<ApiResponse<Phase1TokenResponse>> {
    const deviceId = await getDeviceId();
    return this.request<Phase1TokenResponse>(API_ENDPOINTS.AUTH.PHONE_VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ mobile, code, deviceId, trustDevice }),
    });
  }

  async sendEmailLoginOTP(email: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.EMAIL_SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyEmailLoginOTP(email: string, code: string, trustDevice: boolean = false): Promise<ApiResponse<Phase1TokenResponse>> {
    const deviceId = await getDeviceId();
    return this.request<Phase1TokenResponse>(API_ENDPOINTS.AUTH.EMAIL_VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ email, code, deviceId, trustDevice }),
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Attach a second identity (email or mobile) to the currently authenticated
  // user — the "complete your profile" second-auth-method flow.
  // ─────────────────────────────────────────────────────────────────────────

  async sendAttachPhoneOTP(mobile: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.IDENTITY_PHONE_SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ mobile }),
    });
  }

  async verifyAttachPhoneOTP(mobile: string, code: string): Promise<ApiResponse<{ mobile: string; mobileVerified: boolean }>> {
    return this.request<{ mobile: string; mobileVerified: boolean }>(API_ENDPOINTS.AUTH.IDENTITY_PHONE_VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ mobile, code }),
    });
  }

  async sendAttachEmailOTP(email: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.IDENTITY_EMAIL_SEND_OTP, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyAttachEmailOTP(email: string, code: string): Promise<ApiResponse<{ email: string; emailVerified: boolean }>> {
    return this.request<{ email: string; emailVerified: boolean }>(API_ENDPOINTS.AUTH.IDENTITY_EMAIL_VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  async socialLoginGoogle(idToken: string): Promise<ApiResponse<Phase1TokenResponse>> {
    return this.request<Phase1TokenResponse>(API_ENDPOINTS.AUTH.SOCIAL_GOOGLE, {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  }

  async socialLoginMeta(code: string, redirectUri: string): Promise<ApiResponse<Phase1TokenResponse>> {
    return this.request<Phase1TokenResponse>(API_ENDPOINTS.AUTH.SOCIAL_META, {
      method: 'POST',
      body: JSON.stringify({ code, redirectUri }),
    });
  }

  async socialLoginApple(identityToken: string): Promise<ApiResponse<Phase1TokenResponse>> {
    return this.request<Phase1TokenResponse>(API_ENDPOINTS.AUTH.SOCIAL_APPLE, {
      method: 'POST',
      body: JSON.stringify({ identityToken }),
    });
  }

  async deleteOwnedBusiness(businessId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.request<{ deleted: boolean }>(`${API_ENDPOINTS.AUTH.DELETE_BUSINESS}/${encodeURIComponent(businessId)}`, {
      method: 'DELETE',
    });
  }

  /** Deletes the signed-in user's account (DELETE /api/users/me — marks it deleted). */
  async deleteAccount(): Promise<ApiResponse<boolean>> {
    return this.request<boolean>('/api/users/me', { method: 'DELETE' });
  }

  async getMemberships(): Promise<ApiResponse<MembershipCard[]>> {
    return this.request<MembershipCard[]>(API_ENDPOINTS.AUTH.MEMBERSHIPS, {
      method: 'GET',
    });
  }

  async getPendingInvitations(): Promise<ApiResponse<PendingInvitation[]>> {
    return this.authenticatedRequest<PendingInvitation[]>('/api/business/invitations', {
      method: 'GET',
    });
  }

  async acceptInvitationByCode(invitationCode: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/business/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ invitationCode }),
    });
  }

  async declineInvitation(invitationCode: string): Promise<ApiResponse<void>> {
    return this.authenticatedRequest<void>(`/api/business/invitations/${invitationCode}/decline`, {
      method: 'POST',
    });
  }

  async selectBusiness(businessId: string): Promise<ApiResponse<LoginResponse>> {
    const trustDevice = await consumeTrustDeviceIntent();
    let deviceId: string | undefined;
    try {
      deviceId = await getDeviceId();
    } catch {
      // best-effort
    }
    return this.request<LoginResponse>(API_ENDPOINTS.AUTH.SELECT_BUSINESS, {
      method: 'POST',
      body: JSON.stringify({ businessId, trustDevice, deviceId }),
    });
  }

  async selectPlan(businessId: string, planId: string): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.SUBSCRIPTION.SELECT_PLAN, {
      method: 'POST',
      body: JSON.stringify({ businessId, planId }),
    });
  }

  async logout(token: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.LOGOUT, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // Generic authenticated request method for external use
  async authenticatedRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs?: number
  ): Promise<ApiResponse<T>> {
    // Force refresh base URL to ensure we have the latest config
    this.updateBaseURL();
    
    const url = `${this.baseURL}${endpoint}`;
    
    // Ensure token is loaded - try to reload from storage if missing
    if (!this.authToken) {
      console.log('⚠️ [ApiService] No token in memory, attempting to reload from storage...');
      try {
        await this.loadPersistedTokens();
        console.log('🔍 [ApiService] After reload, hasToken:', !!this.authToken);
      } catch (err) {
        console.error('❌ [ApiService] Failed to reload token from storage:', err);
      }
    }
    
    // Debug logging
    console.log('🔍 [ApiService] Making authenticated request:', {
      url,
      baseURL: this.baseURL,
      endpoint,
      hasToken: !!this.authToken,
      tokenPreview: this.authToken ? `${this.authToken.substring(0, 20)}...` : 'NO TOKEN',
      headers: options.headers
    });
    
    // Prepare headers with authentication
    // IMPORTANT: Start with options.headers first, then merge defaultHeaders
    // This ensures Authorization header from options (if any) takes precedence
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
      ...this.defaultHeaders,
    };

    // CRITICAL: Always check and add Authorization header AFTER merging
    // This ensures it's always present and not overwritten
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
      console.log('✅ [ApiService] Added Authorization header');
    } else {
      // Whether there was ever a session to lose. A visitor who never signed
      // in (public storefront pages: catalog/product/cart/checkout/tracking)
      // has neither token — background code that fires an authenticated call
      // for them must just fail, NOT trigger the "session expired → login"
      // redirect that previously bounced every catalog visitor to sign-in.
      let hadSession = !!this.refreshToken;
      // If still no token after reload attempt, try to get from store
      console.log('⚠️ [ApiService] Still no token, checking if we can get from store...');
      try {
        // Try to import and get token from store
        const { useAppStore } = await import('../store/appStore');
        const tokens = useAppStore.getState().tokens;
        hadSession = hadSession || !!tokens?.token || !!tokens?.refreshToken;
        if (tokens?.token) {
          this.authToken = tokens.token;
          headers.Authorization = `Bearer ${this.authToken}`;
          console.log('✅ [ApiService] Got token from store and added Authorization header');
        } else if (tokens?.refreshToken || this.refreshToken) {
          // No access token in memory or storage, but a refresh token exists (e.g. a cold
          // tab reload that lost in-memory state before the access token's own expiry) —
          // exchange it before giving up, instead of forcing a full re-login.
          console.log('⚠️ [ApiService] No access token, but refresh token found — attempting refresh...');
          this.refreshToken = tokens?.refreshToken || this.refreshToken;
          const refreshed = await tokenManager.refreshTokens();
          if (refreshed?.token) {
            this.authToken = refreshed.token;
            headers.Authorization = `Bearer ${this.authToken}`;
            console.log('✅ [ApiService] Refreshed token and added Authorization header');
          } else {
            throw new Error('Token refresh failed');
          }
        } else {
          throw new Error('No token found in store either');
        }
      } catch (storeErr: any) {
        // If still no token after all attempts (including a refresh-token exchange
        // above), this is a genuinely expired/absent session — throw AND trigger the
        // same redirect-to-login flow used for a 401/expired-JWT, instead of leaving
        // the caller with a bare rejected promise and no navigation.
        const error = new Error('Authorization header is required');
        console.error('❌ [ApiService] No auth token available for authenticated request:', {
          endpoint,
          url,
          hasToken: !!this.authToken,
          storeError: storeErr?.message,
        });
        this.authToken = null;
        this.refreshToken = null;
        if (hadSession && this.onAuthError) {
          this.onAuthError();
        }
        throw error;
      }
    }
    
    // Final verification - log the headers that will be sent
    console.log('🔍 [ApiService] Final request headers:', {
      hasAuthorization: !!headers.Authorization,
      authorizationPreview: headers.Authorization ? `${headers.Authorization.substring(0, 30)}...` : 'MISSING',
      allHeaders: Object.keys(headers),
    });

    // Add business ID header for all authenticated requests
    const businessId = this.getBusinessIdFromToken();
    if (businessId) {
      headers['x-business-id'] = businessId;
    }

    // Generate request ID for tracking
    const requestId = `mobile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    headers['x-request-id'] = requestId;
    
    // CRITICAL: Create config without spreading options.headers to prevent overwriting our merged headers
    const { headers: optionsHeaders, ...restOptions } = options;
    const config: RequestInit = {
      ...restOptions,
      headers, // Use our merged headers object
    };
    
    // Final check: Ensure Authorization header is present
    if (!config.headers || !(config.headers as Record<string, string>).Authorization) {
      console.error('❌ [ApiService] CRITICAL: Authorization header missing in final config!', {
        hasHeaders: !!config.headers,
        headerKeys: config.headers ? Object.keys(config.headers as Record<string, string>) : [],
      });
      throw new Error('Authorization header is required');
    }

    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (options.signal?.aborted) cancel();
    else options.signal?.addEventListener('abort', cancel, { once: true });
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      const effectiveTimeout = typeof timeoutMs === 'number' ? timeoutMs : this.timeout;
      timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = null;
        
        try {
          errorData = await response.json();
          if (errorData.errors && errorData.errors.length > 0) {
            const e0 = errorData.errors[0];
            errorMessage = typeof e0 === 'string' ? e0 : (e0?.message || errorMessage);
          }
        } catch (parseError) {
          // Ignore parse errors
        }
        
        // Create a structured error that preserves all backend error data
        const structuredError = new Error(errorMessage);
        (structuredError as any).statusCode = response.status;
        (structuredError as any).data = errorData;
        (structuredError as any).errors = errorData?.errors || [];
        (structuredError as any).message = errorMessage;
        
        throw structuredError;
      }

      return await response.json();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection and try again');
      }
      
      // Structured HTTP error (4xx/5xx): we threw it above with .data = full API body.
      // Rethrow so callers can handle statusCode 1001 (e.g. already_in_use) and other cases.
      if (error.data != null) {
        throw error;
      }
      
      if (error.message && !error.message.includes('HTTP error! status:')) {
        throw new Error(error.message);
      }
      
      throw new Error('Network error occurred - please check your connection and try again');
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      options.signal?.removeEventListener('abort', cancel);
    }
  }

  async validateToken(token: string): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.AUTH.VALIDATE, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  async getProfile(token: string): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.AUTH.PROFILE, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${token}`,
      },
    });
  }

  // Password Management
  async forgotPassword(email: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ email, code, newPassword }),
    });
  }

  // Email Verification
  async sendVerificationEmail(email: string): Promise<ApiResponse<void>> {
    return this.request<void>(API_ENDPOINTS.AUTH.SEND_VERIFICATION, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyEmail(email: string, code: string): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.AUTH.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  // Profile Management
  async getUserProfile(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/users/profile', {
      method: 'GET',
    });
  }

  async updateProfile(data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    mobile?: string;
    profilePicture?: string;
    timezone?: string;
    timezoneOffset?: string;
    preferences?: Record<string, any>;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Upload profile picture
  async uploadProfilePicture(fileUri: string, fileName: string, fileType: string): Promise<ApiResponse<{ avatar?: string; profilePicture?: string; mediaUrl?: string; url?: string; [key: string]: any }>> {
    console.log('🔍 [ApiService] Uploading profile picture:', { 
      fileUri: fileUri.substring(0, 50) + '...', 
      fileName, 
      fileType,
      uriType: fileUri.substring(0, 10) // blob:, data:, file:, etc.
    });
    
    try {
      // Handle blob URLs and data URLs by converting them to File objects
      let fileToUpload: File | { uri: string; type: string; name: string };
      
      if (fileUri.startsWith('blob:')) {
        // Convert blob URL to File object for web
        console.log('🔍 [ApiService] Converting blob URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted blob to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert blob URL to File:', error);
          throw new Error('Failed to process profile picture. Please try again.');
        }
      } else if (fileUri.startsWith('data:')) {
        // Convert data URL to File object for web
        console.log('🔍 [ApiService] Converting data URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted data URL to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert data URL to File:', error);
          throw new Error('Failed to process profile picture. Please try again.');
        }
      } else {
        // For React Native, use the original format
        console.log('🔍 [ApiService] Using React Native file format');
        fileToUpload = {
          uri: fileUri,
          type: fileType,
          name: fileName,
        };
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', fileToUpload as any);
      
      const config = getCurrentApiConfig();
      const baseURL = config?.baseURL?.trim() || '';
      const url = `${baseURL}/api/users/profile/picture`;
      
      // Use getAuthToken() instead of tokenManager.getToken()
      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      
      // Don't set Content-Type for FormData - let the browser/React Native set it with boundary
      console.log('📤 [ApiService] Uploading profile picture to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        console.error('❌ [ApiService] Profile picture upload failed:', responseData);
        return {
          data: null,
          statusCode: response.status,
          errors: responseData.errors || [{ message: responseData.message || 'Failed to upload profile picture' }],
        };
      }
      
      console.log('✅ [ApiService] Profile picture uploaded successfully:', responseData);
      
      // The backend returns: { data: {...user object with avatar/profilePicture...}, statusCode, errors, ... }
      // We need to extract the data property correctly
      const responsePayload = responseData.data || responseData;
      
      console.log('🔍 [ApiService] Response payload:', {
        hasData: !!responsePayload,
        dataType: typeof responsePayload,
        dataKeys: responsePayload && typeof responsePayload === 'object' ? Object.keys(responsePayload) : [],
        hasAvatar: responsePayload?.avatar ? 'yes' : 'no',
        hasProfilePicture: responsePayload?.profilePicture ? 'yes' : 'no',
        hasMediaUrl: responsePayload?.mediaUrl ? 'yes' : 'no'
      });
      
      return {
        data: responsePayload,
        statusCode: responseData.statusCode || 1000,
        errors: responseData.errors || [],
      };
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading profile picture:', error);
      return {
        data: null,
        statusCode: 500,
        errors: [{ message: error.message || 'Failed to upload profile picture' }],
      };
    }
  }

  async uploadBusinessLogo(businessId: string, fileUri: string, fileName: string, fileType: string): Promise<ApiResponse<{ logo?: string; [key: string]: any }>> {
    try {
      let fileToUpload: File | { uri: string; type: string; name: string };

      if (fileUri.startsWith('blob:') || fileUri.startsWith('data:')) {
        const response = await fetch(fileUri);
        const blob = await response.blob();
        fileToUpload = new File([blob], fileName, { type: fileType });
      } else {
        fileToUpload = { uri: fileUri, type: fileType, name: fileName };
      }

      const formData = new FormData();
      formData.append('file', fileToUpload as any);

      const config = getCurrentApiConfig();
      const baseURL = config?.baseURL?.trim() || '';
      const url = `${baseURL}/api/businesses/${businessId}/logo`;

      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const responseData = await response.json();
      if (!response.ok) {
        return {
          data: null,
          statusCode: response.status,
          errors: responseData.errors || [{ message: responseData.message || 'Failed to upload business logo' }],
        };
      }

      return {
        data: responseData.data || responseData,
        statusCode: responseData.statusCode || 1000,
        errors: responseData.errors || [],
      };
    } catch (error: any) {
      return {
        data: null,
        statusCode: 500,
        errors: [{ message: error.message || 'Failed to upload business logo' }],
      };
    }
  }

  // Timezone Management
  async updateUserTimezone(data: {
    timezone: string;
    timezoneOffset: string;
    country?: string;
    city?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/users/profile/timezone', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Invitation Management
  async acceptInvitation(token: string): Promise<ApiResponse<any>> {
    return this.request<any>('/api/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async setPasswordForInvitedUser(token: string, password: string): Promise<ApiResponse<any>> {
    return this.request<any>('/api/invitations/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Passwordless counterpart to setPasswordForInvitedUser — the caller must already hold a
  // phase-1 token from a successful email OTP verification for the invited address.
  async activateInvitation(invitationToken: string): Promise<ApiResponse<any>> {
    return this.request<any>('/api/invitations/activate', {
      method: 'POST',
      body: JSON.stringify({ token: invitationToken }),
    });
  }

  // Business Management
  async createBusiness(businessData: CreateBusinessRequest): Promise<ApiResponse<Business>> {
    console.log('🔍 [ApiService] createBusiness called with:', businessData);
    console.log('🔍 [ApiService] Using endpoint:', API_ENDPOINTS.BUSINESS.CREATE);
    console.log('🔍 [ApiService] Auth token available:', !!this.authToken);
    console.log('🔍 [ApiService] Auth token preview:', this.authToken ? `${this.authToken.substring(0, 20)}...` : 'NO TOKEN');

    try {
      // Prepare headers - include Authorization if token is available
      const headers: Record<string, string> = {
        ...this.defaultHeaders,
      };

      // Add Authorization header if token is available (optional but recommended)
      if (this.authToken) {
        headers.Authorization = `Bearer ${this.authToken}`;
        console.log('🔑 [ApiService] Including Authorization header for business creation');
        console.log('🔑 [ApiService] Full Authorization header:', `Bearer ${this.authToken.substring(0, 20)}...`);
      } else {
        console.log('⚠️ [ApiService] No auth token available for business creation');
      }

      console.log('📤 [ApiService] Final headers:', headers);

      const response = await this.request<Business>(API_ENDPOINTS.BUSINESS.CREATE, {
        method: 'POST',
        headers,
        body: JSON.stringify(businessData),
      });

      console.log('✅ [ApiService] createBusiness response:', response);
      return response;
    } catch (error) {
      console.log('❌ [ApiService] createBusiness error:', error);
      throw error;
    }
  }

  async getBusinesses(): Promise<ApiResponse<Business[]>> {
    return this.request<Business[]>(API_ENDPOINTS.BUSINESS.LIST);
  }

  /** Location + timezone/currency/working hours of the current business (owner/admin). */
  async updateBusinessSettings(settings: Record<string, any>): Promise<ApiResponse<Business>> {
    return this.request<Business>(API_ENDPOINTS.BUSINESS.SETTINGS, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getBusinessById(id: string): Promise<ApiResponse<Business>> {
    return this.request<Business>(`${API_ENDPOINTS.BUSINESS.GET_BY_ID}/${id}`);
  }

  async updateBusiness(id: string, businessData: Partial<CreateBusinessRequest>): Promise<ApiResponse<Business>> {
    return this.request<Business>(`${API_ENDPOINTS.BUSINESS.UPDATE}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(businessData),
    });
  }

  async deleteBusiness(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`${API_ENDPOINTS.BUSINESS.DELETE}/${id}`, {
      method: 'DELETE',
    });
  }

  async joinBusiness(invitationCode: string): Promise<ApiResponse<Business>> {
    return this.request<Business>(API_ENDPOINTS.BUSINESS.JOIN, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${this.authToken}`,
      },
      body: JSON.stringify({ invitationCode }),
    });
  }

  async getBusinessProfile(): Promise<ApiResponse<Business>> {
    return this.request<Business>(API_ENDPOINTS.BUSINESS.PROFILE, {
      method: 'GET',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${this.authToken}`,
      },
    });
  }

  // Subscription Methods
  async getSubscriptionPlans(filter?: { status?: number }): Promise<ApiResponse<any[]>> {
    console.log('🔍 [ApiService] Getting subscription plans...');
    
    // Build query string with filter
    const queryParams = new URLSearchParams();
    if (filter?.status !== undefined) {
      queryParams.append('status', filter.status.toString());
    }
    const queryString = queryParams.toString();
    const endpoint = queryString ? `${API_ENDPOINTS.SUBSCRIPTION.PLANS}?${queryString}` : API_ENDPOINTS.SUBSCRIPTION.PLANS;
    
    console.log('🔍 [ApiService] Endpoint:', endpoint);
    console.log('🔍 [ApiService] Auth token:', this.authToken ? `${this.authToken.substring(0, 20)}...` : 'NO TOKEN');
    
    try {
      const response = await this.request<any[]>(endpoint, {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
      });
      console.log('✅ [ApiService] Subscription plans response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error getting subscription plans:', error);
      throw error;
    }
  }

  async getSubscriptionServices(): Promise<ApiResponse<any[]>> {
    console.log('🔍 [ApiService] Getting subscription services...');
    console.log('🔍 [ApiService] Endpoint:', API_ENDPOINTS.SUBSCRIPTION.SERVICES);
    
    try {
      const response = await this.request<any[]>(API_ENDPOINTS.SUBSCRIPTION.SERVICES, {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
      });
      console.log('✅ [ApiService] Subscription services response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error getting subscription services:', error);
      throw error;
    }
  }

  async getSubscriptionById(subscriptionId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting subscription by ID:', subscriptionId);
    console.log('🔍 [ApiService] Endpoint:', `/api/subscription/${subscriptionId}`);
    
    try {
      const response = await this.request<any>(`/api/subscription/${subscriptionId}`, {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
      });
      console.log('✅ [ApiService] Subscription by ID response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error getting subscription by ID:', error);
      throw error;
    }
  }

  async getBusinessSubscriptions(businessId: string): Promise<ApiResponse<any[]>> {
    console.log('🔍 [ApiService] Getting business subscriptions for business ID:', businessId);
    console.log('🔍 [ApiService] Endpoint:', `/api/subscription/business/${businessId}`);
    
    try {
      const response = await this.request<any[]>(`/api/subscription/business/${businessId}`, {
        method: 'GET',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
      });
      
      console.log('✅ [ApiService] Business subscriptions response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error getting business subscriptions:', error);
      throw error;
    }
  }

  // WhatsApp Phone Number Registration Methods
  async registerWhatsAppPhoneNumber(accountName: string, phoneNumber: string, phoneData?: any): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Registering WhatsApp phone number:', { accountName, phoneNumber });
    
    try {
      const response = await this.authenticatedRequest<any>('/api/whatsapp/phone-numbers/register', {
        method: 'POST',
        body: JSON.stringify({
          accountName,
          phoneNumber,
          phoneData
        }),
      });
      console.log('✅ [ApiService] WhatsApp phone number registration response:', {
        statusCode: response.statusCode,
        hasData: !!response.data,
        dataStatus: response.data?.status,
        hasErrors: !!response.errors
      });
      
      // Return response even if statusCode is not 1000 (e.g., 1001 for "already in use")
      // The frontend will handle the status appropriately
      return response;
    } catch (error: any) {
      console.error('❌ [ApiService] Error registering WhatsApp phone number:', error);
      console.error('❌ [ApiService] Error details:', {
        message: error.message,
        statusCode: error.statusCode,
        hasData: !!error.data,
        dataStatus: error.data?.status,
        hasErrors: !!error.errors
      });
      
      // Check if error has response data with "already_in_use" status.
      // Backend returns HTTP 409 for statusCode 1001; authenticatedRequest rethrows
      // structured error with error.data = full API body { data: { status, ... }, statusCode, errors }.
      const body = error.data;
      const innerStatus = body?.data?.status ?? body?.status;
      if (body && innerStatus === 'already_in_use') {
        console.log('✅ [ApiService] Detected "already_in_use" (statusCode 1001), returning as response');
        return {
          data: body.data ?? body,
          statusCode: body.statusCode ?? 1001,
          errors: body.errors ?? error.errors ?? [],
          requestIdentifier: body.requestIdentifier ?? null,
          messages: body.messages ?? null,
          additionalInfo: body.additionalInfo ?? null,
          user: body.user ?? null
        };
      }
      
      // Check if error message contains "already_in_use" indicators
      // This handles cases where the error message itself indicates the phone is in use
      if (error.message?.includes('already registered') || 
          error.message?.includes('already in use') ||
          error.message?.includes('disconnect it from the existing account')) {
        // Don't throw network error for "already in use" cases
        // Let the frontend handle it based on the error message
        throw error;
      }
      
      // Provide more specific error messages
      if (error.message?.includes('Network error occurred') || 
          error.message?.includes('fetch') ||
          error.message?.includes('Unable to connect')) {
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.message?.includes('Authentication failed')) {
        throw new Error('Session expired. Please login again.');
      } else if (error.message?.includes('Failed to request SMS verification code')) {
        throw new Error('Failed to send verification code. Please try again.');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    }
  }

  async verifyWhatsAppPhoneNumber(registrationId: string, verificationCode: string, phoneNumber?: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Verifying WhatsApp phone number:', { registrationId, verificationCode, phoneNumber });
    
    try {
      const response = await this.request<any>('/api/whatsapp/phone-numbers/verify', {
        method: 'POST',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({
          registrationId,
          verificationCode,
          phoneNumber
        }),
      });
      console.log('✅ [ApiService] WhatsApp phone number verification response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error verifying WhatsApp phone number:', error);
      throw error;
    }
  }

  async requestWhatsAppVoiceCall(phoneNumberId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Requesting WhatsApp voice call for phone number ID:', phoneNumberId);
    
    try {
      const response = await this.request<any>('/api/whatsapp/phone-numbers/request-voice', {
        method: 'POST',
        headers: {
          ...this.defaultHeaders,
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: JSON.stringify({
          phoneNumberId
        }),
      });
      console.log('✅ [ApiService] WhatsApp voice call request response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error requesting WhatsApp voice call:', error);
      throw error;
    }
  }

  async resendWhatsAppSMS(phoneNumberId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/whatsapp/phone-numbers/resend-sms', {
      method: 'POST',
      body: JSON.stringify({
        phoneNumberId
      }),
    });
  }

  async getWhatsAppPhoneNumbers(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching WhatsApp phone numbers');

    try {
      const response = await this.authenticatedRequest<any>('/api/whatsapp/phone-numbers/registrations', {
        method: 'GET',
      });
      console.log('✅ [ApiService] WhatsApp phone numbers response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching WhatsApp phone numbers:', error);
      throw error;
    }
  }

  /** Registers an already-connected number that's stuck "Pending" on the Cloud API
   * (e.g. an embedded-signup connection that didn't complete registration) without
   * requiring the business to disconnect and reconnect it. */
  async registerPendingWhatsAppPhoneNumber(phoneNumberId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Registering pending WhatsApp phone number:', phoneNumberId);

    try {
      const response = await this.authenticatedRequest<any>(
        `/api/whatsapp/embedded-signup/phone-numbers/${encodeURIComponent(phoneNumberId)}/register`,
        { method: 'POST' }
      );
      console.log('✅ [ApiService] WhatsApp phone number registered:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error registering WhatsApp phone number:', error);
      throw error;
    }
  }

  /** Deregisters a phone number from Meta and removes it from the business's subscription,
   * so it can be reconnected (or a different number connected) via embedded signup again. */
  async deleteWhatsAppPhoneNumber(phoneNumberId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Deleting WhatsApp phone number:', phoneNumberId);

    try {
      const response = await this.authenticatedRequest<any>(
        `/api/whatsapp/embedded-signup/phone-numbers/${encodeURIComponent(phoneNumberId)}`,
        { method: 'DELETE' }
      );
      console.log('✅ [ApiService] WhatsApp phone number deleted:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error deleting WhatsApp phone number:', error);
      throw error;
    }
  }

  // Business-scoped Permissions (leveled: none/view/edit/admin per catalog item)

  /** Reads the `permissions` claim embedded in the current phase-2 token by
   * generateBusinessScopedTokens — no round trip, always reflects what was resolved at the last
   * select-business/refresh call. */
  getPermissionsFromToken(): Record<string, PermissionLevel> | null {
    const token = this.getAuthToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
      return payload.permissions || null;
    } catch (error) {
      console.error('❌ [ApiService] Error decoding permissions from token:', error);
      return null;
    }
  }

  hasPermissionAtLeast(key: string, minLevel: PermissionLevel): boolean {
    const permissions = this.getPermissionsFromToken();
    const level = permissions?.[key] || 'none';
    return levelRank(level) >= levelRank(minLevel);
  }

  async getPermissionCatalog(businessId: string): Promise<ApiResponse<{ general: PermissionCatalogItem[]; services: Record<string, PermissionCatalogItem[]> }>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/catalog`, { method: 'GET' });
  }

  async getUserPermissionProfile(businessId: string, userId: string): Promise<ApiResponse<BusinessPermissionProfile | null>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/users/${userId}`, { method: 'GET' });
  }

  async setUserPermissionProfile(businessId: string, userId: string, grants: Record<string, PermissionLevel>): Promise<ApiResponse<BusinessPermissionProfile>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ grants }),
    });
  }

  async getDepartmentPermissionProfile(businessId: string, departmentId: string): Promise<ApiResponse<BusinessPermissionProfile | null>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/departments/${departmentId}`, { method: 'GET' });
  }

  async setDepartmentPermissionProfile(businessId: string, departmentId: string, grants: Record<string, PermissionLevel>): Promise<ApiResponse<BusinessPermissionProfile>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/departments/${departmentId}`, {
      method: 'PUT',
      body: JSON.stringify({ grants }),
    });
  }

  async bulkSetPermissionProfiles(
    businessId: string,
    targets: Array<{ targetType: 'user' | 'department'; targetId: string }>,
    grants: Record<string, PermissionLevel>
  ): Promise<ApiResponse<{ updated: number }>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/bulk`, {
      method: 'POST',
      body: JSON.stringify({ targets, grants }),
    });
  }

  async getUserPermissionSummary(businessId: string, userId: string): Promise<ApiResponse<PermissionSummary>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/users/${userId}/summary`, { method: 'GET' });
  }

  async getDepartmentPermissionSummary(businessId: string, departmentId: string): Promise<ApiResponse<PermissionSummary>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/departments/${departmentId}/summary`, { method: 'GET' });
  }

  async getBulkUserSummaries(businessId: string, userIds: string[]): Promise<ApiResponse<Record<string, PermissionSummary>>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/users/summaries`, {
      method: 'POST',
      body: JSON.stringify({ ids: userIds }),
    });
  }

  async getBulkDepartmentSummaries(businessId: string, departmentIds: string[]): Promise<ApiResponse<Record<string, PermissionSummary>>> {
    return this.authenticatedRequest(`/api/business-permissions/${businessId}/departments/summaries`, {
      method: 'POST',
      body: JSON.stringify({ ids: departmentIds }),
    });
  }

  // Facebook Messenger Page Registration Methods
  async registerFacebookPage(accountName: string, pageName: string, pageId: string, pageAccessToken: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Registering Facebook page:', { accountName, pageName, pageId });
    
    try {
      const response = await this.authenticatedRequest<any>('/api/facebook-messenger/pages', {
        method: 'POST',
        body: JSON.stringify({
          page_id: pageId,
          page_name: pageName,
          page_access_token: pageAccessToken
        }),
      });
      console.log('✅ [ApiService] Facebook page registration response:', response);
      return response;
    } catch (error: any) {
      console.error('❌ [ApiService] Error registering Facebook page:', error);
      
      // Provide more specific error messages
      if (error.message?.includes('Network error occurred')) {
        throw new Error('Unable to connect to server. Please check your internet connection and try again.');
      } else if (error.message?.includes('Authentication failed')) {
        throw new Error('Session expired. Please login again.');
      } else if (error.message?.includes('Invalid page access token')) {
        throw new Error('Invalid page access token. Please get a new token from Facebook Developer Console.');
      } else if (error.message?.includes('Page not found')) {
        throw new Error('Facebook page not found. Please check the page ID.');
      } else if (error.message?.includes('already registered')) {
        throw new Error('This Facebook page is already registered with another business.');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    }
  }

  async getFacebookChannelLimits(): Promise<ApiResponse<{ maxPages: number; currentPages: number }>> {
    console.log('🔍 [ApiService] Fetching Facebook channel limits');

    try {
      const response = await this.authenticatedRequest<any>('/api/facebook-messenger/limits', {
        method: 'GET',
      });
      console.log('✅ [ApiService] Facebook channel limits response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook channel limits:', error);
      throw error;
    }
  }

  async getFacebookPages(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching Facebook pages');
    
    try {
      const response = await this.authenticatedRequest<any>('/api/facebook-messenger/pages', {
        method: 'GET',
      });
      console.log('✅ [ApiService] Facebook pages response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook pages:', error);
      throw error;
    }
  }

  /** Registers an already-connected page that's stuck "Pending" (webhook subscribe failed at
   * connect time) without requiring the business to disconnect and reconnect it. */
  async registerPendingFacebookPage(pageId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Registering pending Facebook page:', pageId);

    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/embedded-signup/pages/${encodeURIComponent(pageId)}/register`,
        { method: 'POST' }
      );
      console.log('✅ [ApiService] Facebook page registered:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error registering Facebook page:', error);
      throw error;
    }
  }

  /** Confirms which webhook fields Meta actually has registered for a page —
   * distinct from trusting that the subscribe call succeeded. A page
   * connected before a new field (e.g. 'feed') existed never gets
   * auto-resubscribed, so this is how the UI detects that gap and shows a
   * "re-register" prompt instead of webhooks silently never arriving. */
  async getFacebookWebhookSubscriptionStatus(pageId: string): Promise<ApiResponse<{ subscribed: boolean; fields: string[] }>> {
    console.log('🔍 [ApiService] Checking Facebook webhook subscription status:', pageId);
    try {
      const response = await this.authenticatedRequest<{ subscribed: boolean; fields: string[] }>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/webhook-subscription-status`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook webhook subscription status:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error checking Facebook webhook subscription status:', error);
      throw error;
    }
  }

  /** Unsubscribes webhooks and removes a connected page from the business's subscription,
   * so it can be reconnected (or a different page connected) via embedded signup again. */
  async deleteFacebookPage(pageId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Deleting Facebook page:', pageId);

    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/embedded-signup/pages/${encodeURIComponent(pageId)}`,
        { method: 'DELETE' }
      );
      console.log('✅ [ApiService] Facebook page deleted:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error deleting Facebook page:', error);
      throw error;
    }
  }

  async importFacebookPageConversations(pageId: string): Promise<ApiResponse<{ imported: number; totalUniqueContacts: number; pageId: string; pageName: string }>> {
    console.log('🔍 [ApiService] Importing Facebook page conversations:', { pageId });
    
    try {
      const response = await this.authenticatedRequest<any>(`/api/facebook-messenger/pages/${pageId}/import-conversations`, {
        method: 'POST',
      });
      console.log('✅ [ApiService] Import conversations response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error importing Facebook page conversations:', error);
      throw error;
    }
  }

  // ─── Facebook Page Posts, Comments & Insights ─────────────────────────────

  async getFacebookPosts(pageId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching Facebook posts:', { pageId, params });
    try {
      const query = new URLSearchParams();
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));
      const qs = query.toString();
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook posts response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook posts:', error);
      throw error;
    }
  }

  async createFacebookPost(pageId: string, data: { message?: string; mediaUrls?: string[]; mediaType?: 'photo' | 'video'; scheduledFor?: string }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Creating Facebook post:', { pageId, data });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts`,
        { method: 'POST', body: JSON.stringify(data) }
      );
      console.log('✅ [ApiService] Facebook post created:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error creating Facebook post:', error);
      throw error;
    }
  }

  /** Posts across connected pages (pageId omitted = all), by status / text. */
  async listFacebookPosts(params: { pageId?: string; status?: 'draft' | 'scheduled' | 'published' | 'failed'; search?: string; limit?: number; offset?: number } = {}): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    if (params.pageId) query.set('pageId', params.pageId);
    if (params.status) query.set('status', params.status);
    if (params.search?.trim()) query.set('search', params.search.trim());
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.authenticatedRequest<any>(`/api/facebook-messenger/posts${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** Create, schedule or save a draft on one or several pages. */
  async createFacebookPosts(data: {
    pageIds: string[];
    message?: string;
    mediaUrls?: string[];
    mediaType?: 'photo' | 'video';
    linkUrl?: string;
    scheduledFor?: string;
    asDraft?: boolean;
  }): Promise<ApiResponse<{ posts: any[]; failed: number }>> {
    return this.authenticatedRequest<any>('/api/facebook-messenger/posts', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateFacebookPost(postId: string, patch: { message?: string; mediaUrls?: string[]; mediaType?: 'photo' | 'video'; linkUrl?: string | null; scheduledFor?: string | null }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/facebook-messenger/posts/${encodeURIComponent(postId)}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  async deleteFacebookPost(postId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.authenticatedRequest<any>(`/api/facebook-messenger/posts/${encodeURIComponent(postId)}`, { method: 'DELETE' });
  }

  /** Publish a draft, or retry a failed post. */
  async publishFacebookPost(postId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/facebook-messenger/posts/${encodeURIComponent(postId)}/publish`, { method: 'POST' });
  }

  /** Take a scheduled post off Facebook; it becomes a draft. */
  async cancelFacebookPostSchedule(postId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/facebook-messenger/posts/${encodeURIComponent(postId)}/cancel-schedule`, { method: 'POST' });
  }

  async importFacebookPosts(pageId: string): Promise<ApiResponse<{ imported: number; total: number }>> {
    console.log('🔍 [ApiService] Importing Facebook posts:', { pageId });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts/import`,
        { method: 'POST' }
      );
      console.log('✅ [ApiService] Import posts response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error importing Facebook posts:', error);
      throw error;
    }
  }

  async getFacebookPostComments(pageId: string, postId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching Facebook post comments:', { pageId, postId, params });
    try {
      const query = new URLSearchParams();
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.offset) query.set('offset', String(params.offset));
      const qs = query.toString();
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts/${encodeURIComponent(postId)}/comments${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook post comments response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook post comments:', error);
      throw error;
    }
  }

  async replyToFacebookComment(commentId: string, pageId: string, message: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Replying to Facebook comment:', { commentId, pageId });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/comments/${encodeURIComponent(commentId)}/reply`,
        { method: 'POST', body: JSON.stringify({ pageId, message }) }
      );
      console.log('✅ [ApiService] Facebook comment reply response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error replying to Facebook comment:', error);
      throw error;
    }
  }

  async getFacebookPostReactions(pageId: string, postId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching Facebook post reactions:', { pageId, postId });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts/${encodeURIComponent(postId)}/reactions`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook post reactions response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook post reactions:', error);
      throw error;
    }
  }

  async likeFacebookComment(commentId: string, pageId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Liking Facebook comment:', { commentId, pageId });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/comments/${encodeURIComponent(commentId)}/like`,
        { method: 'POST', body: JSON.stringify({ pageId }) }
      );
      console.log('✅ [ApiService] Facebook comment like response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error liking Facebook comment:', error);
      throw error;
    }
  }

  async unlikeFacebookComment(commentId: string, pageId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Unliking Facebook comment:', { commentId, pageId });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/comments/${encodeURIComponent(commentId)}/like?pageId=${encodeURIComponent(pageId)}`,
        { method: 'DELETE' }
      );
      console.log('✅ [ApiService] Facebook comment unlike response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error unliking Facebook comment:', error);
      throw error;
    }
  }

  async getFacebookPageInsights(pageId: string, params?: { metrics?: string[]; period?: string; since?: string; until?: string }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Fetching Facebook page insights:', { pageId, params });
    try {
      const query = new URLSearchParams();
      if (params?.metrics?.length) query.set('metrics', params.metrics.join(','));
      if (params?.period) query.set('period', params.period);
      if (params?.since) query.set('since', params.since);
      if (params?.until) query.set('until', params.until);
      const qs = query.toString();
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/insights${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook page insights response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook page insights:', error);
      throw error;
    }
  }

  async getFacebookPageFollowers(pageId: string): Promise<ApiResponse<{ followers: number }>> {
    console.log('🔍 [ApiService] Fetching Facebook page follower count:', { pageId });
    try {
      const response = await this.authenticatedRequest<{ followers: number }>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/followers`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook page follower count response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook page follower count:', error);
      throw error;
    }
  }

  async getFacebookPageViewsSummary(pageId: string, params: { since: string; until: string }): Promise<ApiResponse<{ current: number; previous: number; changePct: number | null }>> {
    console.log('🔍 [ApiService] Fetching Facebook page views summary:', { pageId, params });
    try {
      const query = new URLSearchParams({ since: params.since, until: params.until });
      const response = await this.authenticatedRequest<{ current: number; previous: number; changePct: number | null }>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/views-summary?${query.toString()}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook page views summary response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook page views summary:', error);
      throw error;
    }
  }

  async getFacebookViewsChartSeries(pageId: string, params: { since: string; until: string }): Promise<ApiResponse<Array<{ date: string; views: number }>>> {
    console.log('🔍 [ApiService] Fetching Facebook views chart series:', { pageId, params });
    try {
      const query = new URLSearchParams({ since: params.since, until: params.until });
      const response = await this.authenticatedRequest<Array<{ date: string; views: number }>>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/views-chart-series?${query.toString()}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook views chart series response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook views chart series:', error);
      throw error;
    }
  }

  async getFacebookViewsByContentType(pageId: string, params?: { since?: string; until?: string }): Promise<ApiResponse<Array<{ mediaType: string; views: number }>>> {
    console.log('🔍 [ApiService] Fetching Facebook views by content type:', { pageId, params });
    try {
      const query = new URLSearchParams();
      if (params?.since) query.set('since', params.since);
      if (params?.until) query.set('until', params.until);
      const qs = query.toString();
      const response = await this.authenticatedRequest<Array<{ mediaType: string; views: number }>>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/views-by-content-type${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook views by content type response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook views by content type:', error);
      throw error;
    }
  }

  async getFacebookViewsByFollowerStatus(pageId: string, params?: { since?: string; until?: string }): Promise<ApiResponse<{ followers: number; nonFollowers: number }>> {
    console.log('🔍 [ApiService] Fetching Facebook views by follower status:', { pageId, params });
    try {
      const query = new URLSearchParams();
      if (params?.since) query.set('since', params.since);
      if (params?.until) query.set('until', params.until);
      const qs = query.toString();
      const response = await this.authenticatedRequest<{ followers: number; nonFollowers: number }>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/views-by-follower-status${qs ? `?${qs}` : ''}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook views by follower status response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook views by follower status:', error);
      throw error;
    }
  }

  async getFacebookPageVideoStats(pageId: string, params: { since: string; until: string }): Promise<ApiResponse<{ threeSecondViews: number; watchTimeMinutes: number }>> {
    console.log('🔍 [ApiService] Fetching Facebook page video stats:', { pageId, params });
    try {
      const query = new URLSearchParams({ since: params.since, until: params.until });
      const response = await this.authenticatedRequest<{ threeSecondViews: number; watchTimeMinutes: number }>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/video-stats?${query.toString()}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook page video stats response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook page video stats:', error);
      throw error;
    }
  }

  async getFacebookRecentPostsWithViews(pageId: string, limit?: number): Promise<ApiResponse<Array<{ postId: string; message?: string; mediaUrl?: string; mediaType: string; publishedAt?: string; views: number }>>> {
    console.log('🔍 [ApiService] Fetching Facebook recent posts with views:', { pageId, limit });
    try {
      const qs = limit ? `?limit=${limit}` : '';
      const response = await this.authenticatedRequest<Array<{ postId: string; message?: string; mediaUrl?: string; mediaType: string; publishedAt?: string; views: number }>>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/recent-posts-views${qs}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook recent posts with views response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook recent posts with views:', error);
      throw error;
    }
  }

  async getFacebookPostInsights(pageId: string, postId: string, mediaType: string): Promise<ApiResponse<{
    views: number;
    reach: number;
    reactionsTotal: number;
    reactionsByType: Record<string, number>;
    comments: number;
    shares: number;
    linkClicks: number;
    video?: { threeSecondViews: number; views60s: number; watchTimeMinutes: number };
  }>> {
    console.log('🔍 [ApiService] Fetching Facebook post insights:', { pageId, postId, mediaType });
    try {
      const response = await this.authenticatedRequest<any>(
        `/api/facebook-messenger/pages/${encodeURIComponent(pageId)}/posts/${encodeURIComponent(postId)}/insights?mediaType=${encodeURIComponent(mediaType)}`,
        { method: 'GET' }
      );
      console.log('✅ [ApiService] Facebook post insights response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error fetching Facebook post insights:', error);
      throw error;
    }
  }

  // ─── X (Twitter) Channel Methods ─────────────────────────────────────────

  /**
   * Exchange OAuth 2.0 PKCE authorization code for tokens.
   * No auth header required — called before the account is linked.
   */
  async exchangeXTwitterCode(code: string, codeVerifier: string): Promise<ApiResponse<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
    expires_at: string;
    user: { id: string; username: string; name: string; profile_image_url?: string };
  }>> {
    return this.request<any>('/api/x-twitter/auth/exchange', {
      method: 'POST',
      headers: { ...this.defaultHeaders },
      body: JSON.stringify({ code, codeVerifier })
    });
  }

  /**
   * Finalize connection: store tokens + subscribe webhook on the backend.
   */
  async connectXTwitterAccount(payload: {
    xUserId: string;
    xUsername: string;
    displayName: string;
    profileImageUrl?: string;
    oauth2AccessToken: string;
    oauth2RefreshToken: string;
    oauth2ExpiresAt: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/x-twitter/accounts', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getXTwitterAccounts(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/x-twitter/accounts', { method: 'GET' });
  }

  async disconnectXTwitterAccount(xUserId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/x-twitter/accounts/${xUserId}`, { method: 'DELETE' });
  }

  async refreshXTwitterToken(xUserId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/x-twitter/accounts/${xUserId}/refresh-token`, { method: 'POST' });
  }

  async importXTwitterConversations(xUserId: string): Promise<ApiResponse<{ imported: number; totalUniqueContacts: number; xUserId: string }>> {
    return this.authenticatedRequest<any>(`/api/x-twitter/accounts/${xUserId}/import-conversations`, { method: 'POST' });
  }

  async getXTwitterStatus(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/x-twitter/status', { method: 'GET' });
  }

  async getXTwitterLimits(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/x-twitter/limits', { method: 'GET' });
  }

  // ─── Phone channel (multi-provider, e.g. InnoCalls) ───────────────────────

  // Confirmed real bug: on every app load, usePhoneDialer's global init
  // (mounted once in AppNavigator, survives navigation) and Home's
  // useProfileCompletion (mounted per Home visit) each independently call
  // this with identical (no) params, within milliseconds of each other —
  // two network round-trips for the exact same read-only data. A short-TTL
  // in-memory promise cache dedupes this specific pattern (near-simultaneous
  // callers on the same page load share one request) without needing to
  // thread a shared store between two otherwise-unrelated hooks, and
  // without risking stale data beyond a few seconds — this is read-only
  // account/connection info, not anything permission- or balance-critical.
  private phoneAccountCache: { promise: Promise<ApiResponse<any>>; expiresAt: number } | null = null;
  private static readonly PHONE_ACCOUNT_CACHE_TTL_MS = 5000;

  async getPhoneAccount(): Promise<ApiResponse<any>> {
    const now = Date.now();
    if (this.phoneAccountCache && this.phoneAccountCache.expiresAt > now) {
      return this.phoneAccountCache.promise;
    }
    const promise = this.authenticatedRequest<any>('/api/phone/account', { method: 'GET' });
    this.phoneAccountCache = { promise, expiresAt: now + ApiService.PHONE_ACCOUNT_CACHE_TTL_MS };
    // A failed request must not poison the cache for the TTL window — clear
    // it immediately so the next caller (or a retry) gets a fresh attempt
    // instead of an error result cached memory expires.
    promise.catch(() => { this.phoneAccountCache = null; });
    return promise;
  }

  async getAiStatus(): Promise<ApiResponse<AiStatus>> {
    return this.authenticatedRequest<AiStatus>('/api/ai/status', { method: 'GET' });
  }
  async getWorkspaceAssistantChat(): Promise<ApiResponse<WorkspaceChat>> {
    return this.authenticatedRequest('/api/ai/workspace', { method: 'GET' });
  }
  /** One file for the next assistant message (web: a File; native: {uri,type,name}). */
  async uploadWorkspaceAttachment(file: File | { uri: string; type: string; name: string }): Promise<ApiResponse<import('./workspace-assistant.types').WorkspaceAttachment>> {
    const form = new FormData();
    form.append('file', file as any);
    const token = this.getAuthToken();
    if (!token) return { data: null, statusCode: 401, errors: [{ message: 'Sign in again to attach files.' }] } as any;
    try {
      const response = await fetch(`${getCurrentApiConfig()?.baseURL?.trim() || ''}/api/ai/workspace/attachments`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) return { data: null, statusCode: response.status, errors: json.errors || [{ message: response.status === 413 ? 'Files up to 10 MB can be attached.' : 'The file could not be attached.' }] } as any;
      return json;
    } catch {
      return { data: null, statusCode: 0, errors: [{ message: 'The file could not be attached. Check your connection.' }] } as any;
    }
  }
  async askWorkspaceAssistant(body: { message: string; requestId: string; currentScreen: string; locale: string; timezone?: string; attachments?: string[] }, options: { signal?: AbortSignal } = {}): Promise<ApiResponse<WorkspaceChat>> {
    // Longer than the server's own turn limit (DISLIX_AI_WORKSPACE_REPLY_TIMEOUT_MS, up to 240 s): closing the
    // request earlier cancels the turn on the server.
    return this.authenticatedRequest('/api/ai/workspace', { method: 'POST', body: JSON.stringify(body), signal: options.signal }, 250000);
  }
  async moreWorkspaceTableRows(messageId: string, tableId: string): Promise<ApiResponse<WorkspaceChat>> {
    return this.authenticatedRequest(`/api/ai/workspace/messages/${encodeURIComponent(messageId)}/tables/${encodeURIComponent(tableId)}/more`, { method: 'POST' }, 30000);
  }
  async clearWorkspaceAssistantChat(): Promise<ApiResponse<WorkspaceChat>> {
    return this.authenticatedRequest('/api/ai/workspace', { method: 'DELETE' });
  }
  async cancelWorkspaceAssistant(requestId: string): Promise<ApiResponse<WorkspaceChat>> {
    return this.authenticatedRequest('/api/ai/workspace/cancel', { method: 'POST', body: JSON.stringify({ requestId }) });
  }
  async executeWorkspaceProposal(proposalId: string, body: { headerMediaUrl?: string } = {}): Promise<ApiResponse<{ proposal: import('./workspace-assistant.types').WorkspaceProposal }>> {
    return this.authenticatedRequest(`/api/ai/workspace/proposals/${encodeURIComponent(proposalId)}/execute`, { method: 'POST', body: JSON.stringify(body) }, 120000);
  }
  async getAiQuickSuggestions(conversationId: string, locale: string, refresh = false): Promise<ApiResponse<AiQuickSuggestions>> {
    return this.authenticatedRequest('/api/ai/suggestions', { method: 'POST', body: JSON.stringify({ conversationId, locale, ...(refresh ? { refresh: true } : {}) }) }, 250000);
  }
  /** Sends a quotation or order into a conversation: its details, its PDF, or both (optionally with WhatsApp buttons). */
  async shareConversationDocument(conversationId: string, body: { type: 'quotation' | 'order'; id: string; mode: 'details' | 'pdf' | 'both'; buttons?: boolean; language?: 'ar' | 'en' }): Promise<ApiResponse<{ sent: string[]; number: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/conversations/${encodeURIComponent(conversationId)}/share-document`, { method: 'POST', body: JSON.stringify(body) }, 60000);
  }
  /** Clears the assistant's memory for one conversation; rebuild starts a fresh run. */
  /** Sends a product card, catalogue or product photos (as suggested by the assistant). */
  async sendAiMedia(conversationId: string, media: { kind: string; id: string }, language?: string): Promise<ApiResponse<{ sent: number }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/media`, { method: 'POST', body: JSON.stringify({ ...media, language }) }, 30000);
  }

  /** Team → AI agents: the subscription's AI agents and their settings. */
  async getAiAgents(): Promise<ApiResponse<AiAgentsResponse>> {
    return this.authenticatedRequest('/api/ai/agents');
  }

  /** Home → AI activity: what each AI agent did since `from`. */
  async getAiAgentActivity(from: string): Promise<ApiResponse<{ agents: AiAgentActivity[] }>> {
    return this.authenticatedRequest(`/api/ai/agents/activity?from=${encodeURIComponent(from)}`);
  }

  /** Home → Workflow runs grouped by contact since `from`. */
  async getFlowRunsByContact(from: string, limit = 20): Promise<ApiResponse<FlowRunsByContact>> {
    return this.authenticatedRequest(`/api/omni-channel/flow-runs/by-contact?from=${encodeURIComponent(from)}&limit=${limit}`);
  }

  async addAiAgent(): Promise<ApiResponse<AiAgent>> {
    return this.authenticatedRequest('/api/ai/agents', { method: 'POST', body: '{}' });
  }

  async updateAiAgent(agentId: string, changes: Partial<AiAgent>): Promise<ApiResponse<AiAgent>> {
    return this.authenticatedRequest(`/api/ai/agents/${agentId}`, { method: 'PATCH', body: JSON.stringify(changes) });
  }

  async keepAiAgentActive(agentId: string, keep: boolean): Promise<ApiResponse<AiAgentsResponse>> {
    return this.authenticatedRequest(`/api/ai/agents/${agentId}/keep-active`, { method: 'POST', body: JSON.stringify({ keep }) });
  }

  /** The draft carts the AI keeps for this conversation (assistant + workflow AI agents), priced now. */
  /** The conversation's shared cart (AI, browser and team), priced with taxes and charges, and
   * — while it is empty — the customer's open cart on another number. */
  async getConversationCarts(conversationId: string): Promise<ApiResponse<{ cart: ConversationCart; otherCart?: OtherNumberCart }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/carts`);
  }

  async setConversationCart(conversationId: string, lines: Array<{ productId: string; variantId?: string; quantity: number }>): Promise<ApiResponse<{ cart: ConversationCart }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/cart`, { method: 'PUT', body: JSON.stringify({ lines }) });
  }

  async decideConversationCart(conversationId: string, from: string, decision: 'continue' | 'new'): Promise<ApiResponse<{ cart: ConversationCart }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/cart/decide`, { method: 'POST', body: JSON.stringify({ from, decision }) });
  }

  async clearConversationCart(conversationId: string): Promise<ApiResponse<{ cleared: boolean }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/carts/clear`, { method: 'POST', body: '{}' });
  }

  async resetAiMemory(conversationId: string, rebuild: boolean): Promise<ApiResponse<{ cleared: boolean; rebuilding: boolean }>> {
    return this.authenticatedRequest(`/api/ai/conversations/${encodeURIComponent(conversationId)}/memory/reset`, { method: 'POST', body: JSON.stringify({ rebuild }) }, 30000);
  }
  async generateAiAssistance(input: { conversationId: string; mode: 'reply' | 'quotation'; productSearch?: string; instructions?: string }): Promise<ApiResponse<AiAssistantResult>> {
    return this.authenticatedRequest('/api/ai/assistant', { method: 'POST', body: JSON.stringify(input) }, 250000);
  }
  async approveAiQuotation(proposalId: string, customer: { name: string; phone: string }): Promise<ApiResponse<{ quotationId: string; quotationNumber: string }>> {
    return this.authenticatedRequest(`/api/ai/actions/${encodeURIComponent(proposalId)}/approve`, { method: 'POST', body: JSON.stringify({ confirmed: true, customer }) }, 30000);
  }
  async getAiModels(): Promise<ApiResponse<{ accountId: string; models: AiModelChoice[] }>> {
    return this.authenticatedRequest('/api/ai/models', { method: 'GET' });
  }
  async connectAiModel(modelKey: string): Promise<ApiResponse<{ config: AiConfig; test: AiTestResult }>> {
    return this.authenticatedRequest('/api/ai/config', { method: 'PUT', body: JSON.stringify({ modelKey }) }, 250000);
  }
  async saveAiKnowledge(knowledge: string): Promise<ApiResponse<{ knowledge: string }>> {
    return this.authenticatedRequest('/api/ai/config/knowledge', { method: 'PUT', body: JSON.stringify({ knowledge }) });
  }
  async disconnectAiModel(): Promise<ApiResponse<AiStatus>> {
    return this.authenticatedRequest('/api/ai/config', { method: 'DELETE' });
  }
  async testAiModel(modelKey: string): Promise<ApiResponse<AiTestResult>> {
    return this.authenticatedRequest('/api/ai/test', { method: 'POST', body: JSON.stringify({ modelKey }) }, 250000);
  }
  async getAiUsage(query: { from?: string; to?: string; page?: number; status?: string } = {}): Promise<ApiResponse<AiUsageHistory>> {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => { if (value !== undefined && value !== '') params.set(key, String(value)); });
    return this.authenticatedRequest(`/api/ai/usage?${params}`, { method: 'GET' });
  }

  /** Configurable per-call disposition list (mirrors Visits' outcomes editor). */
  async getPhoneOutcomesConfig(): Promise<ApiResponse<{ outcomes: any[] }>> {
    return this.authenticatedRequest<any>('/api/phone/outcomes-config', { method: 'GET' });
  }

  async updatePhoneOutcomesConfig(outcomes: any[]): Promise<ApiResponse<{ outcomes: any[] }>> {
    return this.authenticatedRequest<any>('/api/phone/outcomes-config', {
      method: 'PUT',
      body: JSON.stringify({ outcomes }),
    });
  }

  /** Records how a call went and executes that outcome's configured action
   * (reminder/assign/note) — same relationship ending a Visit has to its
   * outcome. */
  async recordCallOutcome(callId: string, outcomeId: string, note?: string, actionPayload?: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/phone/calls/${callId}/outcome`, {
      method: 'POST',
      body: JSON.stringify({ outcomeId, note, actionPayload }),
    });
  }

  /** Rule-based (not ML) "should we call this contact again" signal — see
   * PhoneCallsService.getRecontactSuggestion's doc comment for how it's derived. */
  async getCallRecontactSuggestion(contactId: string): Promise<ApiResponse<{
    attemptsSinceLastSuccess: number;
    lastOutcome?: { id: string; recordedAt: string };
    recommendation: 'call_again' | 'escalate' | 'no_action_needed';
  }>> {
    return this.authenticatedRequest<any>(`/api/phone/calls/contact/${contactId}/recontact-suggestion`, { method: 'GET' });
  }

  // ─── Visits (field visits configuration) ───────────────────────────────────

  async getVisitsConfig(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/config', { method: 'GET' });
  }

  async updateVisitsConfig(patch: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/config', {
      method: 'PUT',
      body: JSON.stringify(patch)
    });
  }

  async disableVisitsService(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/config', { method: 'DELETE' });
  }

  async getVisitsEligibleUsers(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/config/eligible-users', { method: 'GET' });
  }

  async listVisits(params: Record<string, any>): Promise<ApiResponse<Visit[]>> {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])).toString();
    return this.authenticatedRequest<Visit[]>(`/api/visits${query ? '?' + query : ''}`, { method: 'GET' });
  }

  async getVisitCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>('/api/visits/custom-fields', { method: 'GET' });
  }

  async getVisitCustomFieldValues(key: string): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>(`/api/visits/custom-fields/${encodeURIComponent(key)}/values`, { method: 'GET' });
  }

  async createVisit(payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits', { method: 'POST', body: JSON.stringify(payload) });
  }

  async getVisit(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}`, { method: 'GET' });
  }

  async updateVisit(id: string, patch: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  async cancelVisit(id: string, reason?: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  async computeVisitRoute(payload: {
    anchor: { latitude: number; longitude: number; label?: string; source: 'live_gps' | 'manual' };
    mode: 'driving' | 'walking' | 'cycling' | 'motorcycle';
    date?: string;
    visitIds?: string[];
    engineOverride?: 'haversine' | 'osrm';
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/route', { method: 'POST', body: JSON.stringify(payload) });
  }

  // Live in-app navigation (Talabat/Uber-style directions view) — polled
  // repeatedly during an in-progress visit as the agent's GPS position
  // updates, distinct from computeVisitRoute (one-time multi-stop plan).
  async getVisitLiveDirections(
    visitId: string,
    latitude: number,
    longitude: number,
    mode: 'driving' | 'walking' | 'cycling' | 'motorcycle' = 'driving'
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ latitude: String(latitude), longitude: String(longitude), mode });
    return this.authenticatedRequest<any>(`/api/visits/${visitId}/live-directions?${params.toString()}`, { method: 'GET' });
  }

  async getPlacesNearby(params: {
    latitude: number;
    longitude: number;
    radiusMeters?: number;
    category?: string;
    countryCode?: 'EG' | 'SA' | 'PS';
    limit?: number;
  }): Promise<ApiResponse<Place[]>> {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])
    ).toString();
    return this.authenticatedRequest<Place[]>(`/api/places/nearby?${query}`, { method: 'GET' });
  }

  async searchPlaces(params: {
    query: string;
    latitude?: number;
    longitude?: number;
    countryCode?: 'EG' | 'SA' | 'PS';
    limit?: number;
  }): Promise<ApiResponse<Place[]>> {
    // Confirmed real bug: the backend's places.controller.ts reads the
    // search text as `q` (`req.query.q`), not `query` — sending this
    // method's own `query` param name straight through as the URL key
    // meant the backend always saw an empty `q`, always returned 400, and
    // MapLocationPicker's suggestion fetch silently swallowed the error
    // and cleared its suggestion list. `query` -> `q` is renamed ONLY at
    // the wire boundary here so every call site keeps its readable name.
    const { query: q, ...rest } = params;
    const queryString = new URLSearchParams(
      Object.entries({ q, ...rest }).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])
    ).toString();
    return this.authenticatedRequest<Place[]>(`/api/places/search?${queryString}`, { method: 'GET' });
  }

  async claimVisit(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/claim`, { method: 'POST' });
  }

  async getVisitEffectiveConfig(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/effective-config`, { method: 'GET' });
  }

  async startVisit(id: string, payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/start`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async endVisit(id: string, payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/end`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async addVisitPhotos(id: string, photos: any[]): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/photos`, { method: 'POST', body: JSON.stringify({ photos }) });
  }

  async addVisitNote(id: string, text: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/notes`, { method: 'POST', body: JSON.stringify({ text }) });
  }

  /** Logs something done for the contact during a visit (reminder, list, assignment…). */
  async addVisitActivity(id: string, activity: { type: VisitActivity['type']; refId?: string; label?: string }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/${id}/activities`, { method: 'POST', body: JSON.stringify(activity) });
  }

  // ─── Visit Campaigns (field visits, Phase 3) ────────────────────────────

  async listVisitCampaigns(params?: Record<string, any>): Promise<ApiResponse<any[]>> {
    const query = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])).toString() : '';
    return this.authenticatedRequest<any[]>(`/api/visits/campaigns${query ? '?' + query : ''}`, { method: 'GET' });
  }

  async createVisitCampaign(payload: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/visits/campaigns', { method: 'POST', body: JSON.stringify(payload) });
  }

  async getVisitCampaign(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}`, { method: 'GET' });
  }

  async updateVisitCampaign(id: string, patch: Record<string, any>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  async cancelVisitCampaign(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}`, { method: 'DELETE' });
  }

  async activateVisitCampaign(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/activate`, { method: 'POST' }, 45000);
  }

  // Any contact lacking stored coordinates gets geocoded server-side, which
  // sleeps ~1.1s per geocode to respect Nominatim's ~1 req/sec usage policy
  // (visit-target-location.util.ts) — real wall-clock time that scales with
  // contactIds.length and can't be shortened without breaking that rate
  // limit. The default request timeout (15s) was getting exceeded by as few
  // as 2-3 contacts needing geocoding; scale generously with the batch size
  // instead of a flat value; but confirmed real production timeout with
  // just 2 contacts.
  async attachVisitCampaignContacts(id: string, contactIds: string[]): Promise<ApiResponse<any>> {
    const timeoutMs = Math.max(20000, contactIds.length * 3000 + 15000);
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/contacts`, { method: 'POST', body: JSON.stringify({ contactIds }) }, timeoutMs);
  }

  async attachVisitCampaignContactsFromJob(id: string, jobId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/contacts/from-job`, { method: 'POST', body: JSON.stringify({ jobId }) });
  }

  // Same geocode-throttle timeout scaling as attachVisitCampaignContacts —
  // this endpoint runs the identical per-contact resolveTargetLocationFromProfile check.
  async getVisitCampaignContactEligibility(id: string, contactIds: string[]): Promise<ApiResponse<any>> {
    const timeoutMs = Math.max(20000, contactIds.length * 3000 + 15000);
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/contacts/eligibility?contactIds=${contactIds.join(',')}`, { method: 'GET' }, timeoutMs);
  }

  async removeVisitCampaignContact(id: string, contactId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/contacts/${contactId}`, { method: 'DELETE' });
  }

  async getVisitCampaignDistribution(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/distribution`, { method: 'GET' });
  }

  async autoDistributeVisitCampaign(id: string, payload: { departmentId: string; userIds?: string[] }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/distribute/auto`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async manualDistributeVisitCampaign(id: string, payload: { allocations: Array<{ visitId: string; assignedTo: string }> }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/visits/campaigns/${id}/distribute/manual`, { method: 'POST', body: JSON.stringify(payload) });
  }

  async connectPhoneAccount(payload: {
    provider: string;
    providerConfig: Record<string, any>;
    callerNumber?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/phone/account', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async disconnectPhoneAccount(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/phone/account', { method: 'DELETE' });
  }

  async getPhoneLimits(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/phone/limits', { method: 'GET' });
  }

  /**
   * Admin/owner only. Lists every extension already provisioned on the
   * connected InnoCalls account, each flagged with the business user it's
   * currently linked to (if any).
   */
  async discoverPhoneAgents(): Promise<ApiResponse<Array<{ extensionNumber: string; status: string; linkedUserId: string | null }>>> {
    return this.authenticatedRequest<any>('/api/phone/agents/discover', { method: 'GET' });
  }

  /**
   * Admin/owner only. Links an existing InnoCalls extension (found via
   * discoverPhoneAgents) to a business user.
   */
  async linkPhoneAgent(payload: { extensionNumber: string; userId: string; pin: string }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/phone/agents/link', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  /**
   * Returns { apiKey, extension, webrtcSecret } for the current user —
   * what the mobile WebRTC dialer needs to initialize. apiId/apiSecret
   * never leave the server.
   */
  async getPhoneAgentCredentials(): Promise<ApiResponse<{ apiKey: string; extension: string; webrtcSecret: string }>> {
    return this.authenticatedRequest<any>('/api/phone/agent/credentials', { method: 'GET' });
  }

  /**
   * Creates a call record — call this when the WebRTC SDK's callStarted
   * event fires (there is no server-side call dispatch for InnoCalls).
   */
  async createPhoneCall(payload: {
    phoneNumber: string;
    contactId?: string;
    direction?: 'inbound' | 'outbound';
    providerCallId: string;
    callerNumber?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/phone/calls', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  /**
   * Updates a call record's outcome — call this as the WebRTC SDK fires
   * callAnswered/callEnded/callMissed/callRejected/callFailed.
   */
  async updatePhoneCall(providerCallId: string, payload: { status?: string; duration?: number; endedAt?: string; answeredAt?: string; providerMetadata?: Record<string, any> }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/phone/calls/${providerCallId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  }

  async getPhoneCalls(params?: {
    contactId?: string;
    phoneIndex?: number;
    limit?: number;
    offset?: number;
    startDate?: Date;
    endDate?: Date;
    assignedTo?: string | string[];
    status?: string;
    direction?: string;
  }): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    if (params?.contactId) query.set('contactId', params.contactId);
    if (typeof params?.phoneIndex === 'number' && Number.isFinite(params.phoneIndex)) query.set('phoneIndex', String(params.phoneIndex));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.status) query.set('status', params.status);
    if (params?.direction) query.set('direction', params.direction);
    if (params?.assignedTo) {
      const values = Array.isArray(params.assignedTo) ? params.assignedTo : [params.assignedTo];
      values.forEach((v) => query.append('assignedTo', v));
    }
    const qs = query.toString();
    return this.authenticatedRequest<any>(`/api/phone/calls${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  // Reports — unified performance/reports data. See sendifier_api's reports module
  // (reports.view/reports.export/reports.manageSavedReports permissions) for the
  // View/Manager/Admin scoping this all funnels through server-side.
  /** Multi-select report filters (agents, departments, contacts), sent as
   * comma-separated lists; the API also still accepts the single userId/contactId. */
  private setReportIdFilters(query: URLSearchParams, params?: ReportIdFilters): void {
    if (params?.userIds?.length) query.set('userIds', params.userIds.join(','));
    if (params?.departmentIds?.length) query.set('departmentIds', params.departmentIds.join(','));
    if (params?.contactIds?.length) query.set('contactIds', params.contactIds.join(','));
    // Break times reset at the viewer's midnight, and downloads use local times
    if (!query.has('tz')) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) query.set('tz', tz);
      } catch { /* server falls back to UTC */ }
    }
  }

  /** Serving-time / SLA report: response, callback, visit and assignment timings,
   * per agent and in total (see reports-performance.service.ts). */
  async getReportsPerformance(params?: ReportIdFilters & { startDate?: Date; endDate?: Date }): Promise<ApiResponse<ReportsPerformance>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    this.setReportIdFilters(query, params);
    const qs = query.toString();
    return this.authenticatedRequest<ReportsPerformance>(`/api/reports/performance${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** The single Reports download: performance, messages, calls and visits
   * (each only if the caller may see it) for the current filters, in one file. */
  async exportReportsAll(format: 'csv' | 'xlsx' | 'pdf', params?: ReportIdFilters & {
    startDate?: Date; endDate?: Date; type?: string; templateName?: string; callStatus?: string; visitStatus?: string; lang?: string; tz?: string;
  }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('format', format);
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    this.setReportIdFilters(query, params);
    if (params?.type) query.set('type', params.type);
    if (params?.templateName) query.set('templateName', params.templateName);
    if (params?.callStatus) query.set('callStatus', params.callStatus);
    if (params?.visitStatus) query.set('visitStatus', params.visitStatus);
    if (params?.lang) query.set('lang', params.lang);
    if (params?.tz) query.set('tz', params.tz);
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/reports/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to export reports (${response.status})`);
    }
    const blob = await response.blob();
    const filename = this.extractFilenameFromContentDisposition(response, `reports.${format}`);
    return { blob, filename };
  }

  async exportReportsPerformance(format: 'csv' | 'xlsx' | 'pdf', params?: ReportIdFilters & { startDate?: Date; endDate?: Date; lang?: string; tz?: string }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('format', format);
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    this.setReportIdFilters(query, params);
    if (params?.lang) query.set('lang', params.lang);
    if (params?.tz) query.set('tz', params.tz);
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/reports/performance/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to export report (${response.status})`);
    }
    const blob = await response.blob();
    const filename = this.extractFilenameFromContentDisposition(response, `performance-report.${format}`);
    return { blob, filename };
  }

  async getReportsFilterDepartments(): Promise<ApiResponse<{ departmentId: string; name: string }[]>> {
    return this.authenticatedRequest<{ departmentId: string; name: string }[]>('/api/reports/filters/departments', { method: 'GET' });
  }

  // ─── Billing (server-owned plans, trials and Kashier checkout) ──────────
  async getBillingPlans(): Promise<ApiResponse<BillingPlan[]>> {
    return this.authenticatedRequest<BillingPlan[]>('/api/billing/plans', { method: 'GET' });
  }

  async getBillingOverview(): Promise<ApiResponse<BillingOverview>> {
    return this.authenticatedRequest<BillingOverview>('/api/billing/overview', { method: 'GET' });
  }

  /** Free plan / first-time trial activate here; otherwise requiresPayment. */
  async startPlan(planId: string): Promise<ApiResponse<{ activated: boolean; requiresPayment?: boolean; kind?: 'free' | 'trial'; trialDays?: number }>> {
    return this.authenticatedRequest('/api/billing/start', { method: 'POST', body: JSON.stringify({ planId }) });
  }

  async createBillingCheckout(params: { planId: string; billingCycle: BillingCycle; language?: 'en' | 'ar'; client: 'web' | 'app' }): Promise<ApiResponse<{ paymentId: string; checkoutUrl: string; amount: number; currency: string }>> {
    return this.authenticatedRequest('/api/billing/checkout', { method: 'POST', body: JSON.stringify(params) });
  }

  async getBillingCatalog(): Promise<ApiResponse<BillingCatalog>> {
    return this.authenticatedRequest<BillingCatalog>('/api/billing/catalog', { method: 'GET' });
  }

  async quoteAddons(items: Array<{ code: string; quantity: number }>): Promise<ApiResponse<AddonQuote>> {
    return this.authenticatedRequest<AddonQuote>('/api/billing/addons/quote', { method: 'POST', body: JSON.stringify({ items }) });
  }

  async createAddonCheckout(params: { items: Array<{ code: string; quantity: number }>; language?: 'en' | 'ar'; client: 'web' | 'app' }): Promise<ApiResponse<{ paymentId: string; checkoutUrl: string; amount: number; currency: string }>> {
    return this.authenticatedRequest('/api/billing/addons/checkout', { method: 'POST', body: JSON.stringify(params) });
  }

  /** Keep this many from the next renewal (0 = remove). */
  async scheduleAddon(code: string, keepQuantity: number): Promise<ApiResponse<{ code: string; quantity: number; renewQuantity: number }>> {
    return this.authenticatedRequest(`/api/billing/addons/${encodeURIComponent(code)}/schedule`, { method: 'POST', body: JSON.stringify({ keepQuantity }) });
  }

  /** Short-lived link to a paid payment's invoice PDF (open in a browser). */
  async getInvoiceLink(paymentId: string): Promise<ApiResponse<{ url: string; invoiceNumber?: string }>> {
    return this.authenticatedRequest(`/api/billing/payments/${encodeURIComponent(paymentId)}/invoice-link`, { method: 'GET' });
  }

  async emailInvoice(paymentId: string): Promise<ApiResponse<{ emailed: boolean }>> {
    return this.authenticatedRequest(`/api/billing/payments/${encodeURIComponent(paymentId)}/invoice-email`, { method: 'POST', body: '{}' });
  }

  async getBillingPaymentStatus(paymentId: string): Promise<ApiResponse<BillingPaymentStatus>> {
    return this.authenticatedRequest<BillingPaymentStatus>(`/api/billing/payments/${encodeURIComponent(paymentId)}`, { method: 'GET' });
  }

  /** Home screen dashboard: attention counts, range KPIs, agenda, trend, team. */
  /** "Get your business ready" steps beyond channels (owner/admin; empty for others). */
  async getSetupProgress(): Promise<ApiResponse<{ canSetUp: boolean; steps: Array<{ key: string; done: boolean }> }>> {
    return this.authenticatedRequest('/api/reports/setup-progress', { method: 'GET' });
  }

  async getHomeDashboard(params: { startDate: Date; endDate: Date; dayStart: Date; dayEnd: Date }): Promise<ApiResponse<HomeDashboardData>> {
    const query = new URLSearchParams({
      startDate: params.startDate.toISOString(),
      endDate: params.endDate.toISOString(),
      dayStart: params.dayStart.toISOString(),
      dayEnd: params.dayEnd.toISOString(),
    });
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) query.set('tz', tz);
    } catch { /* no Intl time zone support */ }
    return this.authenticatedRequest<HomeDashboardData>(`/api/reports/home?${query.toString()}`, { method: 'GET' });
  }

  async getReportsSummary(params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; userId?: string; visitStatus?: string }): Promise<ApiResponse<any>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.userId) query.set('userId', params.userId);
    if (params?.visitStatus) query.set('visitStatus', params.visitStatus);
    this.setReportIdFilters(query, params);
    const qs = query.toString();
    return this.authenticatedRequest<any>(`/api/reports/summary${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** Grouped-by-contact by default (one row per contact with a last-message preview +
   * count) — pass flat:true for the old per-message rows. */
  async getReportsMessages(params?: ReportIdFilters & {
    startDate?: Date;
    endDate?: Date;
    contactId?: string;
    type?: string;
    templateName?: string;
    userId?: string;
    flat?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<any[]>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.type) query.set('type', params.type);
    if (params?.templateName) query.set('templateName', params.templateName);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.flat) query.set('flat', 'true');
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.authenticatedRequest<any[]>(`/api/reports/messages${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  /** Returns the export file's Blob directly (not JSON) — caller is responsible for
   * turning it into a download, same pattern as CallHistoryScreen's recording fetch. */
  /** Extracts the filename the server picked (business name + report type + date range +
   * active filters — see reports.controller.ts's buildExportFilename) out of the
   * Content-Disposition header, rather than the caller inventing its own generic name.
   * Falls back to a generic name only if the header is somehow missing/unparsable. */
  private extractFilenameFromContentDisposition(response: Response, fallback: string): string {
    const header = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    if (!header) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
  }

  async exportReportsMessages(format: 'csv' | 'xlsx' | 'pdf', params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; type?: string; templateName?: string; userId?: string; lang?: string; tz?: string }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('format', format);
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.type) query.set('type', params.type);
    if (params?.templateName) query.set('templateName', params.templateName);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.lang) query.set('lang', params.lang);
    if (params?.tz) query.set('tz', params.tz);
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/reports/messages/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to export report (${response.status})`);
    }
    const blob = await response.blob();
    const filename = this.extractFilenameFromContentDisposition(response, `messages-report.${format}`);
    return { blob, filename };
  }

  /** Reports' OWN scoped calls list — deliberately separate from getPhoneCalls,
   * which goes through the generic phone module's contacts.view/assignment-based
   * scoping (a different permission domain). This one is scoped by reports.view's
   * View/Manager/Admin tiers and applies contacts.viewMobileNumber masking, so the
   * Reports screen's calls tab respects the same permission model as its
   * messages/summary tabs instead of silently falling back to whatever the phone
   * module's own access rules allow. */
  async getReportsCalls(params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; userId?: string; status?: string; limit?: number; offset?: number }): Promise<ApiResponse<any[]>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.authenticatedRequest<any[]>(`/api/reports/calls${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async exportReportsCalls(format: 'csv' | 'xlsx' | 'pdf', params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; userId?: string; status?: string; lang?: string; tz?: string }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('format', format);
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.status) query.set('status', params.status);
    if (params?.lang) query.set('lang', params.lang);
    if (params?.tz) query.set('tz', params.tz);
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/reports/calls/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to export report (${response.status})`);
    }
    const blob = await response.blob();
    const filename = this.extractFilenameFromContentDisposition(response, `calls-report.${format}`);
    return { blob, filename };
  }

  /** Reports' OWN scoped visits list — mirrors getReportsCalls: scoped by
   * reports.view's View/Manager/Admin tiers and applies the same
   * contacts.viewMobileNumber-style masking, rather than falling back to
   * whatever the visits module's own access rules allow. */
  async getReportsVisits(params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; userId?: string; status?: string; limit?: number; offset?: number }): Promise<ApiResponse<any[]>> {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.status) query.set('status', params.status);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    return this.authenticatedRequest<any[]>(`/api/reports/visits${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async exportReportsVisits(format: 'csv' | 'xlsx' | 'pdf', params?: ReportIdFilters & { startDate?: Date; endDate?: Date; contactId?: string; userId?: string; status?: string; lang?: string; tz?: string }): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams();
    query.set('format', format);
    if (params?.startDate) query.set('startDate', params.startDate.toISOString());
    if (params?.endDate) query.set('endDate', params.endDate.toISOString());
    if (params?.contactId) query.set('contactId', params.contactId);
    if (params?.userId) query.set('userId', params.userId);
    this.setReportIdFilters(query, params);
    if (params?.status) query.set('status', params.status);
    if (params?.lang) query.set('lang', params.lang);
    if (params?.tz) query.set('tz', params.tz);
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/reports/visits/export?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to export report (${response.status})`);
    }
    const blob = await response.blob();
    const filename = this.extractFilenameFromContentDisposition(response, `visits-report.${format}`);
    return { blob, filename };
  }

  /** Agents the caller's reports.view tier allows filtering by — View tier gets just
   * themselves (so the UI can hide the picker), Manager their resolved team, Admin
   * everyone active in the business. */
  async getReportsFilterUsers(): Promise<ApiResponse<{ userId: string; name: string; email?: string }[]>> {
    return this.authenticatedRequest<{ userId: string; name: string; email?: string }[]>('/api/reports/filters/users', { method: 'GET' });
  }

  async getSavedReports(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest<any[]>('/api/reports/saved', { method: 'GET' });
  }

  async createSavedReport(payload: {
    name: string;
    description?: string;
    reportType: string;
    filters: Record<string, any>;
    columns?: string[];
    isTemplate?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/reports/saved', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSavedReport(id: string, payload: {
    name?: string;
    description?: string;
    filters?: Record<string, any>;
    columns?: string[];
    isTemplate?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/reports/saved/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteSavedReport(id: string): Promise<ApiResponse<void>> {
    return this.authenticatedRequest<void>(`/api/reports/saved/${id}`, { method: 'DELETE' });
  }

  async updatePhoneNumberVerificationStatus(phoneNumberId: string, updateData: any): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Updating phone number verification status:', { phoneNumberId, updateData });
    
    try {
      const response = await this.authenticatedRequest<any>(`/api/whatsapp/phone-numbers/${phoneNumberId}/verification-status`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      console.log('✅ [ApiService] Phone number verification status update response:', response);
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error updating phone number verification status:', error);
      throw error;
    }
  }

  async createSubscription(subscriptionData: any): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.SUBSCRIPTION.CREATE, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${this.authToken}`,
      },
      body: JSON.stringify(subscriptionData),
    });
  }

  async refreshTokenWithSubscription(): Promise<ApiResponse<{ token: string; refreshToken: string; expiresIn: number }>> {
    console.log('🔄 [ApiService] Refreshing token with subscription data...');
    const response = await this.request<{ token: string; refreshToken: string; expiresIn: number }>('/api/subscription/refresh-token', {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${this.authToken}`,
      },
    });
    
    // Log response for debugging
    console.log('🔍 [ApiService] Refresh token response:', {
      statusCode: response.statusCode,
      hasData: !!response.data,
      hasToken: !!response.data?.token,
      tokenLength: response.data?.token?.length,
      tokenParts: response.data?.token ? response.data.token.split('.').length : 0,
      hasRefreshToken: !!response.data?.refreshToken,
      refreshTokenLength: response.data?.refreshToken?.length
    });
    
    return response;
  }

  async sendSubscriptionEmail(emailData: {
    to: string;
    userName: string;
    planName: string;
    planPrice: number;
    currency: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>(API_ENDPOINTS.SUBSCRIPTION.EMAIL, {
      method: 'POST',
      headers: {
        ...this.defaultHeaders,
        'Authorization': `Bearer ${this.authToken}`,
      },
      body: JSON.stringify(emailData),
    });
  }

  // Omni-Channel methods
  async getOmniChannelDashboard(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/omni-channel/dashboard');
  }

  async getOmniChannelStats(startDate?: Date, endDate?: Date): Promise<ApiResponse<OmniChannelStats>> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const queryString = params.toString();
    const url = queryString ? `/api/omni-channel/stats?${queryString}` : '/api/omni-channel/stats';

    return this.authenticatedRequest<OmniChannelStats>(url);
  }

  /** Ongoing/pending flow sessions — contacts currently paused mid-flow. */
  async getFlowSessions(options?: { status?: FlowSessionStatus; limit?: number; skip?: number; flowId?: string; withErrors?: boolean }): Promise<ApiResponse<FlowSessionsResponse>> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.skip) params.append('skip', String(options.skip));
    if (options?.flowId) params.append('flowId', options.flowId);
    if (options?.withErrors) params.append('withErrors', 'true');
    const queryString = params.toString();
    const url = queryString ? `/api/omni-channel/flow-sessions?${queryString}` : '/api/omni-channel/flow-sessions';
    return this.authenticatedRequest<FlowSessionsResponse>(url);
  }

  /** Dashboard → Flow runs: every run, filtered. */
  async getFlowRuns(filters: FlowRunFilters & { limit?: number; skip?: number }): Promise<ApiResponse<FlowRunsResponse>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    }
    return this.authenticatedRequest<FlowRunsResponse>(`/api/omni-channel/flow-runs?${params.toString()}`);
  }

  /** One run: steps, affected records, errors, variables, parent/child runs. */
  async getFlowRun(runId: string): Promise<ApiResponse<FlowRunDetail>> {
    return this.authenticatedRequest<FlowRunDetail>(`/api/omni-channel/flow-runs/${runId}`);
  }

  /** Attached flows that did not start, and why (kept 7 days). */
  async getFlowRunSkips(filters: Pick<FlowRunFilters, 'flowId' | 'contact' | 'from' | 'to'> & { limit?: number; skip?: number }): Promise<ApiResponse<{ items: FlowRunSkip[]; total: number }>> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
    }
    return this.authenticatedRequest(`/api/omni-channel/flow-run-skips?${params.toString()}`);
  }

  /** Continue a session stopped at a Pause node. */
  async resumeFlowSession(sessionId: string): Promise<ApiResponse<{ status: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/flow-sessions/${sessionId}/resume`, { method: 'POST', body: '{}' });
  }

  /** Continue a run from one of its flow's steps (reopens a finished one). */
  async moveFlowSession(sessionId: string, nodeId: string): Promise<ApiResponse<{ status: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/flow-sessions/${sessionId}/move`, { method: 'POST', body: JSON.stringify({ nodeId }) });
  }

  /** End a running or paused session. */
  async stopFlowSession(sessionId: string): Promise<ApiResponse<{ stopped: boolean }>> {
    return this.authenticatedRequest(`/api/omni-channel/flow-sessions/${sessionId}/stop`, { method: 'POST', body: '{}' });
  }

  /** Assignment stats for the business — includes a per-agent breakdown
   * (byAgent) when called without a specific userId. */
  async getAssignmentStats(userId?: string): Promise<ApiResponse<AssignmentStats>> {
    const url = userId ? `/api/assignments/stats?userId=${encodeURIComponent(userId)}` : '/api/assignments/stats';
    return this.authenticatedRequest<AssignmentStats>(url);
  }

  /** Paginated list of individual assignment records — used by the dashboard's
   * "View all" Assignments screen (aggregate-only data comes from
   * getAssignmentStats above). */
  async getAssignments(options?: {
    status?: AssignmentItem['status'];
    priority?: AssignmentItem['priority'];
    assignedTo?: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<AssignmentListResponse>> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.priority) params.append('priority', options.priority);
    if (options?.assignedTo) params.append('assignedTo', options.assignedTo);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.offset) params.append('offset', String(options.offset));
    const queryString = params.toString();
    const url = queryString ? `/api/assignments?${queryString}` : '/api/assignments';
    return this.authenticatedRequest<AssignmentListResponse>(url);
  }

  /** Latest campaigns (bulk-send tasks) — same endpoint/shape HomeScreen.tsx
   * already fetches inline; exposed here as a typed method so the dashboard
   * doesn't duplicate the raw authenticatedRequest call + field mapping. */
  async getLatestCampaigns(limit = 10): Promise<ApiResponse<DashboardCampaign[]>> {
    return this.authenticatedRequest<DashboardCampaign[]>(`/api/omni-channel/campaigns/latest?limit=${limit}`);
  }

  /**
   * Get parent contacts (omni-contact) - replaces getUnifiedContacts
   * @param options - Search and pagination options
   */
  async getContactTags(): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>('/api/omni-contact/tags');
  }

  /** Distinct city values already in use across this business's contacts —
   * backs the location filter's city picker. */
  async getContactLocationCities(): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>('/api/omni-contact/location-cities');
  }

  /** Distinct custom-field KEYS across this business's contacts — backs the
   * filter/sort field picker's "extra fields" section (custom fields vary
   * per business/import, so there's no fixed list). */
  async getContactCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>('/api/omni-contact/custom-fields');
  }

  /** Distinct values seen for one custom field key — backs that field's
   * filter checklist (pick-from-seen-values, same as city/country). */
  async getContactCustomFieldValues(key: string): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>(`/api/omni-contact/custom-fields/${encodeURIComponent(key)}/values`);
  }

  /** Distinct values seen for one FIXED field (name/phone/email/city/
   * country/nationality) — same searchable-checklist UX custom fields
   * already have, generalized to every filterable fixed field. */
  async getContactFixedFieldValues(fieldId: string): Promise<ApiResponse<string[]>> {
    return this.authenticatedRequest<string[]>(`/api/omni-contact/fields/${encodeURIComponent(fieldId)}/values`);
  }

  /** Exports contacts (a specific id set, or all when ids is omitted) as
   * CSV/Excel — masking (contacts.viewMobileNumber) is applied server-side,
   * so the returned body is already the correct, permission-aware export;
   * this returns the raw response so the caller can pick a platform-
   * appropriate way to save it (web: blob download; native: not yet wired,
   * same limitation the old client-side CSV export already had).
   * fields picks which columns to include (fixed field ids or custom field
   * keys) — omitted defaults to every fixed field server-side. When ids is
   * omitted, the rest of the filter/sort params (search/tags/data lists/
   * location/sort/custom fields — same shape ContactsList's own filters
   * use, see its ContactsFilterParams) are applied server-side too, so
   * "export all" exports the currently filtered set rather than literally
   * every contact in the business. Ignored when ids IS given, since an
   * explicit id set already IS the filter. */
  async exportContacts(options: {
    ids?: string[]; format?: 'csv' | 'xlsx'; lang?: string; fields?: string[];
    search?: string; includeTags?: string[]; excludeTags?: string[];
    includeDataListIds?: string[]; excludeDataListIds?: string[];
    filterCities?: string[]; filterCountries?: string[];
    nearLatitude?: number; nearLongitude?: number; radiusKm?: number;
    sortByDistance?: 'nearest' | 'farthest';
    sortBy?: string; sortOrder?: 'asc' | 'desc';
    customFieldFilters?: Array<{ key: string; values: string[]; hasValue?: boolean }>;
    includeAssignmentFilters?: Array<{ type: 'user' | 'department' | 'unassigned'; id?: string }>;
    excludeAssignmentFilters?: Array<{ type: 'user' | 'department' | 'unassigned'; id?: string }>;
  }): Promise<Response> {
    const businessId = this.getBusinessIdFromToken();
    return fetch(`${this.baseURL}/api/omni-contact/export`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json',
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
      body: JSON.stringify(options),
    });
  }

  /** Resolves a pasted Google/Apple Maps share link into
   * { latitude, longitude, address, city, country } — the "paste a link"
   * alternative to dropping a pin in MapLocationPicker. */
  async resolveContactLocationUrl(url: string): Promise<ApiResponse<{ latitude: number; longitude: number; address?: string; text?: string; formatted?: string; city?: string; country?: string }>> {
    return this.authenticatedRequest(`/api/omni-contact/resolve-location-url?url=${encodeURIComponent(url)}`);
  }

  /** As-you-type address suggestions for MapLocationPicker's search box —
   * backed by the self-hosted Nominatim instance, not the Overture Places
   * dataset (that endpoint, getPlacesNearby's sibling /api/places/search, is
   * disabled in production — see sendifier_api's src/routes/index.ts). */
  async searchAddressSuggestions(query: string, limit = 8): Promise<ApiResponse<Array<{ latitude: number; longitude: number; address?: string; city?: string; country?: string }>>> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    // Storefront customers aren't signed in — same search via the public route.
    if (!(await this.hasStoredSession())) return this.request(`/api/checkout/location/suggestions?${params.toString()}`, { method: 'GET' });
    return this.authenticatedRequest(`/api/omni-contact/search-location-suggestions?${params.toString()}`);
  }

  /** Signed in? Loads the persisted token first — on a fresh page load the
   * in-memory token can still be empty when a screen makes its first call,
   * which made signed-in users look like anonymous storefront visitors. */
  private async hasStoredSession(): Promise<boolean> {
    if (this.authToken) return true;
    try {
      await this.loadPersistedTokens();
    } catch {}
    return !!this.authToken;
  }

  /** Google/Apple Maps link (incl. short links) → location. The contact route
   * when signed in; the public route for storefront visitors. */
  async resolveMapsLink(url: string): Promise<ApiResponse<{ latitude: number; longitude: number; address?: string; city?: string; country?: string }>> {
    if (await this.hasStoredSession()) return this.resolveContactLocationUrl(url);
    return this.request(`/api/checkout/location/resolve-url?url=${encodeURIComponent(url)}`, { method: 'GET' });
  }

  /** Generic "what's nearby" POI pins (restaurants, hotels, shops, ...) for
   * MapLocationPicker's nearby-places layer — backed by Overpass (raw OSM
   * tags), not the disabled Overture Places dataset. See
   * sendifier_api's overpass-places.service.ts for why. */
  async getNearbyOverpassPlaces(params: {
    latitude: number;
    longitude: number;
    radiusMeters?: number;
    limit?: number;
  }): Promise<ApiResponse<Array<{ id: string; latitude: number; longitude: number; name: string; category: string }>>> {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)])
    ).toString();
    return this.authenticatedRequest(`/api/omni-contact/nearby-places?${query}`, { method: 'GET' });
  }

  async bulkUpdateContactTags(contactIds: string[], tags: string[], action: 'add' | 'remove'): Promise<ApiResponse<{ matchedCount: number; modifiedCount: number }>> {
    return this.authenticatedRequest<{ matchedCount: number; modifiedCount: number }>('/api/omni-contact/tags/bulk', {
      method: 'POST',
      body: JSON.stringify({ contactIds, tags, action }),
    });
  }

  async getParentContacts(options?: {
    limit?: number; offset?: number; search?: string; phone?: string; email?: string; name?: string; tag?: string;
    includeTags?: string[]; excludeTags?: string[]; dataListId?: string; includeDataListIds?: string[]; excludeDataListIds?: string[];
    filterCities?: string[]; filterCountries?: string[];
    nearLatitude?: number; nearLongitude?: number;
    /** Only applied when radiusFilterEnabled is true — contacts outside
     * radiusKm are excluded entirely, in either sort direction. A radiusKm
     * value with radiusFilterEnabled left false/omitted has NO effect —
     * this is what keeps the radius filter fully independent of
     * sortByDistance (e.g. "farthest" ranks every matching contact by
     * default; radiusFilterEnabled additionally restricts which ones are
     * even considered, regardless of which direction they're sorted). */
    radiusKm?: number;
    radiusFilterEnabled?: boolean;
    sortByDistance?: 'nearest' | 'farthest';
    /** When a radius/sortByDistance query is active, also include contacts
     * that have location TEXT (address/city/country) but no coordinates —
     * otherwise invisible to a coordinate-only geo query even though they
     * clearly have SOME known location. Defaults true server-side; only
     * pass false to opt out. Irrelevant with no distance filter active. */
    includeUnlocated?: boolean;
    /** Sort by an arbitrary fixed field (see backend's CONTACT_SORT_FIELD_PATHS,
     * e.g. 'name'/'phone'/'city'/'createdAt') or, when sortBy isn't one of
     * those ids, a literal profile.customFields key. Ignored when
     * sortByDistance is also set. */
    sortBy?: string; sortOrder?: 'asc' | 'desc';
    /** Exact-match filter on values, ANDed together — key is either a
     * fixed field id (name/phone/email/nationality — city/country have
     * their own dedicated filterCities/filterCountries above) or a literal
     * custom field key; same shape the backend's list() expects. */
    customFieldFilters?: Array<{ key: string; values: string[]; hasValue?: boolean }>;
    /** Restricts results to contacts with SOME location signal (coordinates
     * or address/city/country text) — used by the Visits contact picker,
     * where a location-less contact can never have a visit created for it. */
    hasLocationOrAddress?: boolean;
    /** "No reply from the contact in at least N days" — a real, user-typed
     * threshold (ContactAnalyticalFiltersModal), replacing what used to be
     * a fixed 3-day followUpDue constant. */
    noReplyDays?: number;
    /** "Has an order" (true) or "has NO order" (false) — optionally scoped
     * to [orderDateFrom, orderDateTo]; omitting both dates means "ever",
     * not "never" (a churn/lapsed-customer filter when hasOrderInRange is
     * false and no dates are given: "never placed an order at all"). */
    orderDateFrom?: string;
    orderDateTo?: string;
    hasOrderInRange?: boolean;
    /** "Contacted" (true) / "NOT contacted" (false) via contactedChannel
     * (any channel when omitted) within [contactedDateFrom, contactedDateTo]
     * — a channel's most recent message falling in that range. */
    contactedChannel?: string;
    contactedDateFrom?: string;
    contactedDateTo?: string;
    hasContactedInRange?: boolean;
    /** "Contacted us back N+ times" — total real messages ever exchanged
     * across every channel. */
    minMessageCount?: number;
    /** Current-assignment filter — legacy single-target params kept for compatibility. */
    assignmentType?: 'user' | 'department' | 'unassigned';
    assignmentId?: string;
    assignmentMode?: 'include' | 'exclude';
    /** Multi-select current-assignment filters. Each entry is user:<id>,
     * department:<id>, or unassigned. */
    includeAssignmentFilters?: Array<{ type: 'user' | 'department' | 'unassigned'; id?: string }>;
    excludeAssignmentFilters?: Array<{ type: 'user' | 'department' | 'unassigned'; id?: string }>;
    /** The Inbox list: only contacts something was said to or by, optionally by inbox status. */
    inbox?: boolean;
    inboxStatus?: 'unread' | 'active' | 'near expiry' | 'expired';
  }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (options?.inbox) params.append('inbox', 'true');
    if (options?.inboxStatus) params.append('inboxStatus', options.inboxStatus);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.name) params.append('name', options.name);
    if (options?.tag) params.append('tag', options.tag);
    if (options?.includeTags && options.includeTags.length > 0) params.append('includeTags', options.includeTags.join(','));
    if (options?.excludeTags && options.excludeTags.length > 0) params.append('excludeTags', options.excludeTags.join(','));
    if (options?.dataListId) params.append('dataListId', options.dataListId);
    if (options?.includeDataListIds && options.includeDataListIds.length > 0) params.append('includeDataListIds', options.includeDataListIds.join(','));
    if (options?.excludeDataListIds && options.excludeDataListIds.length > 0) params.append('excludeDataListIds', options.excludeDataListIds.join(','));
    if (options?.filterCities && options.filterCities.length > 0) params.append('filterCities', options.filterCities.join(','));
    if (options?.filterCountries && options.filterCountries.length > 0) params.append('filterCountries', options.filterCountries.join(','));
    if (options?.nearLatitude != null) params.append('nearLatitude', options.nearLatitude.toString());
    if (options?.nearLongitude != null) params.append('nearLongitude', options.nearLongitude.toString());
    if (options?.radiusKm != null) params.append('radiusKm', options.radiusKm.toString());
    if (options?.radiusFilterEnabled) params.append('radiusFilterEnabled', 'true');
    if (options?.sortByDistance) params.append('sortByDistance', options.sortByDistance);
    if (options?.includeUnlocated === false) params.append('includeUnlocated', 'false');
    if (options?.sortBy) params.append('sortBy', options.sortBy);
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options?.customFieldFilters && options.customFieldFilters.length > 0) {
      params.append('customFieldFilters', JSON.stringify(options.customFieldFilters));
    }
    if (options?.hasLocationOrAddress) params.append('hasLocationOrAddress', 'true');
    if (options?.noReplyDays) params.append('noReplyDays', options.noReplyDays.toString());
    if (options?.orderDateFrom) params.append('orderDateFrom', options.orderDateFrom);
    if (options?.orderDateTo) params.append('orderDateTo', options.orderDateTo);
    if (options?.hasOrderInRange !== undefined) params.append('hasOrderInRange', options.hasOrderInRange ? 'true' : 'false');
    if (options?.contactedChannel) params.append('contactedChannel', options.contactedChannel);
    if (options?.contactedDateFrom) params.append('contactedDateFrom', options.contactedDateFrom);
    if (options?.contactedDateTo) params.append('contactedDateTo', options.contactedDateTo);
    if (options?.hasContactedInRange !== undefined) params.append('hasContactedInRange', options.hasContactedInRange ? 'true' : 'false');
    if (options?.minMessageCount) params.append('minMessageCount', options.minMessageCount.toString());
    if (options?.assignmentType) params.append('assignmentType', options.assignmentType);
    if (options?.assignmentId) params.append('assignmentId', options.assignmentId);
    if (options?.assignmentMode) params.append('assignmentMode', options.assignmentMode);
    const encodeAssignmentFilters = (filters?: Array<{ type: 'user' | 'department' | 'unassigned'; id?: string }>) =>
      filters?.map((filter) => filter.type === 'unassigned' ? 'unassigned' : `${filter.type}:${filter.id}`).filter(Boolean).join(',');
    const includeAssignmentFilters = encodeAssignmentFilters(options?.includeAssignmentFilters);
    const excludeAssignmentFilters = encodeAssignmentFilters(options?.excludeAssignmentFilters);
    if (includeAssignmentFilters) params.append('includeAssignmentFilters', includeAssignmentFilters);
    if (excludeAssignmentFilters) params.append('excludeAssignmentFilters', excludeAssignmentFilters);

    const queryString = params.toString();
    // Use list endpoint for general listing, search endpoint only for specific identifier searches
    if (options?.phone || options?.email) {
      // Use search endpoint for identifier-based searches
      if (options?.phone) params.append('phone', options.phone);
      if (options?.email) params.append('email', options.email);
      const searchQueryString = params.toString();
      const url = `/api/omni-contact/search${searchQueryString ? `?${searchQueryString}` : ''}`;
      return this.authenticatedRequest<any>(url);
    } else {
      // Use list endpoint for general listing with optional name search
      const url = `/api/omni-contact${queryString ? `?${queryString}` : ''}`;
      return this.authenticatedRequest<any>(url);
    }
  }

  /**
   * Get parent contact by ID
   */
  async getParentContactById(contactId: string): Promise<ApiResponse<any>> {
    const url = `/api/omni-contact/${contactId}`;
    return this.authenticatedRequest<any>(url);
  }

  /**
   * Resolves the real, unmasked phone number to dial for a contact — deliberately
   * separate from getParentContactById, whose profile.phone may be masked
   * (contacts.viewMobileNumber) for the current user. Always call this right before
   * placing a call rather than reading a Contact object's own phone field, which may
   * be display-masked and unusable by the dialer.
   */
  async getContactDialTarget(contactId: string, channel?: 'whatsapp', channelContactId?: string, conversationId?: string, phoneIndex?: number): Promise<ApiResponse<{ phoneNumber: string }>> {
    const params = new URLSearchParams();
    if (channel) params.set('channel', channel);
    if (channelContactId) params.set('channelContactId', channelContactId);
    // When known, wins over everything else server-side — resolves the number
    // from THIS specific conversation rather than the contact's most-recently-
    // linked one, correct for a contact with more than one linked WhatsApp number.
    if (conversationId) params.set('conversationId', conversationId);
    // Targets one specific identifiers.phone[] entry by position — used by the
    // Contacts list's call-button picker for a contact with more than one saved
    // phone number (calling has no channelContactId-based link the way WhatsApp
    // does, so index-into-the-array is the only way to name a specific one).
    if (phoneIndex !== undefined) params.set('phoneIndex', String(phoneIndex));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.authenticatedRequest<{ phoneNumber: string }>(`/api/omni-contact/${contactId}/dial-target${query}`);
  }

  /** Mints a short-lived, single-use code standing in for this session's own
   * auth token + call target — see sendifier_api's call-session-code.service.ts
   * for why: this app's own JWT is large enough (embeds the full business/
   * subscription/permissions payload) that putting it directly in a URL
   * broke iOS's SFSafariViewController with "site can't be reached" on
   * native calling's own /call in-app-browser flow (AppNavigator.tsx's
   * startNativePhoneCall). */
  async createCallSessionCode(phoneNumber: string, contactId?: string, contactName?: string): Promise<ApiResponse<{ code: string }>> {
    return this.authenticatedRequest<{ code: string }>('/api/phone/call-session-code', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, contactId, contactName }),
    });
  }

  /**
   * Batch version of getContactDialTarget — resolves REAL, unmasked phone numbers for
   * many contacts at once. Use this (never a Contact object's own phoneNumber/phone
   * field, which may be display-masked) when building a bulk send recipient list —
   * confirmed real production bug: a masked user's bulk WhatsApp send was built from
   * masked contact data and got rejected by Meta's API for a malformed phone number.
   */
  async getContactSendTargets(contactIds: string[]): Promise<ApiResponse<{ results: Array<{ id: string; name: string; phoneNumber: string | null }> }>> {
    return this.authenticatedRequest<{ results: Array<{ id: string; name: string; phoneNumber: string | null }> }>('/api/omni-contact/send-targets', {
      method: 'POST',
      body: JSON.stringify({ contactIds }),
    });
  }

  /**
   * Create parent contact manually
   */
  async createParentContact(data: {
    primaryIdentifier: string;
    primaryIdentifierType?: string;
    identifiers?: {
      phone?: string;
      email?: string;
      whatsapp?: string;
      'facebook-messenger'?: string;
      instagram?: string;
      telegram?: string;
    };
    profile: {
      name: string;
      email?: string;
      phone?: string;
      avatar?: string;
      location?: {
        latitude?: number;
        longitude?: number;
        address?: string;
        city?: string;
        country?: string;
        nationality?: string;
      };
      tags?: string[];
      customFields?: Record<string, any>;
    };
    channels?: Array<{
      channel: string;
      channelContactId?: string;
      channelIdentifier?: string;
    }>;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/omni-contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update parent contact
   */
  async updateParentContact(contactId: string, data: {
    identifiers?: {
      phone?: string;
      email?: string;
      whatsapp?: string;
      'facebook-messenger'?: string;
      instagram?: string;
      telegram?: string;
    };
    // Explicit removal list — a channel/value NOT in `identifiers` is treated as
    // "not part of this update" (e.g. masked, unchanged, never loaded), never as
    // "delete this". Only entries listed here are actually removed. Confirmed
    // real production bug: before this existed, `identifiers` was treated as a
    // full-replacement snapshot, so a masked-and-therefore-omitted old number
    // (contacts.viewMobileNumber) was silently deleted the moment ANY other
    // identifier was added/edited and the form was saved.
    // `index` = this entry's position WITHIN its own identifiers.<type>[]
    // array — required to remove a MASKED identifier (the client never saw
    // its real value, so value-matching can never work server-side).
    removedIdentifiers?: Array<{ type: string; value: string; index?: number }>;
    profile?: {
      name?: string;
      email?: string;
      phone?: string;
      avatar?: string;
      location?: {
        latitude?: number;
        longitude?: number;
        address?: string;
        city?: string;
        country?: string;
        nationality?: string;
      };
      tags?: string[];
      customFields?: Record<string, any>;
    };
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * "Deletes" a contact — actually a soft-delete (status: 'archived') server-side.
   * Requires contacts.manage at the 'admin' tier, stricter than the 'edit' tier
   * that covers regular field updates.
   */
  async deleteContact(contactId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Marks one specific identifier value as the primary one for its channel type —
   * a contact can have several identifiers of the same type (e.g. two WhatsApp
   * numbers); this clears isPrimary on any other linkedChannelContacts entry of
   * the same channel and sets it on the one matching `value`.
   */
  async setPrimaryContactIdentifier(contactId: string, channel: string, value: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}/primary-identifier`, {
      method: 'PUT',
      body: JSON.stringify({ channel, value }),
    });
  }

  /**
   * Add a note to a parent contact
   */
  async addContactNote(contactId: string, content: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  /**
   * Delete a note from a parent contact
   */
  async deleteContactNote(contactId: string, noteId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}/notes/${noteId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Search parent contacts by identifiers (phone, email, etc.)
   */
  async searchParentContactsByIdentifiers(query: {
    phone?: string;
    email?: string;
    whatsapp?: string;
    'facebook-messenger'?: string;
    name?: string;
  }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (query.phone) params.append('phone', query.phone);
    if (query.email) params.append('email', query.email);
    if (query.whatsapp) params.append('whatsapp', query.whatsapp);
    if (query['facebook-messenger']) params.append('facebook-messenger', query['facebook-messenger']);
    if (query.name) params.append('name', query.name);
    
    const queryString = params.toString();
    const url = `/api/omni-contact/search${queryString ? `?${queryString}` : ''}`;
    return this.authenticatedRequest<any>(url);
  }

  /**
   * @deprecated Use getParentContacts instead
   * Get unified contacts - kept for backward compatibility
   */
  async getUnifiedContacts(options?: { limit?: number; offset?: number; search?: string }): Promise<ApiResponse<any>> {
    // Redirect to parent contacts API
    return this.getParentContacts(options);
  }

  /**
   * Get unified contacts for public checkout page (no auth required)
   * @param businessId - Business ID (required)
   * @param options - Search and pagination options
   */
  async getUnifiedContactsPublic(businessId: string, options?: { limit?: number; offset?: number; search?: string }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    params.append('businessId', businessId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    
    const queryString = params.toString();
    const url = `/api/checkout/contacts/unified?${queryString}`;
    return this.request<any>(url, {
      method: 'GET',
    });
  }

  /**
   * Search for a contact globally by phone number (across all businesses)
   * Public endpoint - no authentication required
   */
  /**
   * Product minimum quantities + vendor minimum order amounts for a cart —
   * the same rules the server enforces when the order is created (public).
   */
  async checkOrderMinimums(body: {
    businessId: string;
    currency?: string;
    items: Array<{ productId: string; variantId?: string; quantity: number; lineTotal: number }>;
    productIds?: string[];
  }): Promise<ApiResponse<OrderMinimumsReport>> {
    return this.request<OrderMinimumsReport>('/api/checkout/order-minimums', { method: 'POST', body: JSON.stringify(body) });
  }

  /**
   * Checkout prefill for the contact a storefront link was sent to — scoped
   * to that business + contact (public, no auth).
   */
  async getCheckoutPrefill(businessId: string, contactId: string): Promise<ApiResponse<{ found: boolean; contact?: { name?: string; phone?: string; email?: string; address?: string } }>> {
    const params = new URLSearchParams({ businessId, contactId });
    return this.request<any>(`/api/checkout/contacts/prefill?${params.toString()}`, { method: 'GET' });
  }

  async searchContactGloballyByPhone(phoneNumber: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    params.append('phoneNumber', phoneNumber);
    
    const queryString = params.toString();
    const url = `/api/checkout/contacts/search-by-phone?${queryString}`;
    return this.request<any>(url, {
      method: 'GET',
    });
  }

  /**
   * Submit order and send WhatsApp bill message with interactive buttons
   * Public endpoint - no authentication required
   */
  async submitOrder(data: {
    businessId: string;
    phoneNumber: string;
    items: any[];
    customer: {
      name: string;
      phone: string;
      email?: string;
      address?: string;
      location?: {
        latitude: number;
        longitude: number;
        address: string;
      };
    };
    subtotal?: number;
    tax?: number;
    extraCharges?: { name: string; amount: number }[];
    discount?: number;
    couponCode?: string;
    referralCode?: string;
    totalPrice?: number;
    notes?: string;
    catalogId?: string;
    quickReplyItemId?: string;
    /** From the storefront link — lets the server confirm the order back in
     * the conversation it came from (e.g. Messenger). */
    contactId?: string;
    conversationId?: string;
    language?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/api/checkout/submit-order', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getUnifiedConversations(options?: { limit?: number; offset?: number; search?: string; status?: string; channel?: string; groupByContact?: boolean; parentContactIds?: string[] }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (options?.parentContactIds?.length) params.append('parentContactIds', options.parentContactIds.join(','));
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    if (options?.search) params.append('search', options.search);
    if (options?.status) params.append('status', options.status);
    if (options?.channel) params.append('channel', options.channel);
    // Only sent when explicitly false — omitting it keeps the server's default
    // (grouped) behavior, matching every other existing caller of this method.
    if (options?.groupByContact === false) params.append('groupByContact', 'false');

    const queryString = params.toString();
    const url = `/api/omni-channel/conversations/unified${queryString ? `?${queryString}` : ''}`;
    console.log('🔍 [ApiService] Getting unified conversations with options:', options);
    const result = await this.authenticatedRequest<any>(url);
    return result;
  }

  /** Cross-channel outreach-guidance counts + sample lists (needs response /
   * follow-up due / never contacted) — powers HomeScreen's guidance widgets. */
  async getOutreachSummary(): Promise<ApiResponse<{
    needsResponse: { count: number; sample: Array<{ id: string; name?: string; channel?: string; channels?: string[]; lastMessageAt?: string | null; avatar?: string }> };
    followUpDue: { count: number; sample: Array<{ id: string; name?: string; channel?: string; channels?: string[]; lastMessageAt?: string | null; avatar?: string }> };
    neverContacted: { count: number; sample: Array<{ id: string; name?: string; avatar?: string }> };
    expiringSoon?: { count: number; sample: Array<{ id: string; name?: string; reason?: string; expiresAt?: string | null; avatar?: string }> };
    followUpDueDays: number;
    lastUpdated: string;
  }>> {
    return this.authenticatedRequest<any>('/api/omni-channel/outreach-summary');
  }

  // WhatsApp specific methods
  async getWhatsAppStats(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp stats...');
    return this.authenticatedRequest<any>('/api/whatsapp/stats');
  }

  async getWhatsAppDashboard(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp dashboard...');
    return this.authenticatedRequest<any>('/api/whatsapp/dashboard');
  }

  async getWhatsAppContacts(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp contacts...');
    return this.authenticatedRequest<any>('/api/whatsapp/contacts');
  }

  async getWhatsAppCustomTemplates(options?: { approvedOnly?: boolean }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp custom templates...');
    const query = options?.approvedOnly ? '?status=APPROVED' : '';
    return this.authenticatedRequest<any>(`/api/whatsapp/custom-templates${query}`);
  }

  async getWhatsAppCustomTemplate(templateId: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp custom template...', { templateId });
    return this.authenticatedRequest<any>(`/api/whatsapp/custom-templates/${templateId}`);
  }

  async processWhatsAppTemplate(templateId: string, parameters: Record<number, string>): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Processing WhatsApp template...', { templateId, parameters });
    return this.authenticatedRequest<any>('/api/whatsapp/custom-templates/process', {
      method: 'POST',
      body: JSON.stringify({ templateId, parameters }),
    });
  }

  /**
   * Submit a new WhatsApp template for Meta approval. Saves locally as PENDING
   * and calls Meta's Business Management API. Distinct from any local-only
   * template creation — this is the self-service submit-for-review flow.
   */
  async submitWhatsAppTemplateToMeta(data: {
    templateName: string;
    templateBody: string;
    templateHeader?: string;
    headerType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
    headerMediaUrl?: string;
    templateFooter?: string;
    language: string;
    category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    buttons?: Array<{
      type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL' | 'COPY_CODE';
      text: string;
      phoneNumber?: string;
      url?: string;
      example?: string;
      quickReplyItemId?: string;
    }>;
    parameters?: Array<{
      position: number;
      section: 'header' | 'body' | 'footer' | 'button';
      placeholder?: string;
      isRequired?: boolean;
    }>;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Submitting WhatsApp template to Meta...', { templateName: data.templateName });
    return this.authenticatedRequest<any>('/api/whatsapp/custom-templates/submit-to-meta', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Discovers templates on the business's Meta WABA and syncs local records/statuses.
   * Picks up templates approved/rejected on Meta's side and any created directly
   * via Meta's own UI, not just ones submitted through Sendifier.
   */
  async syncWhatsAppTemplatesFromMeta(): Promise<ApiResponse<{ templates: any[]; updatedCount: number; discoveredCount: number; deactivatedCount: number }>> {
    console.log('🔍 [ApiService] Syncing WhatsApp templates from Meta...');
    return this.authenticatedRequest<any>('/api/whatsapp/custom-templates/sync-from-meta', {
      method: 'POST',
    });
  }

  /**
   * Edits an already-submitted template's content on Meta (name is immutable;
   * only APPROVED/REJECTED/PAUSED templates can be edited). Resets the
   * template to PENDING for re-review on success.
   */
  async editWhatsAppTemplateOnMeta(templateId: string, data: {
    templateBody?: string;
    templateHeader?: string;
    headerType?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
    headerMediaUrl?: string;
    templateFooter?: string;
    language?: string;
    category?: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
    buttons?: Array<{
      type: 'QUICK_REPLY' | 'PHONE_NUMBER' | 'URL' | 'COPY_CODE';
      text: string;
      phoneNumber?: string;
      url?: string;
      example?: string;
      quickReplyItemId?: string;
    }>;
    parameters?: Array<{
      position: number;
      section: 'header' | 'body' | 'footer' | 'button';
      placeholder?: string;
      isRequired?: boolean;
    }>;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Editing WhatsApp template on Meta...', { templateId });
    return this.authenticatedRequest<any>(`/api/whatsapp/custom-templates/${templateId}/edit-on-meta`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Facebook Messenger specific methods
  async getFacebookStats(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Facebook Messenger stats...');
    return this.authenticatedRequest<any>('/api/facebook-messenger/stats');
  }

  async getFacebookDashboard(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Facebook Messenger dashboard...');
    return this.authenticatedRequest<any>('/api/facebook-messenger/dashboard');
  }

  async getFacebookContacts(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Facebook Messenger contacts...');
    return this.authenticatedRequest<any>('/api/facebook-messenger/contacts');
  }

  // Instagram specific methods
  async getInstagramStats(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Instagram stats...');
    return this.authenticatedRequest<any>('/api/instagram/stats');
  }

  async getInstagramDashboard(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Instagram dashboard...');
    return this.authenticatedRequest<any>('/api/instagram/dashboard');
  }

  async getInstagramContacts(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Instagram contacts...');
    return this.authenticatedRequest<any>('/api/instagram/contacts');
  }

  // X (Twitter) specific methods
  async getXStats(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting X (Twitter) stats...');
    return this.authenticatedRequest<any>('/api/x/stats');
  }

  async getXDashboard(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting X (Twitter) dashboard...');
    return this.authenticatedRequest<any>('/api/x/dashboard');
  }

  async getXContacts(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting X (Twitter) contacts...');
    return this.authenticatedRequest<any>('/api/x/contacts');
  }

  // Service-specific performance endpoints
  async getUserServicePerformance(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting user service performance...');
    return this.authenticatedRequest<any>('/api/users/performance');
  }

  async getWhatsAppServicePerformance(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting WhatsApp service performance...');
    return this.authenticatedRequest<any>('/api/whatsapp/performance');
  }

  async getFacebookServicePerformance(): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Getting Facebook service performance...');
    return this.authenticatedRequest<any>('/api/facebook-messenger/performance');
  }

  async getConversation(conversationId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-channel/conversations/${conversationId}`);
  }

  async getConversationDeliveryRetry(conversationId: string): Promise<ApiResponse<DeliveryRetryStatus | null>> {
    return this.authenticatedRequest(`/api/omni-channel/conversations/${encodeURIComponent(conversationId)}/delivery-retry`, { method: 'GET' });
  }

  async setConversationDeliveryRetry(conversationId: string, enabled: boolean): Promise<ApiResponse<DeliveryRetryStatus | null>> {
    return this.authenticatedRequest(`/api/omni-channel/conversations/${encodeURIComponent(conversationId)}/delivery-retry`, { method: 'PUT', body: JSON.stringify({ enabled }) });
  }

  async createOrFindConversation(contactData: {
    contactId: string;
    contactName: string;
    phoneNumber?: string;
    channel: string;
    providerId?: string;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Creating or finding conversation for contact:', contactData);
    return this.authenticatedRequest<any>('/api/omni-channel/conversations/create-or-find', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  async getConversationMessages(conversationId: string, options?: { limit?: number; offset?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    params.append('sortBy', options?.sortBy || 'timestamp');
    params.append('sortOrder', options?.sortOrder || 'desc');
    
    const queryString = params.toString();
    const url = `/api/omni-channel/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ''}`;
    return this.authenticatedRequest<any>(url);
  }

  async sendConversationMessage(conversationId: string, messageData: {
    content: string;
    type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'location_request' | 'interactive' | 'order_status' | 'template';
    /** A template message (retry): the approved template with its components. */
    template?: { name: string; language: string; components?: any[] };
    /** A template's filled-in header / body / footer / buttons, shown in the conversation. */
    interactiveData?: any;
    orderId?: string; // required when type is 'order_status' – backend builds formatted order message
    messagingType?: 'RESPONSE' | 'UPDATE' | 'MESSAGE_TAG';
    messageTag?: string; // e.g. 'human_agent' for Facebook outside 24h window
    replyTo?: string;
    replyToMessage?: any;
    interactive?: any;
    interactiveType?: string;
    referenceType?: 'product' | 'catalog' | 'calendar' | 'order';
    referenceId?: string;
    mediaUrl?: string;
    mediaType?: string;
    mediaName?: string;
    mediaSize?: number;
    latitude?: number;
    longitude?: number;
    address?: string;
  }): Promise<ApiResponse<any>> {
    // Validate required fields (content not required when type is order_status and orderId is provided)
    const isOrderStatus = messageData.type === 'order_status' && messageData.orderId;
    const allowEmptyContentTypes: Array<typeof messageData.type> = [
      'image',
      'video',
      'audio',
      'document',
      'location',
      'location_request',
      'interactive',
      'template'
    ];
    if (!isOrderStatus && !messageData.content && !allowEmptyContentTypes.includes(messageData.type)) {
      throw new Error('Message content is required');
    }

    // Use the omni-channel endpoint - backend will determine provider and recipient
    const omniChannelMessageData = {
      content: messageData.content || '',
      type: messageData.type,
      ...(messageData.orderId && { orderId: messageData.orderId }),
      ...(messageData.messagingType && { messagingType: messageData.messagingType }),
      ...(messageData.messageTag && { messageTag: messageData.messageTag }),
      // Include media fields if present
      ...(messageData.mediaUrl && { mediaUrl: messageData.mediaUrl }),
      ...(messageData.mediaType && { mediaType: messageData.mediaType }),
      ...(messageData.mediaName && { mediaName: messageData.mediaName }),
      ...(messageData.mediaSize && { mediaSize: messageData.mediaSize }),
      // Include location fields if present
      ...(messageData.latitude && { latitude: messageData.latitude }),
      ...(messageData.longitude && { longitude: messageData.longitude }),
      ...(messageData.address && { address: messageData.address }),
      // Include reply data if present
      ...(messageData.replyTo && {
        replyTo: messageData.replyTo,
        replyToMessage: messageData.replyToMessage
      }),
      ...(messageData.interactive && { interactive: messageData.interactive }),
      ...(messageData.interactiveType && { interactiveType: messageData.interactiveType }),
      ...(messageData.template && { template: messageData.template, templateName: messageData.template.name, templateLanguage: messageData.template.language }),
      ...(messageData.interactiveData && { interactiveData: messageData.interactiveData }),
      ...(messageData.referenceType && { referenceType: messageData.referenceType }),
      ...(messageData.referenceId && { referenceId: messageData.referenceId })
    };

    // Use the omni-channel endpoint for proper media handling
    console.log('📤 [ApiService] Sending omni-channel message:', {
      endpoint: `/api/omni-channel/conversations/${conversationId}/messages`,
      messageData: omniChannelMessageData
    });
    
    const result = await this.authenticatedRequest<any>(
      `/api/omni-channel/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(omniChannelMessageData),
      },
      60000
    );
    
    console.log('📤 [ApiService] Omni-channel response:', result);
    return result;
  }

  async searchMessages(query: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-channel/messages/search?q=${encodeURIComponent(query)}`);
  }

  async getChannelsStatus(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/omni-channel/channels/status');
  }

  async getChannelsHealth(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/omni-channel/channels/health');
  }

  async getUnifiedNotifications(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/omni-channel/notifications/unified');
  }

  /**
   * Get notifications for current user
   * GET /api/notifications?limit=10&types=system
   */
  async getNotifications(options?: {
    limit?: number;
    offset?: number;
    unreadOnly?: boolean;
    types?: string[];
  }): Promise<ApiResponse<{
    notifications: any[];
    unreadCount: number;
    total: number;
  }>> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.unreadOnly) params.append('unreadOnly', 'true');
      if (options?.types && options.types.length > 0) params.append('types', options.types.join(','));
      
      const queryString = params.toString();
      const url = `/api/notifications${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest<{
        notifications: any[];
        unreadCount: number;
        total: number;
      }>(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting notifications:', error);
      throw error;
    }
  }

  async markConversationAsRead(conversationId: string, contactId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-channel/conversations/${conversationId}/mark-read`, {
      method: 'POST',
      body: JSON.stringify({ contactId }),
    });
  }

  // Helper method to read file as base64
  private async readFileAsBase64(fileUri: string): Promise<string> {
    try {
      const response = await fetch(fileUri);
      const blob = await response.blob();
      
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix if present
          const base64Data = result.includes(',') ? result.split(',')[1] : result;
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('❌ [ApiService] Error reading file as base64:', error);
      throw new Error('Failed to read file');
    }
  }

  // Bulk contact upload methods
  /** Builds a File (web, converting blob: URLs) or {uri,type,name} (native)
   * suitable for a FormData 'file' field — same conversion uploadMedia
   * already does for the same blob-URL-on-web quirk. */
  private async toUploadableFile(fileUri: string, fileName: string, fileType: string): Promise<File | { uri: string; type: string; name: string }> {
    if (fileUri.startsWith('blob:')) {
      const response = await fetch(fileUri);
      const blob = await response.blob();
      return new File([blob], fileName, { type: fileType });
    }
    return { uri: fileUri, type: fileType, name: fileName };
  }

  /** Parses a picked contacts file generically (no fixed column names) and
   * returns its headers/sample rows plus a suggested field mapping — the
   * mapper review screen's data source. Never imports anything.
   * suggestedMapping/targetFields are entity-field-keyed, same shape and
   * direction as the Data List webhook contacts mapper (FieldMatcher.tsx) —
   * one entry per target field (Name, Phone, ...), each pointing at which
   * file column (sourcePath) feeds it. */
  async previewBulkContactUpload(fileUri: string, fileName: string, fileType: string): Promise<ApiResponse<{
    headers: string[];
    sampleRows: Record<string, string>[];
    totalRows: number;
    targetFields: Array<{ id: string; name: string }>;
    suggestedMapping: DataListWebhookFieldMapping[];
    fromRememberedMapping: boolean;
  }>> {
    const businessId = this.getBusinessIdFromToken();
    const fileToUpload = await this.toUploadableFile(fileUri, fileName, fileType);
    const formData = new FormData();
    formData.append('file', fileToUpload as any);

    const response = await fetch(`${this.baseURL}/api/omni-channel/contacts/bulk-upload/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
      body: formData,
    });
    return response.json();
  }

  /** Re-uploads the same file with the user's confirmed mapping — the
   * server now starts the actual import as a background job and responds
   * immediately with a jobId instead of the import result itself (a few
   * thousand rows can take long enough that waiting on this request hits
   * the reverse proxy's own timeout and returns a 504, even though the
   * import keeps running/succeeding server-side). Poll
   * getBulkContactUploadStatus(jobId) for the outcome. */
  async commitBulkContactUpload(
    fileUri: string,
    fileName: string,
    fileType: string,
    mapping: DataListWebhookFieldMapping[],
    channel: 'whatsapp' | 'facebook' = 'whatsapp'
  ): Promise<ApiResponse<{ jobId: string; totalRows: number; status: 'processing' }>> {
    const businessId = this.getBusinessIdFromToken();
    const fileToUpload = await this.toUploadableFile(fileUri, fileName, fileType);
    const formData = new FormData();
    formData.append('file', fileToUpload as any);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('channel', channel);

    const response = await fetch(`${this.baseURL}/api/omni-channel/contacts/bulk-upload/commit`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
      body: formData,
    });
    return response.json();
  }

  /** Polls a bulk-contact-upload job started by commitBulkContactUpload.
   * status stays 'processing' until the background pipeline finishes;
   * result/error are only populated once it's 'completed'/'failed'. */
  async getBulkContactUploadStatus(jobId: string): Promise<ApiResponse<{
    jobId: string;
    status: 'processing' | 'paused' | 'cancelled' | 'completed' | 'failed';
    totalRows: number;
    /** Rows actually processed so far — only meaningful once processingTotal
     * is set (invalid/duplicate rows are filtered out before processing
     * starts, so this counts against processingTotal, not totalRows). */
    processedCount: number;
    processingTotal: number | null;
    result: {
      totalProcessed: number;
      successful: number;
      failed: number;
      duplicates: number;
      contacts: any[];
      errors: any[];
    } | null;
    error: string | null;
  }>> {
    const businessId = this.getBusinessIdFromToken();
    const response = await fetch(`${this.baseURL}/api/omni-channel/contacts/bulk-upload/status/${jobId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
    });
    return response.json();
  }

  // ContactsList can be mounted more than once at a time (full screen +
  // compact right pane), each running its own BulkUploadProgressIndicator —
  // this short-TTL cache dedupes their near-simultaneous polls into one
  // actual request, same fix as getPhoneAccount's cache above.
  private activeBulkUploadJobsCache: { promise: Promise<ApiResponse<any>>; expiresAt: number } | null = null;
  private static readonly ACTIVE_BULK_UPLOAD_JOBS_CACHE_TTL_MS = 2000;

  /** Every still-processing bulk upload job for the business, regardless of
   * which screen/device started it — backs a global progress indicator so
   * navigating away from the upload screen (or a teammate starting their own
   * upload) doesn't mean losing visibility into what's still running. */
  async getActiveBulkContactUploadJobs(): Promise<ApiResponse<{
    jobs: Array<{
      jobId: string;
      status: 'processing' | 'paused';
      totalRows: number;
      processedCount: number;
      processingTotal: number | null;
      createdAt: string;
    }>;
  }>> {
    const now = Date.now();
    if (this.activeBulkUploadJobsCache && this.activeBulkUploadJobsCache.expiresAt > now) {
      return this.activeBulkUploadJobsCache.promise;
    }
    const businessId = this.getBusinessIdFromToken();
    const promise = fetch(`${this.baseURL}/api/omni-channel/contacts/bulk-upload/active`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.authToken}`,
        ...(businessId ? { 'x-business-id': businessId } : {}),
      },
    }).then((response) => response.json());
    this.activeBulkUploadJobsCache = { promise, expiresAt: now + ApiService.ACTIVE_BULK_UPLOAD_JOBS_CACHE_TTL_MS };
    promise.catch(() => { this.activeBulkUploadJobsCache = null; });
    return promise;
  }

  /** Pause/resume/cancel a bulk upload job — lets the user manage an import
   * stuck mid-run (e.g. a deploy restarted the API mid-import, leaving it
   * paused automatically — see sendifier_api's reconcileOrphanedJobsOnStartup)
   * or deliberately pause/cancel one without losing the already-uploaded
   * file and having to redo the mapping step. */
  async pauseBulkContactUpload(jobId: string): Promise<ApiResponse<{ jobId: string; status: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/contacts/bulk-upload/${jobId}/pause`, { method: 'POST' });
  }

  async resumeBulkContactUpload(jobId: string): Promise<ApiResponse<{ jobId: string; status: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/contacts/bulk-upload/${jobId}/resume`, { method: 'POST' });
  }

  async cancelBulkContactUpload(jobId: string): Promise<ApiResponse<{ jobId: string; status: string }>> {
    return this.authenticatedRequest(`/api/omni-channel/contacts/bulk-upload/${jobId}/cancel`, { method: 'POST' });
  }

  async uploadBulkContacts(fileUri: string, fileName: string, fileType: string, channel: 'whatsapp' | 'facebook' = 'whatsapp'): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Uploading bulk contacts:', { fileUri, fileName, fileType, channel });

    try {
      // Extract businessId from token for header
      const businessId = this.getBusinessIdFromToken();
      
      // Read file as base64 for React Native compatibility
      const fileData = await this.readFileAsBase64(fileUri);
      
      console.log('🔍 [ApiService] File read as base64:', {
        fileName,
        fileType,
        dataLength: fileData.length,
        channel
      });

      // Prepare headers with business ID
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${this.authToken}`,
        'Content-Type': 'application/json',
      };
      
      // Add business ID header if available
      if (businessId) {
        headers['x-business-id'] = businessId;
        console.log('🔍 [ApiService] Adding business ID header:', businessId);
      }

      const response = await fetch(`${this.baseURL}/api/omni-channel/contacts/bulk-upload-base64`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          fileData,
          fileName,
          fileMimetype: fileType,
          channel
        }),
      });

      if (!response.ok) {
        let errorData;
        const errorText = await response.text();
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText };
        }
        console.error('❌ [ApiService] Bulk upload failed:', { 
          status: response.status, 
          error: errorText,
          parsed: errorData
        });
        
        // Return the error response in the same format
        return errorData;
      }

      const result = await response.json();
      console.log('✅ [ApiService] Bulk contacts uploaded:', result);
      return result;
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading bulk contacts:', error);
      throw error;
    }
  }

  async downloadContactTemplate(): Promise<string> {
    return `${this.baseURL}/api/omni-channel/contacts/download-template`;
  }

  // Media upload methods
  async uploadMedia(fileUri: string, fileName: string, fileType: string, providerId?: string, itemId?: string): Promise<ApiResponse<{ mediaUrl: string; mediaId: string; mediaType: string; mediaName: string; mediaSize: number }>> {
    console.log('🔍 [ApiService] Uploading media:', { fileUri, fileName, fileType, providerId });
    
    try {
      // Handle blob URLs by converting them to File objects
      let fileToUpload: File | { uri: string; type: string; name: string };
      
      if (fileUri.startsWith('blob:')) {
        // Convert blob URL to File object for web
        console.log('🔍 [ApiService] Converting blob URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted blob to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert blob URL to File:', error);
          throw new Error('Failed to process media file. Please try again.');
        }
      } else {
        // For React Native, use the original format
        fileToUpload = {
          uri: fileUri,
          type: fileType,
          name: fileName,
        };
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', fileToUpload as any);
      
      if (providerId) {
        formData.append('providerId', providerId);
      }
      
      if (itemId) {
        formData.append('itemId', itemId);
      }

      // Determine the correct upload endpoint based on provider
      const uploadEndpoint = providerId === 'default-facebook-messenger' 
        ? '/api/facebook-messenger/media/upload'
        : '/api/whatsapp/media/upload';
        
      console.log('🔍 [ApiService] Upload endpoint determined:', {
        providerId,
        uploadEndpoint,
        fileUri,
        fileName,
        fileType
      });

      const response = await fetch(`${this.baseURL}${uploadEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          // Don't set Content-Type header - let fetch set it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [ApiService] Upload failed:', { status: response.status, error: errorText });
        throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ [ApiService] Media upload response:', result);
      
      if (result.statusCode === 1000 && result.data) {
        // Validate that we received a proper media URL from the server
        if (!result.data.mediaUrl || result.data.mediaUrl.startsWith('file://')) {
          throw new Error('Server did not return a valid media URL');
        }
        
        return {
          data: {
            mediaUrl: result.data.mediaUrl, // Use server URL only - no fallback to local URI
            mediaId: result.data.mediaId, // WhatsApp media ID
            mediaType: result.data.mediaType || fileType, // Use converted MIME type from server (e.g., audio/ogg; codecs=opus)
            mediaName: result.data.mediaName || fileName, // Use server filename
            mediaSize: result.data.mediaSize || 0 // Use server file size
          },
          statusCode: 1000,
          messages: ['Media uploaded successfully'],
        };
      } else {
        throw new Error(result.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading media:', error);
      
      // Return proper error instead of fake success with local URI
      return {
        data: undefined as any,
        statusCode: 5000,
        messages: [],
        errors: [{
          field: 'file',
          message: error.message || 'Failed to upload media. Please check your internet connection and try again.'
        }]
      };
    }
  }

  // User Management Methods
  async getBusinessUsers(businessId: string): Promise<ApiResponse<BusinessUser[]>> {
    return this.authenticatedRequest<BusinessUser[]>(`/api/business/${businessId}/users`);
  }

  async getUserLimits(businessId: string): Promise<ApiResponse<UserLimits>> {
    return this.authenticatedRequest<UserLimits>(`/api/business/${businessId}/users/limits`);
  }

  async inviteUser(businessId: string, inviteData: InviteUserData): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/business/${businessId}/users/invite`, {
      method: 'POST',
      body: JSON.stringify(inviteData),
    });
  }

  async updateUser(businessId: string, userId: string, updateData: Partial<BusinessUser>): Promise<ApiResponse<BusinessUser>> {
    return this.authenticatedRequest<BusinessUser>(`/api/business/${businessId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  async removeUser(businessId: string, userId: string): Promise<ApiResponse<boolean>> {
    return this.authenticatedRequest<boolean>(`/api/business/${businessId}/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async bulkUpdateUserRole(businessId: string, userIds: string[], role: 'admin' | 'manager' | 'member' | 'viewer'): Promise<ApiResponse<{ updated: number; failed: Array<{ userId: string; error: string }> }>> {
    return this.authenticatedRequest(`/api/business/${businessId}/users/bulk/role`, {
      method: 'PUT',
      body: JSON.stringify({ userIds, role }),
    });
  }

  async bulkRemoveUsers(businessId: string, userIds: string[]): Promise<ApiResponse<{ updated: number; failed: Array<{ userId: string; error: string }> }>> {
    return this.authenticatedRequest(`/api/business/${businessId}/users/bulk`, {
      method: 'DELETE',
      body: JSON.stringify({ userIds }),
    });
  }

  async resendInvitation(businessId: string, userId: string): Promise<ApiResponse<boolean>> {
    return this.authenticatedRequest<boolean>(`/api/business/${businessId}/users/${userId}/resend-invitation`, {
      method: 'POST',
    });
  }


  async getUserInvitations(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest<any[]>('/api/users/invitations');
  }

  // WhatsApp Message Methods
  async sendWhatsAppMessage(data: {
    to: string;
    message: string;
    businessPhoneNumberId: string;
    accessToken?: string;
    businessAccountId?: string;
    businessId?: string;
    contactName?: string;
    userId?: string;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Sending WhatsApp message:', data);
    
    try {
      const response = await this.authenticatedRequest<any>('/api/whatsapp/send-message', {
        method: 'POST',
        body: JSON.stringify({
          to: data.to,
          type: 'text',
          content: data.message,
          metadata: {
            businessPhoneNumberId: data.businessPhoneNumberId,
            // Add WhatsApp Business API credentials from parameters
            accessToken: data.accessToken,
            businessAccountId: data.businessAccountId,
            phoneNumberId: data.businessPhoneNumberId,
            businessId: data.businessId, // Add businessId for proper contact/conversation creation
            contactName: data.contactName, // Add contact name for proper contact creation
            userId: data.userId // Add current user ID for proper contact/conversation creation
          }
        }),
      });
      
      console.log('✅ [ApiService] WhatsApp message sent successfully');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error sending WhatsApp message:', error);
      throw error;
    }
  }

  async sendWhatsAppTemplateMessage(data: {
    to: string;
    templateName: string;
    businessPhoneNumberId: string;
    parameters?: any[];
    userId?: string;
    languageCode?: string;
    reason?: string;
    accessToken?: string;
    businessAccountId?: string;
    businessId?: string;
    contactName?: string;
    /** The omni-contact's real _id, when already known (e.g. sending from an
     * already-open conversation/contact profile) — lets the backend reuse that
     * contact's existing linked WhatsApp conversation instead of re-resolving
     * purely from the phone string, which can otherwise land on a different
     * conversation than the one already shown for this contact. */
    contactId?: string;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Sending WhatsApp template message:', data);
    console.log('🔍 [ApiService] Parameters array length:', data.parameters?.length);
    console.log('🔍 [ApiService] Parameters array:', data.parameters);
    
    try {
      const response = await this.authenticatedRequest<any>('/api/whatsapp/send-template', {
        method: 'POST',
        body: JSON.stringify({
          userId: data.userId || 'default-user',
          providerId: 'default-whatsapp',
          to: data.to,
          contactId: data.contactId,
          template: {
            name: data.templateName,
            language: data.languageCode || 'ar', // Backend converts to {code: 'ar'}
            components: [
              {
                type: 'header',
                parameters: [
                  {
                    type: 'text',
                    text: data.parameters?.[0] || '' // First parameter for header
                  }
                ]
              },
              {
                type: 'body',
                parameters: [
                  {
                    type: 'text',
                    text: data.parameters?.[1] || '' // User's custom message
                  }
                ]
              }
            ]
          },
          reason: data.reason || 'customer_initiated', // Required reason parameter
          metadata: {
            businessPhoneNumberId: data.businessPhoneNumberId,
            // Add WhatsApp Business API credentials from parameters
            accessToken: data.accessToken,
            businessAccountId: data.businessAccountId,
            phoneNumberId: data.businessPhoneNumberId,
            businessId: data.businessId, // Add businessId for proper contact/conversation creation
            contactName: data.contactName // Add contact name for proper contact creation
          }
        }),
      });
      
      console.log('🔍 [ApiService] Final template structure sent to backend:', {
        template: {
          name: data.templateName,
          language: data.languageCode || 'ar',
          components: [
            {
              type: 'header',
              parameters: [
                {
                  type: 'text',
                  text: data.parameters?.[0] || ''
                }
              ]
            },
            {
              type: 'body',
              parameters: [
                {
                  type: 'text',
                  text: data.parameters?.[1] || ''
                }
              ]
            }
          ]
        }
      });
      
      console.log('✅ [ApiService] WhatsApp template message sent successfully');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error sending WhatsApp template message:', error);
      throw error;
    }
  }

  /** Send a WhatsApp template with a pre-built components[] array (for custom templates with arbitrary params). */
  async sendWhatsAppTemplateMessageWithComponents(data: {
    to: string;
    templateName: string;
    languageCode: string;
    components: { type: string; parameters: { type: string; text: string }[] }[];
    businessPhoneNumberId: string;
    accessToken?: string;
    businessAccountId?: string;
    businessId?: string;
    contactName?: string;
    userId?: string;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>('/api/whatsapp/send-template', {
      method: 'POST',
      body: JSON.stringify({
        userId: data.userId || 'default-user',
        providerId: 'default-whatsapp',
        to: data.to,
        template: {
          name: data.templateName,
          language: data.languageCode,
          components: data.components,
        },
        reason: data.reason || 'reopen_window',
        metadata: {
          businessPhoneNumberId: data.businessPhoneNumberId,
          accessToken: data.accessToken,
          businessAccountId: data.businessAccountId,
          phoneNumberId: data.businessPhoneNumberId,
          businessId: data.businessId,
          contactName: data.contactName,
        },
      }),
    });
  }

  // Contact management methods
  async checkContactExists(phoneNumber: string): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Checking if contact exists:', phoneNumber);
    try {
      const response = await this.authenticatedRequest<any>(`/api/omni-channel/contacts/check-exists`, {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
      });
      
      console.log('✅ [ApiService] Contact exists check completed');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error checking contact existence:', error);
      throw error;
    }
  }

  async addContact(data: {
    name: string;
    phoneNumber: string;
    businessId?: string;
  }): Promise<ApiResponse<any>> {
    console.log('🔍 [ApiService] Adding contact:', data);
    try {
      const response = await this.authenticatedRequest<any>('/api/omni-channel/contacts', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name,
          phoneNumber: data.phoneNumber,
          businessId: data.businessId,
          // Note: userId is now extracted from JWT token on the backend
        }),
      });
      
      console.log('✅ [ApiService] Contact added successfully');
      return response;
    } catch (error) {
      console.error('❌ [ApiService] Error adding contact:', error);
      throw error;
    }
  }

  async checkWhatsAppLimits(): Promise<ApiResponse<{ canSend: boolean; message: string; current: number; max: number }>> {
    console.log('🔍 [ApiService] Checking WhatsApp message limits...');
    
    // Don't catch errors here - let authenticatedRequest handle token refresh
    const response = await this.authenticatedRequest<{ canSend: boolean; message: string; current: number; max: number }>('/api/whatsapp/check-limits', {
      method: 'GET'
    });
    
    console.log('✅ [ApiService] WhatsApp limits checked:', response);
    return response;
  }

  /**
   * Create a task for sending bulk WhatsApp messages
   * Supports both immediate and scheduled execution
   */
  async createWhatsAppMessageTask(data: {
    contacts: Array<{
      name: string;
      phoneNumber: string;
      countryCode?: string;
      dialCode?: string;
      contactId?: string;
    }>;
    message: string;
    taskName?: string;
    templateName?: string;
    templateLanguage?: string;
    parameters?: string[];
    headerAsset?: {
      type: 'image' | 'video' | 'document' | 'location';
      url?: string;
      mediaId?: string;
      filename?: string;
      isParameterized?: boolean;
      position?: number;
      latitude?: number;
      longitude?: number;
      address?: string;
    };
    schedule?: {
      enabled: boolean;
      scheduledAt?: string; // ISO string
    };
  }): Promise<ApiResponse<{
    task: any;
    executed: boolean;
    executionResult?: any;
    scheduledAt?: string;
    message?: string;
    error?: string;
  }>> {
    try {
      return await this.authenticatedRequest('/api/whatsapp/create-message-task', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating message task:', error);
      throw error;
    }
  }

  /**
   * Upload media file to Firebase storage (base64)
   */
  async uploadMediaToStorage(data: {
    fileName: string;
    fileData: string; // base64 encoded
    assetType: 'image' | 'video' | 'document' | 'location';
    businessId: string;
    campaignName: string;
  }): Promise<{ url: string; mediaId: string }> {
    try {
      const response = await this.authenticatedRequest('/api/storage/upload-media', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      
      if (response.statusCode !== 1000 || !response.data) {
        throw new Error(response.errors?.[0]?.message || 'Failed to upload media');
      }

      return response.data;
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading media:', error);
      throw error;
    }
  }

  /**
   * Forward an existing message to selected conversations (omni-channel)
   */
  async forwardMessage(payload: {
    source: {
      channel: string;
      messageId?: string;
      messageInternalId?: string;
      internalMessageId?: string;
      externalId?: string;
      [key: string]: any;
    };
    targets: Array<{
      channel: string;
      conversationId: string;
      contactId: string;
      contactName?: string;
      phoneNumber?: string;
      providerId?: string;
      phoneNumberId?: string;
    }>;
    schedule?: {
      enabled: boolean;
      scheduledAt?: string;
    };
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/messages/forward', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error forwarding message:', error);
      throw error;
    }
  }

  /**
   * Get business tasks
   */
  async getBusinessTasks(): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest('/api/tasks/business', {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting business tasks:', error);
      throw error;
    }
  }

  /**
   * Get execution history for a specific task
   */
  async getTaskExecutionHistory(taskId: string, limit: number = 50): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/tasks/${taskId}/executions?limit=${limit}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting task execution history:', error);
      throw error;
    }
  }

  /**
   * Get task by ID
   */
  async getTaskById(taskId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/tasks/${taskId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting task:', error);
      throw error;
    }
  }

  /**
   * Delete (cancel) a task
   */
  async deleteTask(taskId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/tasks/${taskId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting task:', error);
      throw error;
    }
  }

  /**
   * Update a task (e.g. reschedule: pass nextExecutionAt)
   */
  async updateTask(taskId: string, data: { nextExecutionAt?: string; cronExpression?: string; status?: number }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating task:', error);
      throw error;
    }
  }

  /**
   * Execute a task immediately — works for onDemand tasks and for 'scheduled'
   * tasks too (lets a business "run now" a periodic job, e.g. a data list's
   * webhook sync, without waiting for its next cron tick).
   */
  async executeTask(taskId: string, options?: { background?: boolean }): Promise<ApiResponse<any>> {
    try {
      // background: the server starts the run and returns at once
      // ({ started, alreadyRunning }); follow it via getTaskExecutionHistory.
      const query = options?.background ? '?background=true' : '';
      return await this.authenticatedRequest(`/api/tasks/${taskId}/execute${query}`, {
        method: 'POST',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error executing task:', error);
      throw error;
    }
  }

  /**
   * Get message statistics for a task
   */
  async getTaskMessageStats(taskId: string): Promise<ApiResponse<{
    totalSent: number;
    failed: number;
    sent: number;
    delivered: number;
    read: number;
    pending: number;
    billableMessages: number;
    successRate: number;
    failureRate: number;
    contactsByStatus?: {
      failed?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      pending?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      sent?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      delivered?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      read?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      replied?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
      handled?: Array<{ contactId: string; name?: string; phoneNumber?: string; error?: string }>;
    };
    /** Set when the campaign was paused/aborted mid-run (Meta account quality
     * flag, or an observed high real delivery-failure rate) — some contacts
     * were never attempted at all, distinct from per-contact send failures. */
    pausedReason?: string;
    breakdown: {
      failed: number;
      sentButNotDelivered: number;
      sent: number;
      delivered: number;
      read: number;
      pending: number;
    };
  }>> {
    try {
      return await this.authenticatedRequest(`/api/whatsapp/task/${taskId}/message-stats`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting task message stats:', error);
      throw error;
    }
  }

  private mapUserFromApi(userData: any): User {
    return {
      id: userData.id || userData._id,
      email: userData.email,
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      username: userData.email ? userData.email.split('@')[0] : '',
      isEmailVerified: userData.emailVerified || false,
      isActive: userData.isActive ?? true,
      mobile: userData.mobile || userData.phone,
      phone: userData.phone || userData.mobile,
      createdAt: userData.createdAt || new Date().toISOString(),
      updatedAt: userData.updatedAt || new Date().toISOString(),
      timezone: userData.timezone,
      timezoneOffset: userData.timezoneOffset,
      country: userData.country,
      city: userData.city,
      lastTimezoneUpdate: userData.lastTimezoneUpdate,
      preferences: userData.preferences,
    };
  }

  // ============================================================================
  // PRODUCT API METHODS
  // ============================================================================

  /**
   * Get products by catalogue ID
   */
  async getProductsByCatalogue(
    catalogueId: string,
    query?: {
      dataListId?: string;
      search?: string;
      categoryIds?: string[];
      brand?: string;
      availability?: string;
      condition?: string;
      minPrice?: number;
      maxPrice?: number;
      hasVariants?: boolean;
      lowStock?: boolean;
      sku?: string;
      barcode?: string;
      limit?: number;
      skip?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      /** JSON-encoded CustomFieldFilterEntry[] — see SortFilterModal.tsx. */
      customFieldFilters?: string;
      /** Advanced filters: vendorIds ("none" = no vendor), stock (in|low|out), productTypes, hasImage (yes|no), createdFrom/createdTo. */
      vendorIds?: string;
      stock?: 'in' | 'low' | 'out';
      productTypes?: string;
      hasImage?: 'yes' | 'no';
      createdFrom?: string;
      createdTo?: string;
    }
  ): Promise<ApiResponse<{
    products: any[];
    pagination: { page: number; limit: number; total: number };
  }>> {
    try {
      const queryParams = new URLSearchParams();
      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              queryParams.append(key, value.join(','));
            } else {
              queryParams.append(key, String(value));
            }
          }
        });
      }
      const queryString = queryParams.toString();
      const endpoint = `/api/omni-channel/catalogues/${catalogueId}/products${queryString ? `?${queryString}` : ''}`;
      return await this.authenticatedRequest(endpoint, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting products by catalogue:', error);
      throw error;
    }
  }

  /**
   * Get products for the business — catalogueId optional, unlike
   * getProductsByCatalogue. For pickers that search/browse across all of a
   * business's products without requiring a catalog to be chosen first
   * (e.g. Orders system Data List's default-product picker).
   */
  async getProducts(query?: {
    catalogueId?: string;
    search?: string;
    sku?: string;
    limit?: number;
    skip?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** JSON-encoded CustomFieldFilterEntry[] — see SortFilterModal.tsx. */
    customFieldFilters?: string;
  }): Promise<ApiResponse<{ products: any[]; pagination: { page: number; limit: number; total: number } }>> {
    try {
      const queryParams = new URLSearchParams();
      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value !== undefined && value !== null) queryParams.append(key, String(value));
        });
      }
      const queryString = queryParams.toString();
      return await this.authenticatedRequest(`/api/omni-channel/products${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting products:', error);
      throw error;
    }
  }

  async getProductCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/products/custom-fields', { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting product custom field keys:', error);
      throw error;
    }
  }

  async getProductCustomFieldValues(key: string): Promise<ApiResponse<Array<string | { value: string; label: string }>>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/products/custom-fields/${encodeURIComponent(key)}/values`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting product custom field values:', error);
      throw error;
    }
  }

  /** One endpoint behind every multi-select bulk-link action — add/remove
   * vendorIds/catalogueIds across many products at once. Used from the
   * Products list ("Add to Vendor"/"Add to Category"), and from the
   * Vendors/Catalogue lists ("Add Product"/"Assign Products") with the
   * direction flipped (one product, many vendor/category ids). */
  async bulkLinkProducts(params: {
    productIds: string[];
    addVendorIds?: string[];
    removeVendorIds?: string[];
    addCatalogueIds?: string[];
    removeCatalogueIds?: string[];
  }): Promise<ApiResponse<{ modified: number }>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/products/bulk-link', {
        method: 'POST',
        body: JSON.stringify(params),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error bulk-linking products:', error);
      throw error;
    }
  }

  /**
   * Get product by ID
   */
  async getProductById(productId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/products/${productId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting product by ID:', error);
      throw error;
    }
  }

  /**
   * Create product
   */
  async createProduct(
    catalogueId: string,
    productData: any
  ): Promise<ApiResponse<any>> {
    try {
      // Use longer timeout for product creation (120 seconds) due to image processing
      const originalTimeout = this.timeout;
      this.timeout = 120000; // 120 seconds
      
      try {
        return await this.authenticatedRequest(
          `/api/omni-channel/catalogues/${catalogueId}/products`,
          {
            method: 'POST',
            body: JSON.stringify(productData),
          }
        );
      } finally {
        // Restore original timeout
        this.timeout = originalTimeout;
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating product:', error);
      throw error;
    }
  }

  /**
   * Update product
   */
  async updateProduct(
    productId: string,
    productData: any
  ): Promise<ApiResponse<any>> {
    try {
      // Use longer timeout for product updates (120 seconds) due to image processing
      const originalTimeout = this.timeout;
      this.timeout = 120000; // 120 seconds
      
      try {
        return await this.authenticatedRequest(`/api/omni-channel/products/${productId}`, {
          method: 'PUT',
          body: JSON.stringify(productData),
        });
      } finally {
        // Restore original timeout
        this.timeout = originalTimeout;
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating product:', error);
      throw error;
    }
  }

  /**
   * Delete product
   */
  async deleteProduct(productId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/products/${productId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting product:', error);
      throw error;
    }
  }

  /**
   * Bulk import products
   */
  async bulkImportProducts(
    catalogueId: string,
    products: any[],
    currency?: string
  ): Promise<ApiResponse<{ success: any[]; failed: Array<{ index: number; error: string }> }>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/products/bulk-import', {
        method: 'POST',
        body: JSON.stringify({
          catalogueId,
          products,
          currency,
        }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error bulk importing products:', error);
      throw error;
    }
  }

  /**
   * Resolve effective price for a product at a given location
   */
  async resolveProductPrice(
    productId: string,
    params: { city?: string; latitude?: number; longitude?: number }
  ): Promise<ApiResponse<{ matched: boolean; zoneName: string | null; price: number; salePrice?: number; currency: string; addons?: { id: string; price: number; currency?: string }[] }>> {
    try {
      return await this.authenticatedRequest(
        `/api/omni-channel/products/${productId}/resolve-price`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        }
      );
    } catch (error: any) {
      console.error('❌ [ApiService] Error resolving product price:', error);
      throw error;
    }
  }

  /**
   * Update product inventory
   */
  async updateProductInventory(
    productId: string,
    inventoryQuantity: number
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(
        `/api/omni-channel/products/${productId}/inventory`,
        {
          method: 'PUT',
          body: JSON.stringify({ inventoryQuantity }),
        }
      );
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating product inventory:', error);
      throw error;
    }
  }

  // ==================== Catalogue Management ====================

  /**
   * Get catalogues by business
   */
  async getCatalogues(query?: {
    search?: string;
    isActive?: boolean;
    dataListId?: string;
    limit?: number;
    skip?: number;
    sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'productCount';
    sortOrder?: 'asc' | 'desc';
    /** JSON-encoded CustomFieldFilterEntry[] — see SortFilterModal.tsx. */
    customFieldFilters?: string;
    /** 'only' = the AI's temporary catalogues (hidden by default). */
    temporary?: 'only' | boolean;
    /** Advanced filters: comma-separated currency / locale, product count range, date added. */
    currency?: string;
    locale?: string;
    minProducts?: number;
    maxProducts?: number;
    createdFrom?: string;
    createdTo?: string;
  }): Promise<ApiResponse<{ catalogues: any[]; pagination: { page: number; limit: number; total: number } }>> {
    try {
      const queryParams = new URLSearchParams();
      for (const key of ['currency', 'locale', 'minProducts', 'maxProducts', 'createdFrom', 'createdTo'] as const) {
        const value = query?.[key];
        if (value !== undefined && value !== null && value !== '') queryParams.append(key, String(value));
      }
      if (query?.search) queryParams.append('search', query.search);
      if (query?.isActive !== undefined) queryParams.append('isActive', query.isActive.toString());
      if (query?.dataListId) queryParams.append('dataListId', query.dataListId);
      if (query?.limit) queryParams.append('limit', query.limit.toString());
      if (query?.skip) queryParams.append('skip', query.skip.toString());
      if (query?.sortBy) queryParams.append('sortBy', query.sortBy);
      if (query?.sortOrder) queryParams.append('sortOrder', query.sortOrder);
      if (query?.customFieldFilters) queryParams.append('customFieldFilters', query.customFieldFilters);
      if (query?.temporary) queryParams.append('temporary', String(query.temporary));

      const queryString = queryParams.toString();
      const url = `/api/omni-channel/catalogues${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogues:', error);
      throw error;
    }
  }

  async getCatalogueCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/catalogues/custom-fields', { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue custom field keys:', error);
      throw error;
    }
  }

  async getCatalogueCustomFieldValues(key: string): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/custom-fields/${encodeURIComponent(key)}/values`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue custom field values:', error);
      throw error;
    }
  }

  /**
   * Get catalogue by ID
   */
  /**
   * Remove a product from a catalogue
   */
  /**
   * Export products to CSV/Excel
   */
  async exportProducts(
    catalogueId: string,
    format: 'csv' | 'excel' = 'csv',
    filters?: {
      search?: string;
      brand?: string;
      availability?: string;
      condition?: string;
      minPrice?: number;
      maxPrice?: number;
      hasVariants?: boolean;
      lowStock?: boolean;
      sku?: string;
      categoryIds?: string[];
    }
  ): Promise<Blob> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('format', format);
      
      if (filters) {
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.brand) queryParams.append('brand', filters.brand);
        if (filters.availability) queryParams.append('availability', filters.availability);
        if (filters.condition) queryParams.append('condition', filters.condition);
        if (filters.minPrice !== undefined) queryParams.append('minPrice', filters.minPrice.toString());
        if (filters.maxPrice !== undefined) queryParams.append('maxPrice', filters.maxPrice.toString());
        if (filters.hasVariants !== undefined) queryParams.append('hasVariants', filters.hasVariants.toString());
        if (filters.lowStock) queryParams.append('lowStock', 'true');
        if (filters.sku) queryParams.append('sku', filters.sku);
        if (filters.categoryIds && filters.categoryIds.length > 0) {
          queryParams.append('categoryIds', filters.categoryIds.join(','));
        }
      }

      const url = `/api/omni-channel/catalogues/${catalogueId}/products/export?${queryParams.toString()}`;
      
      // Get token and business ID
      const token = this.getAuthToken();
      const businessId = this.getBusinessIdFromToken();
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      
      if (businessId) {
        headers['x-business-id'] = businessId;
      }
      
      const response = await fetch(`${this.baseURL}${url}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Export failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error: any) {
      console.error('❌ [ApiService] Error exporting products:', error);
      throw error;
    }
  }

  async removeProductFromCatalogue(
    catalogueId: string,
    productId: string,
    newCatalogueId?: string
  ): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(
        `/api/omni-channel/catalogues/${catalogueId}/products/${productId}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ newCatalogueId }),
        }
      );
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing product from catalogue:', error);
      throw error;
    }
  }

  /** Adds an EXISTING product to a catalogue — additive (many-to-many), never
   * removes the product from any catalogue it already belongs to. */
  async addProductToCatalogue(catalogueId: string, productId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(
        `/api/omni-channel/catalogues/${catalogueId}/products/${productId}/add`,
        { method: 'POST' }
      );
    } catch (error: any) {
      console.error('❌ [ApiService] Error adding product to catalogue:', error);
      throw error;
    }
  }

  async getCatalogueById(catalogueId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue by ID:', error);
      throw error;
    }
  }

  // Channel Binding Methods
  async bindCatalogueToChannel(catalogueId: string, channel: string, data?: any): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels`, {
        method: 'POST',
        body: JSON.stringify({ channel, ...data }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error binding catalogue to channel:', error);
      throw error;
    }
  }

  async getCatalogueChannelBindings(catalogueId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue channel bindings:', error);
      throw error;
    }
  }

  async getCatalogueChannelBinding(catalogueId: string, channel: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/${channel}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue channel binding:', error);
      throw error;
    }
  }

  async updateCatalogueChannelBinding(catalogueId: string, channel: string, data: any): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/${channel}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating catalogue channel binding:', error);
      throw error;
    }
  }

  async unbindCatalogueFromChannel(catalogueId: string, channel: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/${channel}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error unbinding catalogue from channel:', error);
      throw error;
    }
  }

  async getAvailableChannels(): Promise<ApiResponse<Array<{ channel: string; isConnected: boolean; name: string }>>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/channels/available`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting available channels:', error);
      throw error;
    }
  }

  /**
   * Sync catalogue to WhatsApp/Meta
   */
  async syncCatalogueToWhatsApp(catalogueId: string, bindingId?: string): Promise<ApiResponse<{ bindingId: string; syncResult: any }>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/whatsapp/sync`, {
        method: 'POST',
        body: JSON.stringify({ bindingId }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error syncing catalogue to WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Get WhatsApp sync status
   */
  async getWhatsAppSyncStatus(catalogueId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/whatsapp/sync-status`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting WhatsApp sync status:', error);
      throw error;
    }
  }

  /**
   * Get WhatsApp sync history
   */
  async getWhatsAppSyncHistory(catalogueId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/channels/whatsapp/sync-history`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting WhatsApp sync history:', error);
      throw error;
    }
  }

  /**
   * Create catalogue
   */
  async createCatalogue(catalogueData: {
    name: string;
    description?: string;
    currency?: string;
    locale?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/catalogues', {
        method: 'POST',
        body: JSON.stringify(catalogueData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating catalogue:', error);
      throw error;
    }
  }

  /**
   * Update catalogue
   */
  async updateCatalogue(
    catalogueId: string,
    catalogueData: {
      name?: string;
      description?: string;
      currency?: string;
      locale?: string;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}`, {
        method: 'PUT',
        body: JSON.stringify(catalogueData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating catalogue:', error);
      throw error;
    }
  }

  /**
   * Delete catalogue
   */
  async deleteCatalogue(catalogueId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting catalogue:', error);
      throw error;
    }
  }

  // ─── Vendor Methods ───────────────────────────────────────────────────────

  async getVendors(query?: VendorListQuery): Promise<ApiResponse<{ vendors: any[]; pagination: { page: number; limit: number; total: number }; currency?: string }>> {
    try {
      const queryParams = new URLSearchParams();
      for (const [key, value] of Object.entries(query || {})) {
        if (value === undefined || value === null || value === '') continue;
        queryParams.append(key, Array.isArray(value) ? value.join(',') : String(value));
      }
      const queryString = queryParams.toString();
      return await this.authenticatedRequest(`/api/vendors${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting vendors:', error);
      throw error;
    }
  }

  /** A vendor's orders — only the vendor's lines, with their units and subtotal. */
  async getVendorOrders(vendorId: string, query: VendorOrdersQuery & { limit?: number; skip?: number } = {}): Promise<ApiResponse<{ orders: VendorOrder[]; total: number; limit: number; skip: number }>> {
    return this.authenticatedRequest(`/api/vendors/${vendorId}/orders?${vendorOrdersParams(query)}`, { method: 'GET' });
  }

  /** Totals vs the previous period, top products, orders by status, revenue over time. */
  async getVendorAnalytics(vendorId: string, query: VendorOrdersQuery = {}): Promise<ApiResponse<VendorAnalytics>> {
    return this.authenticatedRequest(`/api/vendors/${vendorId}/analytics?${vendorOrdersParams(query)}`, { method: 'GET' });
  }

  /** Packing slips (PDF) for a vendor: chosen orders, or every order matching the filters. */
  async generateVendorPackingSlips(vendorId: string, body: VendorOrdersQuery & PackingSlipOptions & { orderIds?: string[] }): Promise<ApiResponse<PackingSlipResult>> {
    return this.authenticatedRequest(`/api/vendors/${vendorId}/packing-slips`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** Packing slips (PDF) for one order: one per vendor in it, or only `vendorId`'s. */
  async generateOrderPackingSlips(orderId: string, body: PackingSlipOptions & { vendorId?: string } = {}): Promise<ApiResponse<PackingSlipResult>> {
    return this.authenticatedRequest(`/api/vendors/orders/${orderId}/packing-slips`, { method: 'POST', body: JSON.stringify(body) });
  }

  async getVendorCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest('/api/vendors/custom-fields', { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting vendor custom field keys:', error);
      throw error;
    }
  }

  async getVendorCustomFieldValues(key: string): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/custom-fields/${encodeURIComponent(key)}/values`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting vendor custom field values:', error);
      throw error;
    }
  }

  async getVendorById(vendorId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting vendor by ID:', error);
      throw error;
    }
  }

  async createVendor(vendorData: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    location?: { latitude: number; longitude: number; address: string; country?: string; city?: string };
    notes?: string;
    crNumber?: string;
    vatNumber?: string;
    minimumOrderAmount?: number | null;
    minimumOrderCurrency?: string | null;
    /** Contact this vendor represents (created via "Add as vendor"). */
    contactId?: string;
    /** Created during this visit — logged on the visit. */
    visitId?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/vendors', {
        method: 'POST',
        body: JSON.stringify(vendorData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating vendor:', error);
      throw error;
    }
  }

  async updateVendor(vendorId: string, vendorData: {
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
    location?: { latitude: number; longitude: number; address: string; country?: string; city?: string };
    notes?: string;
    crNumber?: string;
    vatNumber?: string;
    minimumOrderAmount?: number | null;
    minimumOrderCurrency?: string | null;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}`, {
        method: 'PUT',
        body: JSON.stringify(vendorData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating vendor:', error);
      throw error;
    }
  }

  async deleteVendor(vendorId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting vendor:', error);
      throw error;
    }
  }

  async getVendorProducts(vendorId: string, opts?: { limit?: number; skip?: number }): Promise<ApiResponse<{ products: any[]; total: number }>> {
    try {
      const queryParams = new URLSearchParams();
      if (opts?.limit) queryParams.append('limit', opts.limit.toString());
      if (opts?.skip) queryParams.append('skip', opts.skip.toString());
      const queryString = queryParams.toString();
      return await this.authenticatedRequest(`/api/vendors/${vendorId}/products${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting vendor products:', error);
      throw error;
    }
  }

  /** Links an EXISTING product to a vendor — additive (many-to-many). */
  async addProductToVendor(vendorId: string, productId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}/products/${productId}/add`, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error adding product to vendor:', error);
      throw error;
    }
  }

  async removeProductFromVendor(vendorId: string, productId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}/products/${productId}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing product from vendor:', error);
      throw error;
    }
  }

  /** Mirrors uploadMedia's pattern — bypasses authenticatedRequest and uses a
   * raw fetch call, since authenticatedRequest always merges in
   * DEFAULT_HEADERS' 'Content-Type': 'application/json' AFTER options.headers,
   * which would corrupt a FormData body's multipart boundary. Also converts
   * blob: URLs to a real File object first, same as uploadMedia — needed on
   * web, where a document/image picker's uri is a blob: URL rather than a
   * real file path. */
  async uploadVendorAttachment(vendorId: string, fileUri: string, fileName: string, mimeType: string): Promise<ApiResponse<any>> {
    try {
      return await this.uploadVendorBulkFile(`/api/vendors/${vendorId}/attachments`, fileUri, fileName, mimeType);
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading vendor attachment:', error);
      throw error;
    }
  }

  async removeVendorAttachment(vendorId: string, url: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/vendors/${vendorId}/attachments`, {
        method: 'DELETE',
        body: JSON.stringify({ url }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing vendor attachment:', error);
      throw error;
    }
  }

  /** Shared raw-fetch multipart uploader for the two vendor bulk-upload
   * endpoints — same reasoning as uploadVendorAttachment: authenticatedRequest
   * always re-merges DEFAULT_HEADERS' application/json Content-Type after
   * options.headers, which would corrupt a FormData body's multipart
   * boundary, so this bypasses it. */
  private async uploadVendorBulkFile(endpoint: string, fileUri: string, fileName: string, mimeType: string): Promise<ApiResponse<any>> {
    let fileToUpload: File | { uri: string; type: string; name: string };
    if (fileUri.startsWith('blob:')) {
      const blobResponse = await fetch(fileUri);
      const blob = await blobResponse.blob();
      fileToUpload = new File([blob], fileName, { type: mimeType });
    } else {
      fileToUpload = { uri: fileUri, type: mimeType, name: fileName };
    }

    const formData = new FormData();
    formData.append('file', fileToUpload as any);

    this.updateBaseURL();
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.authToken}` },
      body: formData,
    });
    return response.json();
  }

  /** Bulk create/update vendors from a CSV/XLSX file — columns matched
   * (case-insensitively) against name/phone/email/address/crNumber/
   * vatNumber/notes; existing vendors matched by CR number are updated,
   * everything else is created new. Synchronous — see
   * VendorService.bulkUpsertVendors' doc comment. */
  async bulkUploadVendors(fileUri: string, fileName: string, mimeType: string): Promise<ApiResponse<{ created: number; updated: number; failed: number; errors: Array<{ row: number; error: string }> }>> {
    try {
      return await this.uploadVendorBulkFile('/api/vendors/bulk-upload', fileUri, fileName, mimeType);
    } catch (error: any) {
      console.error('❌ [ApiService] Error bulk uploading vendors:', error);
      throw error;
    }
  }

  /** Bulk link (or create) products for vendors from a CSV/XLSX file — each
   * row identifies a vendor (Vendor CR Number or Vendor Name) and a product
   * (Product SKU or Product Name, optionally Product Price to create it if
   * missing). */
  async bulkUploadVendorProducts(fileUri: string, fileName: string, mimeType: string): Promise<ApiResponse<{ linked: number; created: number; failed: number; errors: Array<{ row: number; error: string }> }>> {
    try {
      return await this.uploadVendorBulkFile('/api/vendors/bulk-upload-products', fileUri, fileName, mimeType);
    } catch (error: any) {
      console.error('❌ [ApiService] Error bulk uploading vendor products:', error);
      throw error;
    }
  }

  // ─── Quotation Methods ────────────────────────────────────────────────────

  async getQuotations(query?: {
    search?: string;
    dataListId?: string;
    contactId?: string;
    status?: string;
    /** Any of these statuses */
    statuses?: string[];
    createdFrom?: Date;
    createdTo?: Date;
    totalMin?: number;
    totalMax?: number;
    validity?: 'valid' | 'expired' | 'none';
    validFrom?: Date;
    validTo?: Date;
    createdBy?: string;
    currency?: string;
    /** Advanced filters: comma-separated product / vendor ids, converted to an order (yes|no). */
    productIds?: string;
    vendorIds?: string;
    converted?: 'yes' | 'no';
    sortBy?: 'createdAt' | 'totalPrice' | 'validUntil' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ quotations: any[]; pagination: { page: number; limit: number; total: number }; statusCounts?: Record<string, number> }>> {
    try {
      const p = new URLSearchParams();
      for (const key of ['productIds', 'vendorIds', 'converted'] as const) if (query?.[key]) p.append(key, String(query[key]));
      if (query?.statuses?.length) p.append('statuses', query.statuses.join(','));
      if (query?.createdFrom) p.append('createdFrom', query.createdFrom.toISOString());
      if (query?.createdTo) p.append('createdTo', query.createdTo.toISOString());
      if (query?.totalMin !== undefined) p.append('totalMin', String(query.totalMin));
      if (query?.totalMax !== undefined) p.append('totalMax', String(query.totalMax));
      if (query?.validity) p.append('validity', query.validity);
      if (query?.validFrom) p.append('validFrom', query.validFrom.toISOString());
      if (query?.validTo) p.append('validTo', query.validTo.toISOString());
      if (query?.createdBy) p.append('createdBy', query.createdBy);
      if (query?.currency) p.append('currency', query.currency);
      if (query?.sortBy) p.append('sortBy', query.sortBy);
      if (query?.sortOrder) p.append('sortOrder', query.sortOrder);
      if (query?.search) p.append('search', query.search);
      if (query?.dataListId) p.append('dataListId', query.dataListId);
      if (query?.contactId) p.append('contactId', query.contactId);
      if (query?.status) p.append('status', query.status);
      if (query?.limit) p.append('limit', query.limit.toString());
      if (query?.offset) p.append('offset', query.offset.toString());
      const qs = p.toString();
      return await this.authenticatedRequest(`/api/quotations${qs ? `?${qs}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quotations:', error);
      throw error;
    }
  }

  async getQuotationById(quotationId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/${quotationId}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quotation:', error);
      throw error;
    }
  }

  async createQuotation(data: {
    contactId?: string;
    customer: { name: string; phone: string; email?: string; address?: string; location?: { latitude: number; longitude: number; address: string } };
    items: Array<{ productId?: string; quantity: number; product?: any; unitPrice?: number; totalPrice?: number }>;
    subtotal?: number;
    tax?: number;
    extraCharges?: Array<{ name: string; amount: number }>;
    shipping?: number;
    discount?: number;
    manualDiscount?: number;
    couponCode?: string;
    referralCode?: string;
    shippingMethod?: string;
    shippingMethodId?: string;
    shippingMethodSnapshot?: { name: string; type: string; price: number; providerName?: string };
    totalPrice?: number;
    currency?: string;
    quotationStatus?: string;
    validUntil?: string;
    templateId?: string;
    notes?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/quotations', { method: 'POST', body: JSON.stringify(data) });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating quotation:', error);
      throw error;
    }
  }

  async updateQuotation(quotationId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/${quotationId}`, { method: 'PUT', body: JSON.stringify(data) });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating quotation:', error);
      throw error;
    }
  }

  async deleteQuotation(quotationId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/${quotationId}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting quotation:', error);
      throw error;
    }
  }

  async getQuotationPdf(quotationId: string, regenerate?: boolean): Promise<ApiResponse<{ pdfUrl: string; templateUsed?: { id: string | null; name: string; isDefault: boolean } }>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/${quotationId}/pdf${regenerate ? '?regenerate=true' : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error generating quotation PDF:', error);
      throw error;
    }
  }

  async convertQuotationToOrder(quotationId: string): Promise<ApiResponse<{ orderId: string; orderNumber: string }>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/${quotationId}/convert`, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error converting quotation:', error);
      throw error;
    }
  }

  // ─── Business documents (every type) ──────────────────────────────────────

  /** The built-in document layout — a starting point for a new template (edited as code). */
  async getDocumentDefaultHtml(): Promise<ApiResponse<{ html: string }>> {
    return this.authenticatedRequest('/api/documents/default-html', { method: 'GET' });
  }

  /** One order document as a PDF: proforma_invoice | order_confirmation | invoice | receipt | delivery_note. */
  async generateOrderDocument(orderId: string, body: { type: string; templateId?: string; language?: 'auto' | 'en' | 'ar' | 'bilingual'; userLocale?: string; showPrices?: boolean; hideCustomerPhone?: boolean }): Promise<ApiResponse<GeneratedDocument>> {
    return this.authenticatedRequest(`/api/documents/orders/${orderId}`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** A quotation as a PDF with a chosen document template and language. */
  async generateQuotationDocument(quotationId: string, body: { templateId?: string; language?: 'auto' | 'en' | 'ar' | 'bilingual'; userLocale?: string; hideCustomerPhone?: boolean }): Promise<ApiResponse<GeneratedDocument>> {
    return this.authenticatedRequest(`/api/documents/quotations/${quotationId}`, { method: 'POST', body: JSON.stringify(body) });
  }

  /** One search across pages, quick actions and records the user may see, grouped by kind. */
  async globalSearch(q: string, opts: { group?: string; limit?: number; locale?: string; signal?: AbortSignal } = {}): Promise<ApiResponse<import('../components/search/searchTypes').SearchResponse>> {
    const params = new URLSearchParams({ q, ...(opts.group ? { group: opts.group } : {}), ...(opts.limit ? { limit: String(opts.limit) } : {}), ...(opts.locale ? { locale: opts.locale } : {}) });
    return this.authenticatedRequest(`/api/search?${params.toString()}`, { method: 'GET', signal: opts.signal }, 10000);
  }

  /** Send a created document now with a WhatsApp template (values mapped from the document). */
  async sendDocument(body: { orderId?: string; quotationId?: string; vendorId?: string; type: string; phone: string; language?: string; messageTemplate: { templateId: string; mappings: Array<{ position: number; field?: string; text?: string }> }; document: { url: string; fileName?: string; documentNumber?: string; orders?: number } }): Promise<ApiResponse<{ queued: true; taskId: string; phone: string; template: string }>> {
    return this.authenticatedRequest('/api/documents/send', { method: 'POST', body: JSON.stringify(body) });
  }

  /** Purchase order to a vendor for the chosen orders (or every open order of the vendor). */
  async generatePurchaseOrder(vendorId: string, body: { orderIds?: string[]; statuses?: string[]; from?: string; to?: string; deliverBy?: string; notes?: string; templateId?: string; language?: 'auto' | 'en' | 'ar' | 'bilingual'; userLocale?: string; showPrices?: boolean }): Promise<ApiResponse<GeneratedDocument & { orders: number }>> {
    return this.authenticatedRequest(`/api/documents/vendors/${vendorId}/purchase-order`, { method: 'POST', body: JSON.stringify(body) });
  }

  // ─── Quotation Template Methods ───────────────────────────────────────────

  async getQuotationTemplates(type?: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/templates${type ? `?type=${type}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quotation templates:', error);
      throw error;
    }
  }

  async getQuotationTemplateById(templateId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/templates/${templateId}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quotation template:', error);
      throw error;
    }
  }

  async createQuotationTemplate(data: {
    name: string;
    /** Any document type — see utils/documentTypes.ts. */
    type: string;
    htmlContent: string;
    logoUrl?: string;
    colors?: { primary?: string; secondary?: string; text?: string; background?: string };
    isDefault?: boolean;
    language?: 'auto' | 'en' | 'ar' | 'bilingual';
    /** The WhatsApp template sent for this type's event, with its field mapping. */
    messageTemplate?: { templateId: string; mappings: Array<{ position: number; field?: string; text?: string }> } | null;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/quotations/templates', { method: 'POST', body: JSON.stringify(data) });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating quotation template:', error);
      throw error;
    }
  }

  async updateQuotationTemplate(templateId: string, data: Record<string, any>): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/templates/${templateId}`, { method: 'PUT', body: JSON.stringify(data) });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating quotation template:', error);
      throw error;
    }
  }

  async deleteQuotationTemplate(templateId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/quotations/templates/${templateId}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting quotation template:', error);
      throw error;
    }
  }

  async previewQuotationTemplate(data: {
    htmlContent: string;
    type?: string;
    logoUrl?: string;
    colors?: { primary?: string; secondary?: string; text?: string; background?: string };
    language?: 'auto' | 'en' | 'ar' | 'bilingual';
  }): Promise<ApiResponse<{ html: string }>> {
    try {
      return await this.authenticatedRequest('/api/quotations/templates/preview', { method: 'POST', body: JSON.stringify(data) });
    } catch (error: any) {
      console.error('❌ [ApiService] Error previewing quotation template:', error);
      throw error;
    }
  }

  /**
   * Get catalogue statistics
   */
  async getCatalogueStatistics(catalogueId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/statistics`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting catalogue statistics:', error);
      throw error;
    }
  }

  /**
   * Send a catalog to a conversation
   */
  async sendCatalog(catalogueId: string, conversationId: string): Promise<ApiResponse<{ messageId: string; providerId: string }>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/catalogues/${catalogueId}/send`, {
        method: 'POST',
        body: JSON.stringify({ conversationId }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error sending catalog:', error);
      throw error;
    }
  }

  /**
   * Upload product image
   */
  async uploadProductImage(
    productId: string,
    fileUri: string,
    fileName: string,
    fileType: string
  ): Promise<ApiResponse<{ url: string; alt?: string; isPrimary: boolean }>> {
    console.log('🔍 [ApiService] Uploading product image:', { productId, fileUri, fileName, fileType });
    
    try {
      // Handle blob URLs by converting them to File objects for web
      let fileToUpload: File | { uri: string; type: string; name: string };
      
      if (typeof window !== 'undefined' && fileUri.startsWith('blob:')) {
        // Convert blob URL to File object for web
        console.log('🔍 [ApiService] Converting blob URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted blob to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error: any) {
          console.error('❌ [ApiService] Failed to convert blob to File:', error);
          throw new Error(`Failed to convert blob URL: ${error.message}`);
        }
      } else {
        // For mobile, use the URI directly
        fileToUpload = {
          uri: fileUri,
          type: fileType,
          name: fileName,
        };
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('image', fileToUpload as any);

      const config = getCurrentApiConfig();
      const baseURL = config?.baseURL?.trim() || '';
      const url = `${baseURL}/api/omni-channel/products/${productId}/images`;

      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const headers: any = {
        'Authorization': `Bearer ${token}`,
      };

      // Don't set Content-Type for FormData - let fetch set it automatically
      console.log('📤 [ApiService] Uploading product image to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const responseData = await response.json();

      if (!response.ok) {
        console.error('❌ [ApiService] Product image upload failed:', responseData);
        return {
          data: null,
          statusCode: response.status,
          errors: responseData.errors || [{ message: responseData.message || 'Failed to upload product image' }],
        };
      }

      console.log('✅ [ApiService] Product image uploaded successfully:', responseData);
      return responseData;
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading product image:', error);
      throw error;
    }
  }

  /**
   * Download product CSV template
   */
  async downloadProductTemplate(): Promise<ApiResponse<Blob>> {
    try {
      const config = getCurrentApiConfig();
      const baseURL = config?.baseURL?.trim() || '';
      const url = `${baseURL}/api/omni-channel/products/download-template`;

      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null,
          statusCode: response.status,
          errors: errorData.errors || [{ message: 'Failed to download template' }],
        };
      }

      const blob = await response.blob();
      return {
        data: blob,
        statusCode: 1000,
        errors: [],
      };
    } catch (error: any) {
      console.error('❌ [ApiService] Error downloading product template:', error);
      return {
        data: null,
        statusCode: 500,
        errors: [{ message: error.message || 'Failed to download template' }],
      };
    }
  }

  private async attemptSilentReauthentication(): Promise<boolean> {
    if (this.silentLoginPromise) {
      return this.silentLoginPromise;
    }

    this.silentLoginPromise = (async () => {
      try {
        const credentials = await loadCredentials();

        if (!credentials?.email || !credentials?.password) {
          console.log('ℹ️ [ApiService] No remembered credentials available for silent login.');
          return false;
        }

        console.log('🔄 [ApiService] Attempting remember-me reauthentication...');
        const loginResponse = await this.login({
          email: credentials.email,
          password: credentials.password,
        });

        if (loginResponse.statusCode !== 1000 || !loginResponse.data?.token || !loginResponse.user) {
          console.warn('⚠️ [ApiService] Remember-me login failed with status:', loginResponse.statusCode);
          if (loginResponse.statusCode === 1011) {
            await clearCredentials();
          }
          return false;
        }

        const { token, refreshToken } = loginResponse.data;
        const user = this.mapUserFromApi(loginResponse.user);

        const setAuthState = useAppStore.getState().setAuthState;

        this.setAuthToken(token);
        this.setRefreshToken(refreshToken);

        await setAuthState({
          tokens: { token, refreshToken },
          user,
          source: 'token-refresh',
        });

        syncApiServiceWithStore({ token, refreshToken });

        console.log('✅ [ApiService] Remember-me reauthentication completed successfully.');
        return true;
      } catch (error) {
        console.error('❌ [ApiService] Remember-me reauthentication failed:', error);
        return false;
      } finally {
        this.silentLoginPromise = null;
      }
    })();

    return this.silentLoginPromise;
  }

  // Quick Reply Sets Methods
  // =======================

  /**
   * Get quick reply sets by business
   */
  async getQuickReplySets(query?: {
    search?: string;
    isActive?: boolean;
    isDefault?: boolean;
    channel?: string;
    limit?: number;
    skip?: number;
    sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'usageCount' | 'lastUsedAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{ quickReplySets: any[]; pagination: { page: number; limit: number; total: number } }>> {
    try {
      const queryParams = new URLSearchParams();
      if (query?.search) queryParams.append('search', query.search);
      if (query?.isActive !== undefined) queryParams.append('isActive', query.isActive.toString());
      if (query?.isDefault !== undefined) queryParams.append('isDefault', query.isDefault.toString());
      if (query?.channel) queryParams.append('channel', query.channel);
      if (query?.limit) queryParams.append('limit', query.limit.toString());
      if (query?.skip) queryParams.append('skip', query.skip.toString());
      if (query?.sortBy) queryParams.append('sortBy', query.sortBy);
      if (query?.sortOrder) queryParams.append('sortOrder', query.sortOrder);

      const queryString = queryParams.toString();
      const url = `/api/omni-channel/quick-replies${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quick reply sets:', error);
      throw error;
    }
  }

  /**
   * Get quick reply set by ID
   */
  async getQuickReplySetById(setId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quick reply set by ID:', error);
      throw error;
    }
  }

  /**
   * Channel-linked settings (assign user, reply message, quick reply set) for all channels.
   * GET ?businessId= &channel= &channelIdentifier= — single doc when all provided; otherwise list.
   */
  async getChannelSettings(params: {
    businessId: string;
    channel?: 'whatsapp' | 'facebook_messenger' | 'telegram' | 'instagram' | 'email';
    channelIdentifier?: string;
  }): Promise<ApiResponse<any | any[]>> {
    const q = new URLSearchParams();
    q.set('businessId', params.businessId);
    if (params.channel) q.set('channel', params.channel);
    if (params.channelIdentifier) q.set('channelIdentifier', params.channelIdentifier);
    return this.authenticatedRequest(`/api/channel-settings?${q.toString()}`, { method: 'GET' });
  }

  /**
   * Create or update channel-linked settings (upsert by businessId + channel + channelIdentifier).
   */
  async upsertChannelSettings(payload: {
    businessId: string;
    channel: 'whatsapp' | 'facebook_messenger' | 'telegram' | 'instagram' | 'email' | 'x_twitter';
    channelIdentifier: string;
    assignToDepartment?: string;
    assignToUsers?: string[];
    replyWithMessage?: string;
    quickReplySetId?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/channel-settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Create quick reply set
   */
  async createQuickReplySet(setData: {
    name: string;
    description?: string;
    isActive?: boolean;
    isDefault?: boolean;
    items?: any[];
    channelSettings?: { [channel: string]: { enabled: boolean; displayOrder?: number; customText?: string } };
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/quick-replies', {
        method: 'POST',
        body: JSON.stringify(setData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating quick reply set:', error);
      throw error;
    }
  }

  /**
   * Add item to quick reply set
   */
  async addItemToQuickReplySet(setId: string, itemData: {
    displayText?: string;
    type?: string;
    config?: any;
    displayOrder?: number;
    isActive?: boolean;
    icon?: string;
    itemId?: string; // Optional: if provided, links existing item instead of creating new one
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items`, {
        method: 'POST',
        body: JSON.stringify(itemData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error adding item to quick reply set:', error);
      throw error;
    }
  }

  /**
   * Update quick reply set
   */
  async updateQuickReplySet(setId: string, setData: {
    name?: string;
    description?: string;
    isActive?: boolean;
    isDefault?: boolean;
    items?: any[];
    listButtonText?: string;
    channelSettings?: { [channel: string]: { enabled: boolean; displayOrder?: number; customText?: string } };
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}`, {
        method: 'PUT',
        body: JSON.stringify(setData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating quick reply set:', error);
      throw error;
    }
  }

  /**
   * Delete quick reply set
   */
  async deleteQuickReplySet(setId: string): Promise<ApiResponse<void>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting quick reply set:', error);
      throw error;
    }
  }

  /**
   * Create a standalone quick reply item
   */
  async createQuickReplyItem(itemData: {
    displayText: string;
    type: string;
    config: any;
    displayOrder?: number;
    isActive?: boolean;
    icon?: string;
    setIds?: string[];
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/quick-reply-items', {
        method: 'POST',
        body: JSON.stringify(itemData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating quick reply item:', error);
      throw error;
    }
  }

  /**
   * Get quick reply item by ID (standalone)
   */
  async getQuickReplyItemById(itemId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-reply-items/${itemId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quick reply item by ID:', error);
      throw error;
    }
  }

  /**
   * List quick reply items (standalone)
   */
  async getQuickReplyItems(options?: {
    search?: string;
    type?: string;
    isActive?: boolean;
    limit?: number;
    skip?: number;
    sortBy?: 'displayText' | 'createdAt' | 'updatedAt' | 'usageCount';
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.search) queryParams.append('search', options.search);
      if (options?.type) queryParams.append('type', options.type);
      if (options?.isActive !== undefined) queryParams.append('isActive', String(options.isActive));
      if (options?.limit) queryParams.append('limit', String(options.limit));
      if (options?.skip) queryParams.append('skip', String(options.skip));
      if (options?.sortBy) queryParams.append('sortBy', options.sortBy);
      if (options?.sortOrder) queryParams.append('sortOrder', options.sortOrder);
      
      const queryString = queryParams.toString();
      const url = `/api/omni-channel/quick-reply-items${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting quick reply items:', error);
      throw error;
    }
  }

  /**
   * Add item to quick reply set
   */
  async addQuickReplyItem(setId: string, itemData: {
    displayText: string;
    type: string;
    config: any;
    displayOrder?: number;
    isActive?: boolean;
    icon?: string;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items`, {
        method: 'POST',
        body: JSON.stringify(itemData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error adding quick reply item:', error);
      throw error;
    }
  }

  /**
   * Remove item from quick reply set
   */
  async removeItemFromQuickReplySet(setId: string, itemId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items/${itemId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing item from quick reply set:', error);
      throw error;
    }
  }

  /**
   * Update standalone quick reply item
   */
  async updateQuickReplyItemStandalone(
    itemId: string,
    itemData: {
      displayText?: string;
      type?: string;
      config?: any;
      displayOrder?: number;
      isActive?: boolean;
      icon?: string;
    }
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-reply-items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(itemData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating quick reply item:', error);
      throw error;
    }
  }

  /**
   * Delete standalone quick reply item
   */
  async deleteQuickReplyItem(itemId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-reply-items/${itemId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting quick reply item:', error);
      throw error;
    }
  }

  /**
   * Create a dispatch rule
   */
  async createDispatchRule(ruleData: {
    name: string;
    scope: 'reply_item' | 'dataset' | 'channel_settings';
    attachedTo: string;
    channel?: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
    channelIdentifier?: string;
    conditionGroups?: any[];
    defaultOutcome?: any;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/dispatch-rules', {
        method: 'POST',
        body: JSON.stringify(ruleData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating dispatch rule:', error);
      throw error;
    }
  }

  /**
   * Get dispatch rule by ID
   */
  async getDispatchRuleById(ruleId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/dispatch-rules/${ruleId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting dispatch rule by ID:', error);
      throw error;
    }
  }

  /**
   * List dispatch rules, optionally filtered by scope/attachedTo/isActive
   */
  async getDispatchRules(options?: {
    scope?: 'reply_item' | 'dataset' | 'channel_settings';
    attachedTo?: string;
    channelIdentifier?: string;
    isActive?: boolean;
    limit?: number;
    skip?: number;
  }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.scope) queryParams.append('scope', options.scope);
      if (options?.attachedTo) queryParams.append('attachedTo', options.attachedTo);
      if (options?.channelIdentifier) queryParams.append('channelIdentifier', options.channelIdentifier);
      if (options?.isActive !== undefined) queryParams.append('isActive', String(options.isActive));
      if (options?.limit) queryParams.append('limit', String(options.limit));
      if (options?.skip) queryParams.append('skip', String(options.skip));

      const queryString = queryParams.toString();
      const url = `/api/omni-channel/dispatch-rules${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting dispatch rules:', error);
      throw error;
    }
  }

  /**
   * Update a dispatch rule
   */
  async updateDispatchRule(
    ruleId: string,
    ruleData: {
      name?: string;
      scope?: 'reply_item' | 'dataset' | 'channel_settings';
      attachedTo?: string;
      channel?: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
      channelIdentifier?: string;
      conditionGroups?: any[];
      defaultOutcome?: any;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/dispatch-rules/${ruleId}`, {
        method: 'PUT',
        body: JSON.stringify(ruleData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating dispatch rule:', error);
      throw error;
    }
  }

  /**
   * Delete a dispatch rule
   */
  async deleteDispatchRule(ruleId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/dispatch-rules/${ruleId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting dispatch rule:', error);
      throw error;
    }
  }

  /**
   * Create a flow (reusable decision-tree)
   */
  async createFlow(flowData: {
    name: string;
    description?: string;
    nodes: any[];
    entryNodeId: string;
    isActive?: boolean;
    sessionTimeoutMinutes?: number | null;
    stopOnAgentReply?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/flows', {
        method: 'POST',
        body: JSON.stringify(flowData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating flow:', error);
      throw error;
    }
  }

  async getFlowById(flowId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting flow by ID:', error);
      throw error;
    }
  }

  async getFlows(options?: { isActive?: boolean; limit?: number; skip?: number }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.isActive !== undefined) queryParams.append('isActive', String(options.isActive));
      if (options?.limit) queryParams.append('limit', String(options.limit));
      if (options?.skip) queryParams.append('skip', String(options.skip));
      const queryString = queryParams.toString();
      return await this.authenticatedRequest(`/api/omni-channel/flows${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting flows:', error);
      throw error;
    }
  }

  async updateFlow(
    flowId: string,
    flowData: { name?: string; description?: string; nodes?: any[]; entryNodeId?: string; isActive?: boolean; sessionTimeoutMinutes?: number | null; stopOnAgentReply?: boolean }
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}`, {
        method: 'PUT',
        body: JSON.stringify(flowData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating flow:', error);
      throw error;
    }
  }

  async deleteFlow(flowId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting flow:', error);
      throw error;
    }
  }

  /** Publishes the flow's current draft — freezes it into a new immutable version and makes
   * it live. In-flight sessions on a prior version are unaffected. */
  async publishFlow(flowId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}/publish`, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error publishing flow:', error);
      throw error;
    }
  }

  /** Lists a flow's published version history (draft excluded), most recent first. */
  async getFlowVersions(flowId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}/versions`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting flow versions:', error);
      throw error;
    }
  }

  async renameFlowVersion(flowId: string, versionId: string, versionName: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}/versions/${versionId}`, {
        method: 'PUT',
        body: JSON.stringify({ versionName }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error renaming flow version:', error);
      throw error;
    }
  }

  /** Rollback — re-publishes an older version as a new, current published version. */
  async publishFlowVersion(flowId: string, versionId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}/versions/${versionId}/publish`, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error publishing flow version:', error);
      throw error;
    }
  }

  /** Loads an old version into the flow's current editable draft — "View" in the version
   * browser: opens it as normal editable content the business can inspect or edit further. */
  async restoreFlowVersionToDraft(flowId: string, versionId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flows/${flowId}/versions/${versionId}/restore`, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error restoring flow version:', error);
      throw error;
    }
  }

  /**
   * Attach a flow to a reply item / dataset / channel-settings, optionally scoped to one number
   */
  async createFlowAttachment(data: {
    flowId: string;
    scope: 'reply_item' | 'dataset' | 'channel_settings';
    attachedTo: string;
    channel?: 'whatsapp' | 'facebook_messenger' | 'x_twitter';
    channelIdentifier?: string;
    isActive?: boolean;
    /** Which inbound event this attachment fires on — omit for the default
     * (message auto-reply); pass 'comment_received' for a Facebook
     * comment-auto-reply attachment. */
    triggerType?: 'message_received' | 'comment_received';
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/omni-channel/flow-attachments', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating flow attachment:', error);
      throw error;
    }
  }

  async getFlowAttachments(options?: {
    flowId?: string;
    scope?: 'reply_item' | 'dataset' | 'channel_settings';
    attachedTo?: string;
    channelIdentifier?: string;
    isActive?: boolean;
    triggerType?: 'message_received' | 'comment_received';
  }): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (options?.flowId) queryParams.append('flowId', options.flowId);
      if (options?.scope) queryParams.append('scope', options.scope);
      if (options?.attachedTo) queryParams.append('attachedTo', options.attachedTo);
      if (options?.channelIdentifier) queryParams.append('channelIdentifier', options.channelIdentifier);
      if (options?.isActive !== undefined) queryParams.append('isActive', String(options.isActive));
      if (options?.triggerType) queryParams.append('triggerType', options.triggerType);
      const queryString = queryParams.toString();
      return await this.authenticatedRequest(`/api/omni-channel/flow-attachments${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting flow attachments:', error);
      throw error;
    }
  }

  async updateFlowAttachment(id: string, data: { channelIdentifier?: string; isActive?: boolean }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flow-attachments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating flow attachment:', error);
      throw error;
    }
  }

  async deleteFlowAttachment(id: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/flow-attachments/${id}`, { method: 'DELETE' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting flow attachment:', error);
      throw error;
    }
  }

  /**
   * Update item in quick reply set (legacy - for backward compatibility)
   */
  async updateQuickReplyItem(
    setId: string,
    itemId: string,
    itemData: {
      displayText?: string;
      type?: string;
      config?: any;
      displayOrder?: number;
      isActive?: boolean;
      icon?: string;
    }
  ): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(itemData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating quick reply item:', error);
      throw error;
    }
  }

  /**
   * Remove item from quick reply set
   */
  async removeQuickReplyItem(setId: string, itemId: string): Promise<ApiResponse<void>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items/${itemId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing quick reply item:', error);
      throw error;
    }
  }

  /**
   * Reorder items in quick reply set
   */
  async reorderQuickReplyItems(setId: string, itemIds: string[]): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/items/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ itemIds }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error reordering quick reply items:', error);
      throw error;
    }
  }

  /**
   * Send a quick reply set to a conversation
   */
  async sendQuickReplySet(setId: string, conversationId: string): Promise<ApiResponse<{ messageId: string; providerId: string }>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/send`, {
        method: 'POST',
        body: JSON.stringify({ conversationId }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error sending quick reply set:', error);
      throw error;
    }
  }

  async sendDatasetToPhone(setId: string, to: string, message: string): Promise<ApiResponse<{ textMessageId: string; interactiveMessageId: string; providerId: string }>> {
    try {
      return await this.authenticatedRequest(`/api/omni-channel/quick-replies/${setId}/send-to-phone`, {
        method: 'POST',
        body: JSON.stringify({ to, message }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error sending dataset to phone:', error);
      throw error;
    }
  }

  // ============================================================================
  // CALENDAR API METHODS
  // ============================================================================

  /**
   * Create a calendar
   */
  async createCalendar(data: {
    businessId?: string;
    userId?: string;
    name: string;
    description?: string;
    color?: string;
    timezone: string;
    isDefault?: boolean;
    isActive?: boolean;
    visibility?: 'private' | 'public' | 'shared';
    availability?: {
      mode: 'all_time' | 'specific_period' | 'customized_days';
      startDate?: Date;
      endDate?: Date;
      daysOfWeek?: number[];
      isAllDay?: boolean;
      timeRange?: {
        startTime: string;
        endTime: string;
      };
    };
    sharedWith?: CalendarSharedWithEntry[];
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/calendar', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating calendar:', error);
      throw error;
    }
  }

  /**
   * Get all calendars
   */
  async getCalendars(filters?: {
    businessId?: string;
    userId?: string;
    isActive?: boolean;
    isDefault?: boolean;
    visibility?: 'private' | 'public' | 'shared';
  }): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.businessId) queryParams.append('businessId', filters.businessId);
      if (filters?.userId) queryParams.append('userId', filters.userId);
      if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
      if (filters?.isDefault !== undefined) queryParams.append('isDefault', filters.isDefault.toString());
      if (filters?.visibility) queryParams.append('visibility', filters.visibility);

      const queryString = queryParams.toString();
      const endpoint = `/api/calendar${queryString ? `?${queryString}` : ''}`;
      
      // Use longer timeout for calendar requests (60 seconds)
      // Create a custom request with extended timeout
      const originalTimeout = this.timeout;
      this.timeout = 60000; // 60 seconds
      
      try {
        const result = await this.authenticatedRequest(endpoint, {
          method: 'GET',
        });
        return result;
      } catch (error: any) {
        console.error('❌ [ApiService] Error getting calendars:', error);
        throw error;
      } finally {
        // Restore original timeout
        this.timeout = originalTimeout;
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting calendars:', error);
      throw error;
    }
  }

  /**
   * Get calendar by ID
   */
  async getCalendarById(id: string, businessId?: string): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);
      
      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/${id}${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting calendar:', error);
      throw error;
    }
  }

  /**
   * Get business calendars
   */
  async getBusinessCalendars(businessId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/business/${businessId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting business calendars:', error);
      throw error;
    }
  }

  /**
   * Get user calendars
   */
  async getUserCalendars(userId: string, businessId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/user/${userId}/business/${businessId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting user calendars:', error);
      throw error;
    }
  }

  /**
   * Update calendar
   */
  async updateCalendar(id: string, data: {
    name?: string;
    description?: string;
    color?: string;
    timezone?: string;
    isDefault?: boolean;
    isActive?: boolean;
    visibility?: 'private' | 'public' | 'shared';
    availability?: {
      mode: 'all_time' | 'specific_period' | 'customized_days';
      startDate?: Date;
      endDate?: Date;
      daysOfWeek?: number[];
      isAllDay?: boolean;
      timeRange?: {
        startTime: string;
        endTime: string;
      };
    };
    sharedWith?: CalendarSharedWithEntry[];
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating calendar:', error);
      throw error;
    }
  }

  /**
   * Replace who a business calendar is shared with (users/departments/other
   * businesses) — the single write path for calendar "assignment".
   */
  async updateCalendarSharedWith(id: string, sharedWith: CalendarSharedWithEntry[]): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/${id}/shared-with`, {
        method: 'PUT',
        body: JSON.stringify({ sharedWith }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating calendar sharing:', error);
      throw error;
    }
  }

  /**
   * Delete calendar
   */
  async deleteCalendar(id: string, businessId?: string): Promise<ApiResponse<void>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);
      
      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/${id}${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting calendar:', error);
      throw error;
    }
  }

  /**
   * Set default calendar
   */
  async setDefaultCalendar(id: string, businessId: string, userId?: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/${id}/set-default`, {
        method: 'PUT',
        body: JSON.stringify({ businessId, userId }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error setting default calendar:', error);
      throw error;
    }
  }

  // ============================================================================
  // CALENDAR EVENT API METHODS
  // ============================================================================

  /**
   * Create a calendar event
   */
  async createEvent(data: {
    calendarId: string;
    businessId: string;
    title: string;
    description?: string;
    startDate: string | Date;
    endDate: string | Date;
    timezone: string;
    isAllDay?: boolean;
    meetingType?: 'online' | 'offline' | 'hybrid';
    location?: {
      name?: string;
      address?: string;
      city?: string;
      country?: string;
      coordinates?: { lat: number; lng: number };
    };
    onlineMeeting?: {
      provider: 'zoom' | 'google_meet' | 'microsoft_teams' | 'custom';
      meetingId?: string;
      joinUrl: string;
      password?: string;
      dialInNumbers?: { phone: string; country: string }[];
      instructions?: string;
    };
    assignedTo?: {
      userIds?: string[];
      departmentIds?: string[];
    };
    eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    /** Minutes-before-startDate offsets to remind assignedTo (e.g. [15, 60, 1440]). */
    reminders?: number[];
  }): Promise<ApiResponse<any>> {
    try {
      // Convert dates to ISO strings if they're Date objects
      const eventData = {
        ...data,
        startDate: data.startDate instanceof Date ? data.startDate.toISOString() : data.startDate,
        endDate: data.endDate instanceof Date ? data.endDate.toISOString() : data.endDate,
      };

      return await this.authenticatedRequest('/api/calendar/events', {
        method: 'POST',
        body: JSON.stringify(eventData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating event:', error);
      throw error;
    }
  }

  /**
   * Get events with filters
   */
  async getEvents(filters?: {
    businessId?: string;
    calendarId?: string;
    startDate?: string | Date;
    endDate?: string | Date;
    eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    meetingType?: 'online' | 'offline' | 'hybrid';
    assignedToUserId?: string;
    assignedToDepartmentId?: string;
    search?: string;
  }): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.businessId) queryParams.append('businessId', filters.businessId);
      if (filters?.calendarId) queryParams.append('calendarId', filters.calendarId);
      if (filters?.startDate) {
        const startDate = filters.startDate instanceof Date ? filters.startDate.toISOString() : filters.startDate;
        queryParams.append('startDate', startDate);
      }
      if (filters?.endDate) {
        const endDate = filters.endDate instanceof Date ? filters.endDate.toISOString() : filters.endDate;
        queryParams.append('endDate', endDate);
      }
      if (filters?.eventStatus) queryParams.append('eventStatus', filters.eventStatus);
      if (filters?.meetingType) queryParams.append('meetingType', filters.meetingType);
      if (filters?.assignedToUserId) queryParams.append('assignedToUserId', filters.assignedToUserId);
      if (filters?.assignedToDepartmentId) queryParams.append('assignedToDepartmentId', filters.assignedToDepartmentId);
      if (filters?.search) queryParams.append('search', filters.search);

      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/events${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting events:', error);
      throw error;
    }
  }

  /**
   * Get event by ID
   */
  async getEventById(id: string, businessId?: string): Promise<ApiResponse<any>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);
      
      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/events/${id}${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting event:', error);
      throw error;
    }
  }

  /**
   * Get events for a calendar
   */
  async getEventsByCalendar(calendarId: string, businessId?: string, filters?: {
    startDate?: string | Date;
    endDate?: string | Date;
    eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    meetingType?: 'online' | 'offline' | 'hybrid';
    search?: string;
  }): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);
      if (filters?.startDate) {
        const startDate = filters.startDate instanceof Date ? filters.startDate.toISOString() : filters.startDate;
        queryParams.append('startDate', startDate);
      }
      if (filters?.endDate) {
        const endDate = filters.endDate instanceof Date ? filters.endDate.toISOString() : filters.endDate;
        queryParams.append('endDate', endDate);
      }
      if (filters?.eventStatus) queryParams.append('eventStatus', filters.eventStatus);
      if (filters?.meetingType) queryParams.append('meetingType', filters.meetingType);
      if (filters?.search) queryParams.append('search', filters.search);

      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/${calendarId}/events${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting calendar events:', error);
      throw error;
    }
  }

  /**
   * Get events by phone number (public endpoint)
   */
  async getEventsByPhone(phoneNumber: string, businessId: string, calendarId?: string): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('phoneNumber', phoneNumber);
      queryParams.append('businessId', businessId);
      if (calendarId) queryParams.append('calendarId', calendarId);

      const queryString = queryParams.toString();
      const endpoint = `/api/omni-channel/calendar/events/by-phone?${queryString}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting events by phone:', error);
      throw error;
    }
  }

  /**
   * Get events in date range
   */
  async getEventsByDateRange(
    calendarId: string,
    businessId: string,
    startDate: string | Date,
    endDate: string | Date
  ): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('calendarId', calendarId);
      queryParams.append('businessId', businessId);
      queryParams.append('startDate', startDate instanceof Date ? startDate.toISOString() : startDate);
      queryParams.append('endDate', endDate instanceof Date ? endDate.toISOString() : endDate);

      return await this.authenticatedRequest(`/api/calendar/events/date-range?${queryParams.toString()}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting events by date range:', error);
      throw error;
    }
  }

  /**
   * Get user's events
   */
  async getUserEvents(userId: string, businessId?: string, startDate?: string | Date, endDate?: string | Date): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);
      if (startDate) {
        const start = startDate instanceof Date ? startDate.toISOString() : startDate;
        queryParams.append('startDate', start);
      }
      if (endDate) {
        const end = endDate instanceof Date ? endDate.toISOString() : endDate;
        queryParams.append('endDate', end);
      }

      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/events/user/${userId}${queryString ? `?${queryString}` : ''}`;
      
      return await this.authenticatedRequest(endpoint, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting user events:', error);
      throw error;
    }
  }

  /**
   * Update event
   */
  async updateEvent(id: string, data: {
    title?: string;
    description?: string;
    startDate?: string | Date;
    endDate?: string | Date;
    timezone?: string;
    isAllDay?: boolean;
    meetingType?: 'online' | 'offline' | 'hybrid';
    location?: {
      name?: string;
      address?: string;
      city?: string;
      country?: string;
      coordinates?: { lat: number; lng: number };
    };
    onlineMeeting?: {
      provider?: 'zoom' | 'google_meet' | 'microsoft_teams' | 'custom';
      meetingId?: string;
      joinUrl?: string;
      password?: string;
      dialInNumbers?: { phone: string; country: string }[];
      instructions?: string;
    };
    assignedTo?: {
      userIds?: string[];
      departmentIds?: string[];
    };
    eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    attachments?: Array<{
      _id?: string;
      name: string;
      type: string;
      size: number;
      url: string;
      storagePath?: string;
      uploadedBy?: string;
      uploadedAt?: Date;
    }>;
    notes?: Array<{
      _id?: string;
      content: string;
      createdBy?: string;
      createdAt?: Date;
      updatedAt?: Date;
      updatedBy?: string;
      isPrivate?: boolean;
    }>;
    /** Minutes-before-startDate offsets to remind assignedTo (e.g. [15, 60, 1440]). */
    reminders?: number[];
  }): Promise<ApiResponse<any>> {
    try {
      // Convert dates to ISO strings if they're Date objects
      const eventData: any = { ...data };
      if (eventData.startDate instanceof Date) {
        eventData.startDate = eventData.startDate.toISOString();
      }
      if (eventData.endDate instanceof Date) {
        eventData.endDate = eventData.endDate.toISOString();
      }

      return await this.authenticatedRequest(`/api/calendar/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify(eventData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating event:', error);
      throw error;
    }
  }

  /**
   * Delete event
   */
  /**
   * Upload attachment for calendar event
   * POST /api/calendar/events/:eventId/attachments
   */
  async uploadCalendarAttachment(eventId: string, fileUri: string, fileName: string, fileType: string): Promise<ApiResponse<{ name: string; type: string; size: number; url: string; storagePath?: string; uploadedBy?: string; uploadedAt?: Date }>> {
    console.log('🔍 [ApiService] Uploading calendar attachment:', { eventId, fileUri, fileName, fileType });
    
    try {
      // Handle blob URLs by converting them to File objects
      let fileToUpload: File | { uri: string; type: string; name: string };
      
      if (fileUri.startsWith('blob:')) {
        // Convert blob URL to File object for web
        console.log('🔍 [ApiService] Converting blob URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted blob to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert blob URL to File:', error);
          throw new Error('Failed to process media file. Please try again.');
        }
      } else {
        // For React Native, use the original format
        fileToUpload = {
          uri: fileUri,
          type: fileType,
          name: fileName,
        };
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', fileToUpload as any);

      const response = await fetch(`${this.baseURL}/api/calendar/events/${eventId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          // Don't set Content-Type header - let fetch set it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [ApiService] Calendar attachment upload failed:', { status: response.status, error: errorText });
        throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ [ApiService] Calendar attachment upload response:', result);
      
      if (result.statusCode === 1000 && result.data) {
        return {
          data: result.data,
          statusCode: 1000,
          messages: ['Calendar attachment uploaded successfully'],
        };
      } else {
        throw new Error(result.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading calendar attachment:', error);
      throw error;
    }
  }

  /**
   * Upload contact attachment (avatar or document)
   * Path: {businessId}/contacts/{contactId}/{subfolder}/{filename}
   */
  async uploadContactAttachment(
    contactId: string,
    fileUri: string,
    fileName: string,
    fileType: string,
    subfolder: 'avatar' | 'documents' = 'documents'
  ): Promise<ApiResponse<{ name: string; type: string; size: number; url: string; storagePath?: string; uploadedBy?: string; uploadedAt?: Date }>> {
    console.log('🔍 [ApiService] Uploading contact attachment:', { contactId, fileUri, fileName, fileType, subfolder });
    
    try {
      // Handle blob URLs by converting them to File objects
      let fileToUpload: File | { uri: string; type: string; name: string };
      
      if (fileUri.startsWith('blob:') || Platform.OS === 'web') {
        // Convert blob URL to File object for web
        console.log('🔍 [ApiService] Converting blob URL to File object');
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          const file = new File([blob], fileName, { type: fileType });
          fileToUpload = file;
          console.log('✅ [ApiService] Successfully converted blob to File:', { name: file.name, size: file.size, type: file.type });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert blob URL to File:', error);
          throw new Error('Failed to process media file. Please try again.');
        }
      } else {
        // For React Native, use the original format
        fileToUpload = {
          uri: fileUri,
          type: fileType,
          name: fileName,
        };
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', fileToUpload as any);
      formData.append('subfolder', subfolder);

      const response = await fetch(`${this.baseURL}/api/omni-contact/${contactId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
          // Don't set Content-Type header - let fetch set it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [ApiService] Contact attachment upload failed:', { status: response.status, error: errorText });
        throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ [ApiService] Contact attachment upload response:', result);
      
      if (result.statusCode === 1000 && result.data) {
        return {
          data: result.data,
          statusCode: 1000,
          messages: ['Contact attachment uploaded successfully'],
        };
      } else {
        throw new Error(result.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading contact attachment:', error);
      throw error;
    }
  }

  /**
   * Remove a previously uploaded document attachment from a parent contact
   */
  async removeContactAttachment(contactId: string, url: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest<any>(`/api/omni-contact/${contactId}/attachments`, {
      method: 'DELETE',
      body: JSON.stringify({ url }),
    });
  }

  // ==================== Assignment rules (Assignment Setup) ====================
  // Mirrors dislix-connect/api/src/modules/assignment-rules (mounted at /api/assignment-rules).

  async getAssignmentRules(): Promise<ApiResponse<{ items: AssignmentRule[] }>> {
    return this.authenticatedRequest('/api/assignment-rules', { method: 'GET' });
  }

  async getAssignmentRule(ruleId: string): Promise<ApiResponse<AssignmentRule>> {
    return this.authenticatedRequest(`/api/assignment-rules/${ruleId}`, { method: 'GET' });
  }

  async createAssignmentRule(body: AssignmentRuleInput): Promise<ApiResponse<AssignmentRule>> {
    return this.authenticatedRequest('/api/assignment-rules', { method: 'POST', body: JSON.stringify(body) });
  }

  async updateAssignmentRule(ruleId: string, body: AssignmentRuleInput): Promise<ApiResponse<AssignmentRule>> {
    return this.authenticatedRequest(`/api/assignment-rules/${ruleId}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async setAssignmentRuleActive(ruleId: string, isActive: boolean): Promise<ApiResponse<AssignmentRule>> {
    return this.authenticatedRequest(`/api/assignment-rules/${ruleId}/active`, { method: 'POST', body: JSON.stringify({ isActive }) });
  }

  async deleteAssignmentRule(ruleId: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return this.authenticatedRequest(`/api/assignment-rules/${ruleId}`, { method: 'DELETE' });
  }

  async getAssignmentRuleHistory(ruleId: string): Promise<ApiResponse<{ items: AssignmentRuleBatch[] }>> {
    return this.authenticatedRequest(`/api/assignment-rules/${ruleId}/history`, { method: 'GET' });
  }

  async getAssignmentRuleBatchIssues(batchId: string): Promise<ApiResponse<{ items: AssignmentRuleJobIssue[] }>> {
    return this.authenticatedRequest(`/api/assignment-rules/batches/${batchId}/issues`, { method: 'GET' });
  }

  // ==================== Lead Finder API ====================
  // Mirrors dislix-connect/api/src/modules/lead-finder (mounted at /api/lead-finder).

  async getLeadFinderUsage(): Promise<ApiResponse<{ allowance: LeadAllowance; usage: LeadUsage }>> {
    return this.authenticatedRequest('/api/lead-finder/usage', { method: 'GET' });
  }

  async getLeadFinderCategories(): Promise<ApiResponse<{ categories: string[] }>> {
    return this.authenticatedRequest('/api/lead-finder/categories', { method: 'GET' });
  }

  async getLeadRuns(query?: { limit?: number; skip?: number; status?: LeadRunStatus }): Promise<ApiResponse<{ items: LeadRun[]; total: number; allowance: LeadAllowance; usage: LeadUsage }>> {
    const params = new URLSearchParams();
    if (query?.limit !== undefined) params.append('limit', String(query.limit));
    if (query?.skip !== undefined) params.append('skip', String(query.skip));
    if (query?.status) params.append('status', query.status);
    const qs = params.toString();
    return this.authenticatedRequest(`/api/lead-finder/runs${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async getLeadRun(runId: string): Promise<ApiResponse<LeadRun & { counts: Record<string, number> }>> {
    return this.authenticatedRequest(`/api/lead-finder/runs/${runId}`, { method: 'GET' });
  }

  async createLeadRun(body: LeadRunInput & { aiPlanned?: boolean; clonedFrom?: string }): Promise<ApiResponse<LeadRun>> {
    return this.authenticatedRequest('/api/lead-finder/runs', { method: 'POST', body: JSON.stringify(body) });
  }

  /** The same search again, as a new run; onlyNew skips businesses earlier runs of it found. */
  async rerunLeadRun(runId: string, onlyNew = true): Promise<ApiResponse<LeadRun>> {
    return this.authenticatedRequest(`/api/lead-finder/runs/${runId}/rerun`, { method: 'POST', body: JSON.stringify({ onlyNew }) });
  }

  /** The AI turns a brief into run settings (to review before running). */
  async planLeadRun(brief: string, region?: LeadRegion | null): Promise<ApiResponse<LeadRunInput>> {
    return this.authenticatedRequest('/api/lead-finder/plan', { method: 'POST', body: JSON.stringify({ brief, region }) }, 90000);
  }

  async suggestLeadSearches(body: { agentId?: string; language?: 'ar' | 'en' }): Promise<ApiResponse<LeadSearchSuggestions>> {
    return this.authenticatedRequest('/api/lead-finder/suggestions', { method: 'POST', body: JSON.stringify(body) }, 180000);
  }

  /** The AI plans and the run starts right away. */
  async planAndRunLeadRun(body: LeadRunInput & { brief: string }): Promise<ApiResponse<LeadRun>> {
    return this.authenticatedRequest('/api/lead-finder/runs/ai', { method: 'POST', body: JSON.stringify(body) }, 90000);
  }

  async cancelLeadRun(runId: string): Promise<ApiResponse<{ cancelled: boolean }>> {
    return this.authenticatedRequest(`/api/lead-finder/runs/${runId}/cancel`, { method: 'POST' });
  }

  async getLeadResults(runId: string, query?: LeadResultsQuery): Promise<ApiResponse<{ items: LeadResult[]; total: number; otherTotal?: number }>> {
    const params = new URLSearchParams();
    Object.entries(query || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, String(v)); });
    const qs = params.toString();
    return this.authenticatedRequest(`/api/lead-finder/runs/${runId}/results${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async actOnLeadResults(runId: string, body: {
    action: 'contact' | 'vendor' | 'dismiss' | 'restore' | 'data_list' | 'keep'; ids: string[]; tags?: string[]; assignTo?: string;
    listId?: string; newList?: { name: string; kind: 'static' | 'contacts' }; mapping?: Record<string, string>;
  }): Promise<ApiResponse<{ done: number; failed: Array<{ id: string; reason: string }>; list?: { id: string; name: string; kind: 'static' | 'contacts' } }>> {
    return this.authenticatedRequest(`/api/lead-finder/runs/${runId}/results/actions`, { method: 'POST', body: JSON.stringify(body) }, 120000);
  }

  /** A data list's columns and the Lead Finder field suggested for each (for "Add to list"). */
  async getLeadListMapping(listId: string, runId?: string): Promise<ApiResponse<{ id: string; name: string; kind: 'static' | 'contacts'; columns: Array<{ id: string; name: string; type: string }>; mapping: Record<string, string>; fields: string[]; fieldLabels?: Record<string, string> }>> {
    return this.authenticatedRequest(`/api/lead-finder/lists/${listId}/mapping${runId ? `?runId=${encodeURIComponent(runId)}` : ''}`, { method: 'GET' });
  }

  async exportLeadResults(runId: string): Promise<{ blob: Blob; filename: string }> {
    const token = this.getAuthToken();
    const response = await fetch(`${this.getBaseURL()}/api/lead-finder/runs/${runId}/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) throw new Error(`Failed to export results (${response.status})`);
    const blob = await response.blob();
    return { blob, filename: this.extractFilenameFromContentDisposition(response, `lead-finder-${runId}.csv`) };
  }

  // ==================== Data Lists API ====================
  // Mirrors sendifier_api/src/modules/data-lists (mounted at /api/data-lists).
  // Result envelope matches the standard { statusCode, data, errors, ... } shape.

  async getDataLists(query?: {
    search?: string;
    assignedUserId?: string;
    systemModule?: DataListSystemModule;
    limit?: number;
    offset?: number;
    /** Advanced filters: custom / system lists, comma-separated modules / sources, has records, date added, sort. */
    columnsSource?: 'custom' | 'system';
    systemModules?: string;
    dataSources?: string;
    hasRecords?: 'yes' | 'no';
    createdFrom?: string;
    createdTo?: string;
    sortBy?: 'name' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<DataList[]>> {
    const params = new URLSearchParams();
    for (const key of ['columnsSource', 'systemModules', 'dataSources', 'hasRecords', 'createdFrom', 'createdTo', 'sortBy', 'sortOrder'] as const) {
      const value = query?.[key];
      if (value) params.append(key, String(value));
    }
    if (query?.search) params.append('search', query.search);
    if (query?.assignedUserId) params.append('assignedUserId', query.assignedUserId);
    if (query?.systemModule) params.append('systemModule', query.systemModule);
    if (query?.limit !== undefined) params.append('limit', String(query.limit));
    if (query?.offset !== undefined) params.append('offset', String(query.offset));
    const qs = params.toString();
    return this.authenticatedRequest<DataList[]>(`/api/data-lists${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async getDataList(id: string): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}`, { method: 'GET' });
  }

  async createDataList(body: {
    name: string;
    description?: string;
    columns?: Array<Omit<DataListColumn, 'id' | 'createdAt'>>;
    /** Only meaningful when columnsSource === 'system' — 'add' ops seeding customColumns. */
    customColumns?: Array<{ op: 'add' } & Omit<DataListColumn, 'id' | 'createdAt'>>;
    columnsSource?: DataListColumnsSource;
    dataSource?: DataListDataSource;
    systemModule?: DataListSystemModule;
    webhookConfig?: DataListWebhookConfig;
    assignedUserIds?: string[];
    assignedDepartmentIds?: string[];
  }): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>('/api/data-lists', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getDataListSystemModules(): Promise<ApiResponse<DataListSystemModuleInfo[]>> {
    return this.authenticatedRequest<DataListSystemModuleInfo[]>('/api/data-lists/system-modules', { method: 'GET' });
  }

  async updateDataListWebhookConfig(id: string, webhookConfig: DataListWebhookConfig): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}/webhook-config`, {
      method: 'PATCH',
      body: JSON.stringify({ webhookConfig }),
    });
  }

  /** Fires the webhook once for the mapping-builder preview — never persists. */
  async testDataListWebhookConfig(webhookConfig: DataListWebhookConfig): Promise<ApiResponse<{ rows: Array<{ values: Record<string, any> }>; rawSample?: any[] }>> {
    return this.authenticatedRequest(`/api/data-lists/webhook-config/test`, {
      method: 'POST',
      body: JSON.stringify({ webhookConfig }),
    });
  }

  async downloadDataListTemplate(listId: string): Promise<ApiResponse<Blob>> {
    try {
      const config = getCurrentApiConfig();
      const baseURL = config?.baseURL?.trim() || '';
      const url = `${baseURL}/api/data-lists/${listId}/template`;

      const token = this.getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          data: null,
          statusCode: response.status,
          errors: errorData.errors || [{ message: 'Failed to download template' }],
        };
      }

      const blob = await response.blob();
      return {
        data: blob,
        statusCode: 1000,
        errors: [],
      };
    } catch (error: any) {
      console.error('❌ [ApiService] Error downloading data list template:', error);
      return {
        data: null,
        statusCode: 500,
        errors: [{ message: error.message || 'Failed to download template' }],
      };
    }
  }

  async uploadDataListTemplate(
    listId: string,
    fileUri: string,
    fileName: string,
    fileType: string
  ): Promise<ApiResponse<{ totalRows: number; created: number; invalidRows: Array<{ row: number; errors: string[] }>; createErrors: Array<{ row: number; error: string }> }>> {
    try {
      let fileToUpload: File | { uri: string; type: string; name: string };
      if (fileUri.startsWith('blob:') || fileUri.startsWith('http')) {
        const response = await fetch(fileUri);
        const blob = await response.blob();
        fileToUpload = new File([blob], fileName, { type: fileType });
      } else {
        fileToUpload = { uri: fileUri, type: fileType, name: fileName };
      }

      const formData = new FormData();
      formData.append('file', fileToUpload as any);

      const response = await fetch(`${this.baseURL}/api/data-lists/${listId}/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Import failed with status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      if (result.statusCode === 1000 && result.data) {
        return { data: result.data, statusCode: 1000, messages: ['Template imported successfully'] };
      } else {
        throw new Error(result.errors?.[0]?.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading data list template:', error);
      throw error;
    }
  }

  /** Links existing real-module record(s) — created via the core module's own
   * add/bulk-add screen — into a `columnsSource: 'system'` Data List, so they
   * appear scoped to that list. Server verifies each id belongs to the
   * business before linking; returns how many were actually linked vs. skipped
   * (already-linked or failed verification). */
  async linkDataListRecords(listId: string, recordIds: string[]): Promise<ApiResponse<{ linked: number; skipped: number }>> {
    return this.authenticatedRequest<{ linked: number; skipped: number }>(`/api/data-lists/${listId}/link-records`, {
      method: 'POST',
      body: JSON.stringify({ recordIds }),
    });
  }

  /** Removes real-module record(s) from a `columnsSource: 'system'` Data
   * List — only deletes the membership row, never the underlying record
   * (e.g. the Contact itself is untouched, it just stops appearing scoped
   * to this list). */
  async unlinkDataListRecords(listId: string, recordIds: string[]): Promise<ApiResponse<{ unlinked: number }>> {
    return this.authenticatedRequest<{ unlinked: number }>(`/api/data-lists/${listId}/unlink-records`, {
      method: 'POST',
      body: JSON.stringify({ recordIds }),
    });
  }

  async updateDataList(id: string, body: {
    name?: string;
    description?: string;
    aiMeaning?: boolean;
    assignedUserIds?: string[];
    assignedDepartmentIds?: string[];
  }): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async updateDataListColumns(id: string, body: {
    ops: Array<
      | ({ op: 'add' } & Omit<DataListColumn, 'id' | 'createdAt'>)
      | ({ op: 'update'; id: string } & Partial<Omit<DataListColumn, 'id' | 'createdAt' | 'type'>>)
      | { op: 'remove'; id: string }
    >;
  }): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}/columns`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /** Add/edit/remove CUSTOM fields on a system-backed list — the mirror of
   * updateDataListColumns, targeting customColumns instead of the module's
   * fixed columns (which stay immutable, unlike a fully custom list). */
  async updateDataListCustomColumns(id: string, body: {
    ops: Array<
      | ({ op: 'add' } & Omit<DataListColumn, 'id' | 'createdAt'>)
      | ({ op: 'update'; id: string } & Partial<Omit<DataListColumn, 'id' | 'createdAt' | 'type'>>)
      | { op: 'remove'; id: string }
    >;
  }): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}/custom-columns`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  /** Re-pulls a system-backed list's fixed columns from its module adapter —
   * needed after the adapter itself gains new columns server-side, since a
   * list's columns are computed once at creation and never change on their
   * own (see DataListService.resyncSystemColumns). */
  async resyncDataListSystemColumns(id: string): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}/resync-system-columns`, { method: 'POST' });
  }

  async deleteDataList(id: string): Promise<ApiResponse<void>> {
    return this.authenticatedRequest<void>(`/api/data-lists/${id}`, { method: 'DELETE' });
  }

  async assignDataList(id: string, body: { userIds?: string[]; departmentIds?: string[] }): Promise<ApiResponse<DataList>> {
    return this.authenticatedRequest<DataList>(`/api/data-lists/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async getDataListRecords(listId: string, query?: {
    match?: Array<{ columnId: string; operator: string; value: any }>;
    search?: string;
    limit?: number;
    offset?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Column filters: [{ columnId, op: contains|in|between|true|false|hasValue|noValue, value?, values?, min?, max? }]. */
    filters?: Array<{ columnId: string; op: string; value?: string; values?: string[]; min?: number | string; max?: number | string }>;
  }): Promise<ApiResponse<DataListRecord[]>> {
    const params = new URLSearchParams();
    if (query?.match && query.match.length > 0) params.append('match', JSON.stringify(query.match));
    if (query?.filters && query.filters.length > 0) params.append('filters', JSON.stringify(query.filters));
    if (query?.search) params.append('search', query.search);
    if (query?.limit !== undefined) params.append('limit', String(query.limit));
    if (query?.offset !== undefined) params.append('offset', String(query.offset));
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.sortOrder) params.append('sortOrder', query.sortOrder);
    const qs = params.toString();
    return this.authenticatedRequest<DataListRecord[]>(`/api/data-lists/${listId}/records${qs ? `?${qs}` : ''}`, { method: 'GET' });
  }

  async getDataListRecord(listId: string, recordId: string): Promise<ApiResponse<DataListRecord>> {
    return this.authenticatedRequest<DataListRecord>(`/api/data-lists/${listId}/records/${recordId}`, { method: 'GET' });
  }

  async createDataListRecord(listId: string, body: { values: Record<string, any> }): Promise<ApiResponse<DataListRecord>> {
    return this.authenticatedRequest<DataListRecord>(`/api/data-lists/${listId}/records`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateDataListRecord(listId: string, recordId: string, body: { values: Record<string, any> }): Promise<ApiResponse<DataListRecord>> {
    return this.authenticatedRequest<DataListRecord>(`/api/data-lists/${listId}/records/${recordId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async deleteDataListRecord(listId: string, recordId: string): Promise<ApiResponse<void>> {
    return this.authenticatedRequest<void>(`/api/data-lists/${listId}/records/${recordId}`, { method: 'DELETE' });
  }

  /** Sets custom-field values for an EXISTING system-list record — used by
   * the real module's own edit/create screens' "Extra Fields" section (see
   * item 1's scoped mode). Reading custom values needs no separate method —
   * getDataListRecord already merges them in for a system list. */
  async upsertDataListRecordCustomValues(listId: string, recordId: string, values: Record<string, any>): Promise<ApiResponse<void>> {
    return this.authenticatedRequest<void>(`/api/data-lists/${listId}/records/${recordId}/custom-values`, {
      method: 'PATCH',
      body: JSON.stringify({ values }),
    });
  }

  /** Multipart upload of a media file for a record's media column (voice/image/video),
   * mirroring uploadContactAttachment. Returns { url } to PATCH into values[columnId]. */
  async uploadDataListRecordFile(
    listId: string,
    recordId: string,
    columnId: string,
    fileUri: string,
    fileName: string,
    fileType: string
  ): Promise<ApiResponse<{ url: string }>> {
    try {
      let fileToUpload: File | { uri: string; type: string; name: string };

      // On web the picker yields a blob: URI; convert it to a File. Native yields a file:/
      // content:/ph: URI passed through as-is (no Platform import needed here — the blob check
      // is sufficient to distinguish the web path).
      if (fileUri.startsWith('blob:') || fileUri.startsWith('http')) {
        try {
          const response = await fetch(fileUri);
          const blob = await response.blob();
          fileToUpload = new File([blob], fileName, { type: fileType });
        } catch (error) {
          console.error('❌ [ApiService] Failed to convert blob URL to File:', error);
          throw new Error('Failed to process media file. Please try again.');
        }
      } else {
        fileToUpload = { uri: fileUri, type: fileType, name: fileName };
      }

      const formData = new FormData();
      formData.append('file', fileToUpload as any);
      formData.append('columnId', columnId);

      const response = await fetch(`${this.baseURL}/api/data-lists/${listId}/records/${recordId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed with status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      if (result.statusCode === 1000 && result.data) {
        return { data: result.data, statusCode: 1000, messages: ['File uploaded successfully'] };
      } else {
        throw new Error(result.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('❌ [ApiService] Error uploading data list record file:', error);
      throw error;
    }
  }

  async assignDataListRecord(listId: string, recordId: string, body: { userIds?: string[]; departmentIds?: string[] }): Promise<ApiResponse<DataListRecord>> {
    return this.authenticatedRequest<DataListRecord>(`/api/data-lists/${listId}/records/${recordId}/assign`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteEvent(id: string, businessId?: string): Promise<ApiResponse<void>> {
    try {
      const queryParams = new URLSearchParams();
      if (businessId) queryParams.append('businessId', businessId);

      const queryString = queryParams.toString();
      const endpoint = `/api/calendar/events/${id}${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(endpoint, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting event:', error);
      throw error;
    }
  }

  /** Respond to your own invite on an event ("Accept"/"Decline"/"Tentative"). */
  async respondToEvent(id: string, status: 'accepted' | 'declined' | 'tentative'): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/calendar/events/${id}/rsvp`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error responding to event:', error);
      throw error;
    }
  }

  // ==================== Orders API ====================

  /**
   * Get all orders with optional filters
   */
  async getOrders(filters?: {
    orderStatus?: string; // Can be comma-separated string for multiple values
    paymentStatus?: string; // Can be comma-separated string for multiple values
    source?: string; // Can be comma-separated string for multiple values
    contactId?: string;
    dataListId?: string;
    search?: string;
    orderDateFrom?: string;
    orderDateTo?: string;
    limit?: number;
    offset?: number;
    /** JSON-encoded CustomFieldFilterEntry[] — see SortFilterModal.tsx. */
    customFieldFilters?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    /** Advanced filters: totalMin/totalMax, currency, vendorIds, productIds (comma-separated), hasInvoice / slip ('yes' | 'no'). */
    totalMin?: number;
    totalMax?: number;
    currency?: string;
    vendorIds?: string;
    productIds?: string;
    hasInvoice?: 'yes' | 'no';
    slip?: 'yes' | 'no';
  }): Promise<ApiResponse<any[]>> {
    try {
      const queryParams = new URLSearchParams();
      for (const key of ['totalMin', 'totalMax', 'currency', 'vendorIds', 'productIds', 'hasInvoice', 'slip'] as const) {
        const value = filters?.[key];
        if (value !== undefined && value !== null && value !== '') queryParams.append(key, String(value));
      }
      if (filters?.orderStatus) queryParams.append('orderStatus', filters.orderStatus);
      if (filters?.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters?.source) queryParams.append('source', filters.source);
      if (filters?.contactId) queryParams.append('contactId', filters.contactId);
      if (filters?.dataListId) queryParams.append('dataListId', filters.dataListId);
      if (filters?.search) queryParams.append('search', filters.search);
      if (filters?.orderDateFrom) queryParams.append('orderDateFrom', filters.orderDateFrom);
      if (filters?.orderDateTo) queryParams.append('orderDateTo', filters.orderDateTo);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      if (filters?.customFieldFilters) queryParams.append('customFieldFilters', filters.customFieldFilters);
      if (filters?.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

      const queryString = queryParams.toString();
      const url = `/api/orders${queryString ? `?${queryString}` : ''}`;

      return await this.authenticatedRequest(url, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting orders:', error);
      throw error;
    }
  }

  async getOrderCustomFieldKeys(): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest('/api/orders/custom-fields', { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting order custom field keys:', error);
      throw error;
    }
  }

  async getOrderCustomFieldValues(key: string): Promise<ApiResponse<string[]>> {
    try {
      return await this.authenticatedRequest(`/api/orders/custom-fields/${encodeURIComponent(key)}/values`, { method: 'GET' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting order custom field values:', error);
      throw error;
    }
  }

  /**
   * Get order by ID
   */
  async getOrderById(orderId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/orders/${orderId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting order:', error);
      throw error;
    }
  }

  /**
   * Create a new order
   */
  async createOrder(orderData: {
    contactId?: string;
    customer: {
      name: string;
      phone: string;
      email?: string;
      address?: string;
      location?: {
        latitude?: number;
        longitude?: number;
        address?: string;
      };
    };
    items: Array<{
      productId: string;
      variantId?: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
    subtotal: number;
    tax?: number;
    extraCharges?: { name: string; amount: number }[];
    shipping?: number;
    discount?: number;
    manualDiscount?: number;
    couponCode?: string;
    referralCode?: string;
    selectedChargeNames?: string[];
    totalPrice: number;
    currency?: string;
    orderStatus?: 'pending' | 'placed' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
    paymentStatus?: 'unpaid' | 'partial' | 'paid' | 'refunded';
    source?: 'checkout' | 'catalog' | 'quick_reply' | 'manual' | 'api';
    catalogId?: string;
    quickReplyItemId?: string;
    shippingAddress?: string;
    shippingMethod?: string;
    notes?: string;
    metadata?: Record<string, any>;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating order:', error);
      throw error;
    }
  }

  /**
   * Update an order
   */
  async updateOrder(orderId: string, updateData: {
    orderStatus?: 'pending' | 'placed' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
    paymentStatus?: 'unpaid' | 'partial' | 'paid' | 'refunded';
    shippingAddress?: string;
    shippingLocation?: any;
    shippingMethod?: string;
    shippingMethodId?: string;
    shippingMethodSnapshot?: any;
    trackingNumber?: string;
    notes?: string;
    cancelledReason?: string;
    metadata?: Record<string, any>;
    customer?: any;
    items?: any[];
    contactId?: string;
    subtotal?: number;
    tax?: number;
    extraCharges?: { name: string; amount: number }[];
    shipping?: number;
    discount?: number;
    manualDiscount?: number;
    couponCode?: string;
    selectedChargeNames?: string[];
    totalPrice?: number;
    currency?: string;
    allowNegativeStock?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/orders/${orderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating order:', error);
      throw error;
    }
  }

  /**
   * Delete an order (soft delete)
   */
  async deleteOrder(orderId: string): Promise<ApiResponse<boolean>> {
    try {
      return await this.authenticatedRequest(`/api/orders/${orderId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting order:', error);
      throw error;
    }
  }

  /**
   * Generate invoice for an order
   */
  async generateOrderInvoice(orderId: string, regenerate = false): Promise<ApiResponse<{ invoiceUrl: string; invoiceNumber: string }>> {
    try {
      const url = `/api/orders/${orderId}/generate-invoice${regenerate ? '?regenerate=true' : ''}`;
      return await this.authenticatedRequest(url, { method: 'POST' });
    } catch (error: any) {
      console.error('❌ [ApiService] Error generating invoice:', error);
      throw error;
    }
  }

  // ==================== Department Management ====================

  /**
   * Get all departments for a business
   */
  async getDepartments(businessId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/departments/business/${businessId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting departments:', error);
      throw error;
    }
  }

  /**
   * Create a new department
   */
  async getDepartmentById(departmentId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('Error fetching department:', error);
      throw error;
    }
  }

  async createDepartment(data: {
    name: string;
    description?: string;
    businessId: string;
    managerId?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest('/api/departments', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error creating department:', error);
      throw error;
    }
  }

  /**
   * Update a department
   */
  async updateDepartment(departmentId: string, data: {
    name?: string;
    description?: string;
    managerId?: string;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error updating department:', error);
      throw error;
    }
  }

  /**
   * Delete a department
   */
  async deleteDepartment(departmentId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error deleting department:', error);
      throw error;
    }
  }

  /**
   * Add user to department
   */
  async addUserToDepartment(departmentId: string, userId: string, businessId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}/users/${userId}/assign`, {
        method: 'POST',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error adding user to department:', error);
      throw error;
    }
  }

  /**
   * Bulk-add users to a department in one call (skips already-active memberships)
   */
  async bulkAddUsersToDepartment(departmentId: string, userIds: string[]): Promise<ApiResponse<{ added: number; skipped: number; errors: Array<{ userId: string; error: string }> }>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}/users/bulk`, {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error bulk-adding users to department:', error);
      throw error;
    }
  }

  /**
   * Remove user from department
   */
  async removeUserFromDepartment(departmentId: string, userId: string): Promise<ApiResponse<any>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}/users/${userId}/remove`, {
        method: 'DELETE',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error removing user from department:', error);
      throw error;
    }
  }

  /**
   * Get all users in a department
   */
  async getDepartmentUsers(departmentId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/departments/${departmentId}/users`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting department users:', error);
      throw error;
    }
  }

  /**
   * Get all departments for a user
   */
  async getUserDepartments(userId: string, businessId: string): Promise<ApiResponse<any[]>> {
    try {
      return await this.authenticatedRequest(`/api/departments/user/${userId}?businessId=${businessId}`, {
        method: 'GET',
      });
    } catch (error: any) {
      console.error('❌ [ApiService] Error getting user departments:', error);
      throw error;
    }
  }

  // ============================================================================
  // DEVELOPER ACCESS (Software Access) API METHODS
  // ============================================================================

  /** List all API keys for the business */
  async listApiKeys(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest('/api/software-access/api-keys?status=1');
  }

  /** Create a new API key. Returns raw key once — store it immediately. */
  async createApiKey(label: string): Promise<ApiResponse<{ rawKey: string; key: any }>> {
    return this.authenticatedRequest('/api/software-access/api-keys', {
      method: 'POST',
      body: JSON.stringify({ label }),
    });
  }

  /** Revoke an API key by ID */
  async revokeApiKey(keyId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  /** List all registered channel webhooks */
  async listWebhooks(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest('/api/software-access/webhooks');
  }

  /** Get webhook for a specific channel */
  async getWebhook(channel: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/webhooks/${channel}`);
  }

  /**
   * Register or replace a channel webhook.
   * Returns rawSecret once — store it immediately.
   */
  async upsertWebhook(data: {
    title: string;
    channel: string;
    webhookUrl: string;
    events?: string[];
  }): Promise<ApiResponse<{ rawSecret: string; webhook: any }>> {
    return this.authenticatedRequest('/api/software-access/webhooks', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Update webhook URL or events for a specific registration (does NOT regenerate secret) */
  async updateWebhook(webhookId: string, data: {
    title?: string;
    webhookUrl?: string;
    events?: string[];
    /** Issues a new signing secret, returned once as data.rawSecret. */
    regenerateSecret?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/webhooks/${webhookId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Delete a specific webhook registration */
  async deleteWebhook(webhookId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  // ─── Assignment Webhooks ─────────────────────────────────────────────────
  // Channel-independent — fire when an Assignment is created, for whichever
  // assignmentType(s) the subscriber opted into. Separate resource from
  // channel webhooks above (no `channel` field at all).

  /** List all registered assignment webhooks */
  async listAssignmentWebhooks(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest('/api/software-access/assignment-webhooks');
  }

  /** Get a specific assignment webhook by id */
  async getAssignmentWebhook(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/assignment-webhooks/${id}`);
  }

  /**
   * Register a new assignment webhook.
   * Returns rawSecret once — store it immediately.
   */
  async upsertAssignmentWebhook(data: {
    title: string;
    webhookUrl: string;
    assignmentTypes?: string[];
    allAssignments?: boolean;
    userIds?: string[];
    departmentIds?: string[];
  }): Promise<ApiResponse<{ rawSecret: string; webhook: any }>> {
    return this.authenticatedRequest('/api/software-access/assignment-webhooks', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** Update an assignment webhook (does NOT regenerate secret) */
  async updateAssignmentWebhook(webhookId: string, data: {
    title?: string;
    webhookUrl?: string;
    assignmentTypes?: string[];
    allAssignments?: boolean;
    userIds?: string[];
    departmentIds?: string[];
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/assignment-webhooks/${webhookId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** Delete a specific assignment webhook registration */
  async deleteAssignmentWebhook(webhookId: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/software-access/assignment-webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  // ─── Devices ───────────────────────────────────────────────────────────────

  async getDevices(query?: {
    category?: 'scanner' | 'printer' | 'camera' | 'other';
    scannerType?: 'pos' | 'a4';
    printerType?: 'a4' | 'receipt';
    connectionType?: 'usb' | 'network' | 'bluetooth' | 'wifi' | 'cloud';
    deviceStatus?: 'online' | 'offline' | 'idle' | 'error';
    location?: string;
    search?: string;
    limit?: number;
    skip?: number;
  }): Promise<ApiResponse<{ devices: any[]; pagination: { total: number; limit: number; skip: number } }>> {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v !== undefined && v !== null) params.append(k, String(v));
      });
    }
    const qs = params.toString();
    return this.authenticatedRequest(`/api/devices${qs ? `?${qs}` : ''}`);
  }

  async getDeviceById(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/devices/${id}`);
  }

  async createDevice(data: {
    name: string;
    description?: string;
    category: 'scanner' | 'printer' | 'camera' | 'other';
    scannerType?: 'pos' | 'a4';
    printerType?: 'a4' | 'receipt';
    connectionType: 'usb' | 'network' | 'bluetooth' | 'wifi' | 'cloud';
    ipAddress?: string;
    port?: number;
    macAddress?: string;
    serialNumber?: string;
    location?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/devices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDevice(id: string, data: Partial<{
    name: string;
    description: string;
    category: 'scanner' | 'printer' | 'camera' | 'other';
    scannerType: 'pos' | 'a4';
    printerType: 'a4' | 'receipt';
    connectionType: 'usb' | 'network' | 'bluetooth' | 'wifi' | 'cloud';
    ipAddress: string;
    port: number;
    macAddress: string;
    serialNumber: string;
    deviceStatus: 'online' | 'offline' | 'idle' | 'error';
    location: string;
  }>): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/devices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateDeviceStatus(id: string, deviceStatus: 'online' | 'offline' | 'idle' | 'error'): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/devices/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ deviceStatus }),
    });
  }

  async deleteDevice(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/devices/${id}`, { method: 'DELETE' });
  }

  // ─── Units ─────────────────────────────────────────────────────────────────

  async getUnits(query?: { search?: string; limit?: number; skip?: number }): Promise<ApiResponse<{ units: any[]; pagination: { total: number; limit: number; skip: number } }>> {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v !== undefined) params.append(k, String(v));
      });
    }
    const qs = params.toString();
    return this.authenticatedRequest(`/api/units${qs ? `?${qs}` : ''}`);
  }

  async getUnitById(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/units/${id}`);
  }

  async createUnit(data: { name: string; abbreviation?: string; description?: string }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/units', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUnit(id: string, data: { name?: string; abbreviation?: string; description?: string }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUnit(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/units/${id}`, { method: 'DELETE' });
  }

  // ─── Shipping Methods ───────────────────────────────────────────────────────

  async getShippingMethods(query?: {
    type?: string;
    isActive?: boolean;
    availableFor?: string;
    search?: string;
    limit?: number;
    skip?: number;
  }): Promise<ApiResponse<{ methods: any[]; pagination: { total: number; limit: number; skip: number } }>> {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([k, v]) => {
        if (v !== undefined) params.append(k, String(v));
      });
    }
    const qs = params.toString();
    return this.authenticatedRequest(`/api/shipping${qs ? `?${qs}` : ''}`);
  }

  async getShippingMethodById(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/shipping/${id}`);
  }

  async createShippingMethod(data: {
    name: string;
    type: string;
    description?: string;
    price: number;
    estimatedDays?: { min: number; max: number };
    isActive?: boolean;
    availableFor?: string[];
    providerName?: string;
    accountReference?: string;
    minOrderAmount?: number;
    pickupAddress?: string;
    pickupInstructions?: string;
    deliveryInstructions?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/shipping', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateShippingMethod(id: string, data: {
    name?: string;
    type?: string;
    description?: string;
    price?: number;
    estimatedDays?: { min: number; max: number };
    isActive?: boolean;
    availableFor?: string[];
    providerName?: string;
    accountReference?: string;
    minOrderAmount?: number;
    pickupAddress?: string;
    pickupInstructions?: string;
    deliveryInstructions?: string;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/shipping/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteShippingMethod(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/shipping/${id}`, { method: 'DELETE' });
  }

  async resolveShippingZone(
    methodId: string,
    location: { city?: string; latitude?: number; longitude?: number },
  ): Promise<ApiResponse<{ matched: boolean; zone: any; price: number; fallback: boolean }>> {
    return this.authenticatedRequest(`/api/shipping/${methodId}/resolve-zone`, {
      method: 'POST',
      body: JSON.stringify(location),
    });
  }

  // ─── Inventory ──────────────────────────────────────────────────────────────

  async getInventory(params?: { productId?: string; lowStockOnly?: boolean; limit?: number; offset?: number }): Promise<ApiResponse<any[]>> {
    const qs = new URLSearchParams();
    if (params?.productId)              qs.set('productId',    params.productId);
    if (params?.lowStockOnly)           qs.set('lowStockOnly', 'true');
    if (params?.limit !== undefined)    qs.set('limit',        String(params.limit));
    if (params?.offset !== undefined)   qs.set('offset',       String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.authenticatedRequest(`/api/inventory${query}`);
  }

  async getProductInventory(productId: string): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest(`/api/inventory/product/${productId}`);
  }

  async adjustInventory(productId: string, data: { type: 'in' | 'out' | 'adjustment'; quantity: number; reason?: string; note?: string; variantId?: string }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/inventory/product/${productId}/adjust`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ─── Business Config ────────────────────────────────────────────────────────

  async getBusinessConfig(): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/business-config');
  }

  async patchBusinessConfigTaxes(taxes: {
    id: string;
    enabled: boolean;
    rate: number;
    name: string;
    inclusive: boolean;
    appliesTo: ('physical' | 'digital' | 'service')[];
  }[]): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/business-config/taxes', {
      method: 'PATCH',
      body: JSON.stringify({ taxes }),
    });
  }

  async patchBusinessConfigCharges(charges: {
    name: string;
    type: 'percentage' | 'flat';
    value: number;
    appliesTo?: ('physical' | 'digital' | 'service')[];
    enabled: boolean;
  }[]): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/business-config/charges', {
      method: 'PATCH',
      body: JSON.stringify({ charges }),
    });
  }

  async patchBusinessConfigDiscount(discount: {
    maxDiscountType?: 'percentage' | 'flat';
    maxDiscountValue?: number;
    allowCoupons?: boolean;
    allowCouponStacking?: boolean;
    contactIds?: string[];
    productIds?: string[];
    catalogueIds?: string[];
    zones?: any[];
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/business-config/discount', {
      method: 'PATCH',
      body: JSON.stringify(discount),
    });
  }

  async patchBusinessConfigReferral(referral: {
    enabled?: boolean;
    rewardType?: 'percentage' | 'flat';
    rewardValue?: number;
    referrerRewardValue?: number;
    minOrderAmount?: number;
    expiryDays?: number;
    firstTimeOnly?: boolean;
    appliesTo?: ('physical' | 'digital' | 'service')[];
    eligibleContactIds?: string[];
    eligibleProductIds?: string[];
    eligibleCatalogueIds?: string[];
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/business-config/referral', {
      method: 'PATCH',
      body: JSON.stringify(referral),
    });
  }

  async previewCharges(data: {
    subtotal: number;
    productTypes?: ('physical' | 'digital' | 'service')[];
    orderProductIds?: string[];
    contactId?: string;
    location?: { latitude?: number; longitude?: number; city?: string };
  }): Promise<ApiResponse<{
    taxes: { name: string; rate: number; amount: number }[];
    taxAmount: number;
    charges: { name: string; amount: number; mandatory: boolean }[];
    totalCharges: number;
  }>> {
    return this.authenticatedRequest('/api/business-config/preview-charges', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ─── Coupons ────────────────────────────────────────────────────────────────

  async listCoupons(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest('/api/coupons');
  }

  async createCoupon(data: {
    code: string;
    type: 'percentage' | 'flat';
    value: number;
    minOrderAmount?: number;
    startsAt?: Date | string;
    expiresAt?: Date | string;
    isPublic?: boolean;
    firstTimeOnly?: boolean;
    productTypes?: ('physical' | 'digital' | 'service')[];
    contactIds?: string[];
    productIds?: string[];
    catalogueIds?: string[];
    maxUsageTotal?: number;
    maxUsagePerContact?: number;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/coupons', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCoupon(id: string, data: {
    code?: string;
    type?: 'percentage' | 'flat';
    value?: number;
    minOrderAmount?: number;
    startsAt?: Date | string | null;
    expiresAt?: Date | string | null;
    isPublic?: boolean;
    firstTimeOnly?: boolean;
    productTypes?: ('physical' | 'digital' | 'service')[];
    contactIds?: string[];
    productIds?: string[];
    catalogueIds?: string[];
    maxUsageTotal?: number;
    maxUsagePerContact?: number;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/coupons/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteCoupon(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/coupons/${id}`, { method: 'DELETE' });
  }

  async validateCoupon(
    businessId: string,
    code: string,
    orderAmount: number,
    contactId?: string,
    orderItems?: { productId: string; totalPrice: number }[],
    location?: { latitude?: number; longitude?: number; city?: string },
  ): Promise<ApiResponse<{ valid: boolean; discountAmount: number; couponId?: string; reason?: string; matchedZone?: string }>> {
    const orderProductIds = orderItems?.map(i => i.productId);
    return this.authenticatedRequest('/api/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({ businessId, code, orderAmount, contactId, orderProductIds, orderItems, location }),
    });
  }

  // ─── Referrals ──────────────────────────────────────────────────────────────

  async listReferrals(): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest('/api/referrals');
  }

  async validateReferral(
    businessId: string,
    code: string,
    newContactId: string,
    orderAmount: number,
    location?: { latitude?: number; longitude?: number; city?: string },
  ): Promise<ApiResponse<{ valid: boolean; referredDiscount: number; referrerReward: number; reason?: string }>> {
    return this.authenticatedRequest('/api/referrals/validate', {
      method: 'POST',
      body: JSON.stringify({ businessId, code, newContactId, orderAmount, location }),
    });
  }

  async createReferralCode(label: string, code?: string, override?: any): Promise<ApiResponse<any>> {
    return this.authenticatedRequest('/api/referrals', {
      method: 'POST',
      body: JSON.stringify({ label, code, override }),
    });
  }

  async updateReferralCode(id: string, updates: { label?: string; override?: any }): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/referrals/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteReferralCode(id: string): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/referrals/${id}`, { method: 'DELETE' });
  }

  async getReferralRedemptions(id: string): Promise<ApiResponse<any[]>> {
    return this.authenticatedRequest(`/api/referrals/${id}/redemptions`);
  }

  async updateRedemptionStatus(redemptionId: string, rewardStatus: 'paid' | 'pending' | 'cancelled'): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/referrals/redemptions/${redemptionId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ rewardStatus }),
    });
  }

  async bulkUpdateRedemptionStatus(referralId: string, rewardStatus: 'paid' | 'cancelled'): Promise<ApiResponse<any>> {
    return this.authenticatedRequest(`/api/referrals/${referralId}/redemptions/bulk`, {
      method: 'PATCH',
      body: JSON.stringify({ rewardStatus }),
    });
  }

  /** Public — no auth. Fetches order tracking info for a customer. */
  async trackOrder(businessId: string, orderNumber: string): Promise<ApiResponse<any>> {
    const url = `${this.baseURL}/api/orders/track?businessId=${encodeURIComponent(businessId)}&orderNumber=${encodeURIComponent(orderNumber)}`;
    const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    return response.json();
  }

  /** Public — no auth. Fetches order history for a contact (for TrackOrderScreen). */
  async trackOrderHistory(businessId: string, contactId: string): Promise<ApiResponse<any[]>> {
    const url = `${this.baseURL}/api/orders/history?businessId=${encodeURIComponent(businessId)}&contactId=${encodeURIComponent(contactId)}`;
    const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
    return response.json();
  }
}

// WhatsApp Custom Template Types — fields confirmed via TemplateManagementScreen.tsx's
// existing (untyped) consumption of this same endpoint. No usage/send-count field
// exists on this shape; templates are purely definitional.
export interface WhatsAppCustomTemplate {
  _id: string;
  templateName: string;
  category: string;
  metaCategory?: string;
  language: string;
  templateBody: string;
  templateHeader?: string;
  templateFooter?: string;
  metaStatus?: 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | 'DISABLED';
  metaQualityScore?: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';
  metaRejectionReason?: string;
  metaTemplateId?: string;
}

// User Management Types
export type FlowRunStatusFilter = 'all' | 'active' | 'waiting' | 'paused' | 'completed' | 'stopped' | 'errors';

export interface FlowRunFilters {
  status?: FlowRunStatusFilter;
  flowId?: string;
  contact?: string;
  contactId?: string;
  userId?: string;
  departmentId?: string;
  ref?: string;
  from?: string; // ISO
  to?: string;
}

export interface FlowRunStep {
  nodeId: string;
  nodeName?: string;
  nodeType: string;
  at: string;
  ms?: number;
  outcome: 'done' | 'error' | 'waiting' | 'paused' | 'called' | 'ended' | 'ignored';
  path?: string;
  detail?: string;
}

export interface FlowRunEffect {
  kind: 'contact' | 'custom_fields' | 'task' | 'assignment' | 'calendar_event' | 'data_list_record' | 'message' | 'webhook' | 'post' | 'otp' | 'dataset'
    | 'order' | 'quotation' | 'vendor' | 'notification';
  action: 'created' | 'updated' | 'deleted' | 'sent' | 'assigned' | 'delivered' | 'booked';
  refId?: string;
  listId?: string;
  listName?: string;
  label?: string;
  nodeId: string;
  at: string;
  users?: Array<{ id: string; name?: string }>;
  departments?: Array<{ id: string; name?: string }>;
}

export interface FlowRunTrigger { scope: string; attachedTo?: string; triggerType?: string; label?: string; text?: string }

export interface FlowRunItem {
  id: string;
  flowId: string;
  flowName: string;
  status: 'active' | 'waiting' | 'paused' | 'completed' | 'stopped';
  contactId: string;
  contactName?: string;
  contactPhone?: string;
  conversationId?: string;
  channel: string;
  trigger?: FlowRunTrigger;
  participants?: { users: Array<{ id: string; name?: string }>; departments: Array<{ id: string; name?: string }> };
  parentSessionId?: string;
  endedReason?: string;
  waitUntil?: string;
  startedAt: string;
  lastAdvancedAt: string;
  endedAt?: string;
  stepCount: number;
  errorCount: number;
  lastStep?: Pick<FlowRunStep, 'nodeName' | 'nodeType' | 'outcome' | 'detail'>;
  effectCounts: Record<string, number>;
}

export interface FlowRunsResponse { items: FlowRunItem[]; total: number; limit: number; skip: number }

export interface FlowRunDetail extends Omit<FlowRunItem, 'stepCount' | 'errorCount' | 'lastStep' | 'effectCounts'> {
  versionNumber?: number;
  channelIdentifier: string;
  currentNode?: { id: string; name?: string; type?: string };
  /** The steps of the run's flow version ("Continue from" choices). */
  nodes?: Array<{ id: string; name?: string; type?: string }>;
  stoppedBy?: { id: string; name?: string };
  resumedBy?: { id: string; name?: string };
  steps: FlowRunStep[];
  effects: FlowRunEffect[];
  nodeErrors: FlowSessionNodeError[];
  variables: Record<string, any>;
  parent?: { id: string; flowName?: string; status: string };
  children: Array<{ id: string; flowName?: string; status: string; startedAt: string }>;
}

export interface AiAgent {
  id: string;
  userId: string;
  /** Its generated flow (the runs of its conversations). */
  flowId?: string;
  slot: number;
  name: string;
  avatar?: string;
  status: 'active' | 'suspended';
  suspendedReason?: 'plan_limit' | 'no_ai' | 'no_subscription';
  suspendedAt?: string;
  keepActive: boolean;
  paused: boolean;
  isDefault: boolean;
  tone: string;
  instructions: string;
  language: 'auto' | 'ar' | 'en';
  /** How it shows products: the store page (default) or in-chat lists. */
  presentation: 'page' | 'chat';
  channels: string[];
  channelIdentifiers: string[];
  hours: 'always' | 'business' | 'after';
  actions: string[];
  catalogueIds: string[];
  handoff: { target: 'previous' | 'user' | 'department'; userId?: string; departmentId?: string; onAskHuman: boolean; onNegative: boolean; maxTurns: number; keywords: string[] };
}

export interface AiAgentActivity {
  id: string;
  userId: string;
  name: string;
  status: 'active' | 'suspended';
  paused: boolean;
  /** The agent's generated flow (its runs). */
  flowId?: string;
  conversations: number;
  contacts: number;
  runs: number;
  live: number;
  replies: number;
  orders: number;
  ordersValue: number;
  quotations: number;
  quotationsValue: number;
  currency: string;
  handoffs: number;
  errors: number;
}

export interface FlowRunsByContact {
  totalRuns: number;
  contacts: Array<{ contactId: string; contactName?: string; contactPhone?: string; runs: number; live: number; errors: number; flows: string[]; lastActivityAt: string }>;
}

export interface AiAgentsResponse {
  /** null when unlimited */
  allowed: number | null;
  unlimited: boolean;
  /** Why every agent is suspended, if so. */
  reason?: 'no_ai' | 'no_subscription';
  agents: AiAgent[];
}

/** A conversation's shared cart, priced now (taxes and mandatory charges included in total). */
export interface ConversationCart {
  conversationId: string;
  lines: Array<{ productId: string; variantId?: string; name: string; quantity: number; unitPrice: number; total: number; addons?: any[] }>;
  subtotal: number;
  taxes: Array<{ name: string; rate: number; amount: number }>;
  charges: Array<{ name: string; amount: number }>;
  total: number;
  currency: string;
  /** The AI agent that worked on it last. */
  agent?: { id: string; name: string };
  /** 'customer', 'ai', or a team member's user id. */
  updatedBy?: string;
  updatedAt?: string;
}

/** The customer's open cart on another number (offered while this one is empty). */
export interface OtherNumberCart { conversationId: string; channel?: string; items: number; total: number; currency: string; updatedAt?: string }

export interface FlowRunSkip {
  id: string;
  flowId?: string;
  flowName?: string;
  contactId?: string;
  contactName?: string;
  conversationId?: string;
  channel?: string;
  scope: string;
  reason: 'flow_inactive' | 'not_published' | 'already_running' | 'contact_assigned';
  text?: string;
  createdAt: string;
}

export interface BusinessUser {
  _id: string;
  userId: string;
  businessId: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer' | 'ai_agent';
  /** An AI agent: never signs in, not counted in the user seats. */
  isAiAgent?: boolean;
  userStatus: 'active' | 'pending' | 'suspended' | 'left';
  joinDate: string;
  lastActiveAt?: string;
  invitationCode?: string;
  invitationAcceptedAt?: string;
  departmentId?: string;
  notes?: string;
  user: {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    avatar?: string;
    phone?: string;
    isEmailVerified: boolean;
    lastLoginAt?: string;
  };
}

export interface InviteUserData {
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'manager' | 'member' | 'viewer';
  departmentId?: string;
  departmentIds?: string[];
  notes?: string;
}

export interface UserLimits {
  currentUsers: number;
  maxUsers: number;
  canAddUser: boolean;
  remainingSlots: number;
}

// ==================== Data Lists Types ====================
// Mirror sendifier_api/src/modules/data-lists entities exactly.

export type DataListColumnType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'phone'
  | 'voice'
  | 'image'
  | 'video'
  | 'object'
  | 'relation'
  | 'repeater';

export interface DataListColumnConstraints {
  required?: boolean;
  unique?: boolean;
  min?: number;
  max?: number;
  enum?: string[];
  regex?: string;
  dateMode?: 'date' | 'datetime';
}

export interface DataListRelationConfig {
  targetListId?: string;
  targetModule?: 'contacts' | 'products' | 'catalog' | 'calendar' | 'orders';
  onDelete: 'cascade' | 'setNull' | 'restrict';
  multiple?: boolean;
}

export type DataListRepeaterFieldType = 'text' | 'number' | 'boolean' | 'date';

export interface DataListRepeaterSubField {
  id: string;
  name: string;
  type: DataListRepeaterFieldType;
  constraints?: DataListColumnConstraints;
}

export interface DataListRepeaterConfig {
  fields: DataListRepeaterSubField[];
  /** 'system-orders-items' renders a product-picker add-row (ProductSelectorModal)
   * instead of the generic blank sub-field entry form. */
  kind?: 'system-orders-items';
}

export interface DataListColumn {
  id: string;
  name: string;
  type: DataListColumnType;
  nullable: boolean;
  constraints?: DataListColumnConstraints;
  relation?: DataListRelationConfig;
  repeater?: DataListRepeaterConfig;
  order: number;
  createdAt: string;
}

export type DataListColumnsSource = 'custom' | 'system';
export type DataListDataSource = 'manual' | 'webhook';
export type DataListSystemModule = 'contacts' | 'products' | 'catalog' | 'calendar' | 'orders' | 'vendors' | 'quotations';

export type DataListWebhookFieldMappingMode = 'field' | 'fixed' | 'expression';

export interface DataListWebhookFieldMapping {
  columnId: string;
  sourcePath: string; // field-mode dot-path; '' for fixed/expression rows
  isExternalId?: boolean; // Field mode only
  /** system+webhook Orders only — resolves this row's mapped value to a real Product
   * by SKU (or relationTargets.products.searchField, if configured). No
   * auto-create/fallback — a miss fails the row. At most one mapping
   * should set this. */
  isProductSku?: boolean;
  /** system+webhook Orders only — row's mapped value IS an existing Contact's _id.
   * Tried before isContactIdentifier when both are mapped. At most one mapping
   * should set this. */
  isContactId?: boolean;
  /** system+webhook Orders only — row's mapped value is a phone/email/name to look
   * up (or auto-create) a Contact by. At most one mapping should set this. */
  isContactIdentifier?: boolean;
  mode?: DataListWebhookFieldMappingMode; // defaults to 'field'
  fixedValue?: string; // mode === 'fixed'
  expression?: string; // mode === 'expression', e.g. "{{firstName}} {{lastName}}"
}

export type DataListSyncRecurrenceType = 'minutes' | 'hours' | 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/** Structured recurrence for periodic webhook sync — converted to a cron expression server-side. */
export interface DataListSyncRecurrence {
  type: DataListSyncRecurrenceType;
  interval?: number;      // 'minutes'/'hours' only — every N
  hour?: number;           // 0-23
  minute?: number;         // 0-59
  daysOfWeek?: number[];   // 'weekly' only — 0 (Sun) .. 6 (Sat)
  dayOfMonth?: number;     // 'monthly'/'quarterly'/'yearly' — 1-28
  month?: number;          // 'yearly' only — 1-12
  timezone?: string;       // IANA tz, defaults to 'UTC'
}

export interface DataListWebhookConfig {
  url: string;
  method: 'GET' | 'POST';
  headers?: Record<string, string>;
  bodyTemplate?: string;
  responseRootPath?: string;
  fieldMapping: DataListWebhookFieldMapping[];
  refreshedAt?: string;
  /** Periodic background sync. Omit = sync-on-read only (default). */
  syncRecurrence?: DataListSyncRecurrence;
  maxRetries?: number;
  retryDelaySeconds?: number;
  /** Set by the server once a sync Task exists — use with apiService.executeTask/getTaskExecutionHistory. */
  syncTaskId?: string;
  /** system+webhook Orders ONLY — genuine multi-item support: each webhook
   * row has a nested items array (not one flat SKU per row like isProductSku
   * above). Distinct config, not a DataListWebhookFieldMapping entry, since
   * it needs an array-path plus per-item sub-field paths. */
  itemsMapping?: DataListWebhookItemsMapping;
  /** system+webhook Orders ONLY — which field on the target module (Products/
   * Contacts) to search against for each cross-module relation this list
   * resolves. Omitted or a key missing = the pre-existing default field for
   * that relation (Products→sku, Contacts→primaryIdentifier). Lets a
   * business whose upstream system has no concept of DISLIX's own unique
   * fields point the search at a customColumn on that module's own system
   * list instead (e.g. "External Product ID"). */
  relationTargets?: DataListWebhookRelationTargets;
  /** Off by default — a row pulled IN from this sync doesn't fan back OUT to
   * this business's own Developer Access webhook subscribers (one delivery
   * per synced row, and a real feedback-loop risk if the webhook receiver is
   * the same system this list syncs from). Turn on only when the webhook
   * receiver is a genuinely different system that should hear about
   * sync-driven changes too. */
  triggerOutboundWebhooks?: boolean;
}

export interface DataListWebhookItemsMapping {
  arrayPath: string;      // dot-path to the items array within each raw webhook row
  skuPath?: string;       // dot-path within each item element
  namePath?: string;      // dot-path within each item element — fallback display name
  quantityPath: string;   // dot-path within each item element
  pricePath?: string;     // dot-path within each item element — fallback price
}

export interface DataListWebhookRelationTargets {
  products?: DataListWebhookRelationTarget;
  contacts?: DataListWebhookRelationTarget;
}

export interface DataListWebhookRelationTarget {
  /** A fixed adapter column id (e.g. 'sku', 'primaryIdentifier') or a
   * customColumn id on that module's own system-backed DataList — must be
   * marked constraints.unique === true. */
  searchField: string;
}

export interface DataListWebhookSyncOutcome {
  synced: number;
  updated: number;
  reactivated: number;
  skipped: number;
  removed: number;
  failed: number;
  failures: Array<{ row: number; error: string }>;
}

export interface DataList {
  _id: string;
  businessId: string;
  name: string;
  description?: string;
  columns: DataListColumn[];
  /** Extra fields alongside a system list's fixed columns — see updateDataListCustomColumns. */
  customColumns?: DataListColumn[];
  hasRecords: boolean;
  columnsSource: DataListColumnsSource;
  dataSource: DataListDataSource;
  systemModule?: DataListSystemModule;
  webhookConfig?: DataListWebhookConfig;
  /** The AI assistants understand this list's records by meaning. */
  aiMeaning?: boolean;
  assignedUserIds: string[];
  assignedDepartmentIds: string[];
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DataListSystemModuleInfo {
  module: DataListSystemModule;
  columns: DataListColumn[];
}

export interface DataListRecord {
  _id: string;
  businessId: string;
  listId: string;
  values: Record<string, any>;
  createdBy?: string;
  createdVia: 'manual' | 'flow' | 'quick_reply';
  flowId?: string;
  nodeId?: string;
  contactId?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}


// ==================== Assignment rules types ====================

export type AssignmentRuleChannel = 'whatsapp' | 'facebook_messenger';

export interface AssignmentRuleInput {
  name: string;
  isActive?: boolean;
  trigger: {
    to: 'anyone' | 'users' | 'departments';
    userIds?: string[];
    departmentIds?: string[];
    assignmentTypes?: string[];
    sources?: string[];
    batch?: 'any' | 'single' | 'bulk';
  };
  actions: {
    workflow?: { enabled: boolean; flowId?: string; channel?: AssignmentRuleChannel; channelIdentifier?: string };
    notify?: { enabled: boolean; managers?: boolean; userIds?: string[]; message?: string };
    tags?: { enabled: boolean; add?: string[]; remove?: string[] };
    deadline?: { enabled: boolean; priority?: 'low' | 'normal' | 'high' | 'urgent'; dueInHours?: number };
    escalate?: {
      enabled: boolean; afterHours?: number; when?: 'not_started' | 'not_completed';
      toUserId?: string; toDepartmentId?: string; notifyManagers?: boolean; notifyUserIds?: string[];
    };
  };
}

export interface AssignmentRuleBatch {
  id: string;
  kind: 'actions' | 'escalate';
  bulk: boolean;
  total: number;
  done: number;
  failed: number;
  skipped: number;
  createdAt: string;
  finishedAt?: string;
}

export interface AssignmentRule extends AssignmentRuleInput {
  id: string;
  isActive: boolean;
  stats: { items?: number; failed?: number; lastRunAt?: string };
  lastBatch?: AssignmentRuleBatch;
  createdAt: string;
  updatedAt: string;
}

export interface AssignmentRuleJobIssue {
  id: string;
  assignmentId: string;
  kind: 'actions' | 'escalate';
  status: 'done' | 'failed' | 'skipped' | 'queued' | 'running';
  notes: string[];
  error?: string;
  finishedAt?: string;
}

// ==================== Lead Finder types ====================

export type LeadSource = 'map' | 'google_maps' | 'links' | 'web';
export type LeadPlatform = 'website' | 'google' | 'facebook' | 'instagram' | 'linkedin' | 'x' | 'tiktok' | 'youtube' | 'snapchat' | 'whatsapp';
export type LeadField = 'name' | 'phone' | 'location' | 'email' | 'website' | 'description';
export type LeadPhoneType = 'any' | 'mobile' | 'landline';
export type LeadTargetType = 'any' | 'entity' | 'individual';
export interface LeadRequestedField { key: string; label: string; type: 'text' | 'number' | 'boolean'; required: boolean; expected?: string }
export type LeadRunStatus = 'queued' | 'running' | 'done' | 'partial' | 'failed' | 'cancelled';
export type LeadResultStatus = 'new' | 'contact' | 'vendor' | 'dismissed';

export type LeadRegion =
  | { type: 'circle'; center: { latitude: number; longitude: number }; radiusKm: number; label?: string }
  | { type: 'polygon'; polygon: [number, number][]; label?: string } // [lng, lat]
  | { type: 'area'; country?: string; city?: string; district?: string; label?: string };

export interface LeadRunInput {
  targetType?: LeadTargetType;
  professions?: string[];
  excludeKeywords?: string[];
  requestedFields?: LeadRequestedField[];
  minScore?: number;
  evaluateWithAi?: boolean;
  name?: string;
  brief?: string;
  keywords?: string[];
  categories?: string[];
  region?: LeadRegion | null;
  sources?: LeadSource[];
  links?: string[];
  required?: LeadField[];
  /** Which phone a required phone must be. */
  phoneType?: LeadPhoneType;
  platforms?: LeadPlatform[];
  enrich?: boolean;
  limit?: number;
}

export interface LeadSearchSuggestion { title: string; reason: string; prompt: string; settings: LeadRunInput }
export interface LeadSearchSuggestions {
  suggestions: LeadSearchSuggestion[];
  businessName: string;
  persona: { id: string; name: string } | null;
  personas: Array<{ id: string; name: string }>;
  basedOn: string[];
}

export interface LeadRun {
  id: string;
  name: string;
  brief?: string;
  params: Required<Omit<LeadRunInput, 'name' | 'brief'>> & { onlyNew?: boolean };
  /** Duplicated / rerun from this run (rootRunId: the first run of that search). */
  clonedFrom?: string;
  rootRunId?: string;
  origin: 'user' | 'ai' | 'workflow';
  aiPlanned: boolean;
  createdBy?: string;
  status: LeadRunStatus;
  phase?: string;
  progress: { found?: number; kept?: number; duplicates?: number; rejected?: number; rejectionReasons?: Record<string, number>; enriched?: number; existing?: number; previous?: number; bySource?: Record<string, number> };
  notes: string[];
  error?: string;
  allowance?: number;
  cancelRequested: boolean;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface LeadResult {
  profession?: string;
  entityType?: LeadTargetType | 'unknown';
  customFields?: Record<string, string | number | boolean>;
  scoreMethod?: 'ai' | 'rules';
  id: string;
  name: string;
  altNames: string[];
  description?: string;
  category?: string;
  phones: string[];
  /** The phones by kind (one that cannot be told is in both). */
  mobiles: string[];
  landlines: string[];
  emails: string[];
  website?: string;
  socials: Partial<Record<LeadPlatform, string>>;
  platforms: LeadPlatform[];
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  location?: { latitude: number; longitude: number };
  rating?: number;
  reviews?: number;
  sources: Array<{ source: LeadSource; ref?: string; url?: string }>;
  aiScore?: number;
  aiSummary?: string;
  /** A suggested first message to this lead (AI). */
  aiOpener?: string;
  /** On one of the platforms the search asked for. */
  platformMatch?: boolean;
  existingContactId?: string;
  existingVendorId?: string;
  status: LeadResultStatus;
  contactId?: string;
  vendorId?: string;
  /** Data lists it was added to. */
  dataListIds?: string[];
  actedAt?: string;
}

/** "Other found": who it is and why a check turned it away; Keep makes it a match (counted toward the quota). */
export interface LeadOtherResult {
  id: string; name: string; category?: string; profession?: string; entityType?: string;
  city?: string; country?: string; source?: LeadSource; reason?: string; aiScore?: number; aiSummary?: string;
}

export interface LeadResultsQuery {
  /** 'other': what a check turned away — minimal details (LeadOtherResult) and the reason. */
  view?: 'other';
  status?: LeadResultStatus;
  hasPhone?: boolean;
  hasMobile?: boolean;
  hasEmail?: boolean;
  hasWebsite?: boolean;
  existing?: boolean;
  platform?: LeadPlatform;
  minScore?: number;
  maxScore?: number;
  targetType?: LeadTargetType;
  customField?: string;
  customValue?: string;
  search?: string;
  sort?: 'score' | 'name' | 'rating';
  limit?: number;
  skip?: number;
}

export interface LeadAllowance {
  enabled: boolean;
  googleMaps: boolean;
  /** Web search (people's public profiles, business sites): on plans with Google Maps, once the server has a search provider. */
  web: boolean;
  runsPerMonth: number | null;
  resultsPerRun: number | null;
  resultsPerMonth: number | null;
  concurrentRuns: number | null;
  hardCapPerRun: number;
}

export interface LeadUsage {
  periodStart: string;
  runs: number;
  results: number;
  active: number;
}

// Export singleton instance

export const apiService = new ApiService();

// Custom hook to automatically sync API service with app store
export const useApiService = () => {
  // This hook will be used in components to ensure the API service
  // is always up to date with the current authentication state
  return apiService;
};

// Helper function to sync tokens with API service
export const syncApiServiceWithStore = (tokens: { token: string; refreshToken: string } | null) => {
  if (tokens?.token) {
    apiService.setAuthToken(tokens.token);
    
    // Also store refresh token for future use
    if (tokens.refreshToken) {
      apiService.setRefreshToken(tokens.refreshToken);
    }
  } else {
    apiService.setAuthToken(null);
    apiService.setRefreshToken(null);
  }
};

export interface OrderMinimumsReport {
  ok: boolean;
  currency: string;
  products: Array<{ productId: string; name: string; minimumOrderQuantity: number; quantity: number; met: boolean }>;
  vendors: Array<{ vendorId: string; name: string; minimumOrderAmount: number; currency: string; amount: number; comparable: boolean; met: boolean; productIds: string[] }>;
  violations: string[];
}

export interface ReportIdFilters {
  userIds?: string[];
  departmentIds?: string[];
  contactIds?: string[];
}

export interface ReportDurationStats {
  count: number;
  avgSeconds: number;
  medianSeconds: number;
  p90Seconds: number;
  minSeconds: number;
  maxSeconds: number;
}

export interface ReportsPerformanceBlock {
  /** Counted like the Reports "All" tab */
  activity: {
    messagesTotal: number; messagesSent: number; messagesReceived: number; messagesAutomated: number;
    calls: number; callTimeTotalSeconds: number; talkTimeTotalSeconds: number; visits: number; customersReached: number;
    /** Idle time between the agent's calls / message threads, same day */
    breakTime: ReportDurationStats | null; breakTotalSeconds: number;
  };
  messages: { replied: number; unanswered: number; responseTime: ReportDurationStats | null; firstResponseTime: ReportDurationStats | null };
  calls: {
    total: number; inbound: number; outbound: number; answered: number; missedInbound: number;
    calledBack: number; notCalledBack: number; talkTime: ReportDurationStats | null; callbackTime: ReportDurationStats | null;
    ringTime: ReportDurationStats | null; unansweredRingTime: ReportDurationStats | null;
  };
  visits: {
    total: number; completed: number; inProgress: number; missed: number; cancelled: number; open: number;
    assignmentToStart: ReportDurationStats | null; startDelay: ReportDurationStats | null; servingTime: ReportDurationStats | null;
  };
  assignments: {
    /** Assigned to the agent now (any time), and of those reached in the period */
    currentlyAssigned: number; reachedInPeriod: number; notReachedInPeriod: number;
    /** New assignments made in the period */
    total: number; interacted: number; notInteracted: number; timeToFirstInteraction: ReportDurationStats | null;
  };
}

export interface ReportsPerformanceAgent extends ReportsPerformanceBlock {
  userId: string;
  agentName?: string;
  departmentName?: string;
}

export interface ReportsPerformance {
  startDate?: string;
  endDate?: string;
  totals: ReportsPerformanceBlock;
  byAgent: ReportsPerformanceAgent[];
  computedAt: string;
}

// ── Vendors: orders, analytics, packing slips ───────────────────────────────

export type VendorStatSort = 'orders' | 'units' | 'revenue' | 'lastOrderAt' | 'productCount';

export interface VendorListQuery {
  search?: string;
  dataListId?: string;
  limit?: number;
  skip?: number;
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | VendorStatSort;
  sortOrder?: 'asc' | 'desc';
  /** JSON-encoded CustomFieldFilterEntry[] — see SortFilterModal.tsx. */
  customFieldFilters?: string;
  /** Vendors linked to this contact ("Add as vendor"). */
  contactId?: string;
  city?: string;
  country?: string;
  hasMinimum?: 'yes' | 'no';
  createdFrom?: string;
  createdTo?: string;
  /** Period and order statuses for the per-vendor numbers. */
  from?: string;
  to?: string;
  statuses?: string[];
  hasOrders?: 'yes' | 'no';
  minOrders?: number;
  minRevenue?: number;
  maxRevenue?: number;
  minProducts?: number;
  maxProducts?: number;
  withStats?: boolean;
}

export interface VendorOrdersQuery {
  from?: string;
  to?: string;
  statuses?: string[];
  paymentStatuses?: string[];
  productId?: string;
  search?: string;
  slip?: 'yes' | 'no';
}

export interface VendorOrder {
  _id: string;
  orderNumber: string;
  orderDate: string;
  orderStatus: string;
  paymentStatus?: string;
  currency: string;
  customer: { name?: string; phone?: string };
  lines: Array<{ productId: string; name: string; sku?: string; variant?: string; unit?: string; quantity: number; unitPrice: number; totalPrice: number }>;
  units: number;
  subtotal: number;
  otherVendorLines: number;
  slip?: { slipNumber: string; generatedAt: string };
}

export interface VendorAnalyticsTotals { orders: number; units: number; revenue: number; customers: number; averageOrder: number }

export interface VendorAnalytics {
  currency: string;
  range: { from: string; to: string; previousFrom: string; previousTo: string };
  statuses: string[];
  totals: VendorAnalyticsTotals;
  previous: VendorAnalyticsTotals;
  topProducts: Array<{ productId: string; name: string; sku?: string; units: number; revenue: number; orders: number }>;
  byStatus: Record<string, number>;
  series: { bucket: 'day' | 'week' | 'month'; points: Array<{ date: string; orders: number; units: number; revenue: number }> };
  minimum?: { amount: number; currency: string; met: number; total: number };
  truncated: boolean;
}

export interface PackingSlipOptions {
  locale?: 'en' | 'ar' | 'bilingual';
  /** A packing-slip document template; unset = the business's default one. */
  templateId?: string;
  showPrices?: boolean;
  hideCustomerPhone?: boolean;
  pickList?: boolean;
}

export interface PackingSlipResult { url: string; fileName: string; slips: number; orders: number; truncated: boolean }

function vendorOrdersParams(query: Record<string, any>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query || {})) {
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)) continue;
    params.append(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return params.toString();
}

export interface GeneratedDocument { url: string; fileName: string; documentNumber: string; type: string; template: { id: string | null; name: string } }
